import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { projectAPI, featureAPI, taskAPI, aiAPI, knowledgeBaseAPI, storyPointAPI, notificationAPI, taskBatchAPI, departmentAPI, stageGateAPI, testCaseAPI, qaTestPlanAPI } from '../../api/services';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { fetchSprintsByProject, SprintStatus } from '../../store/sprintSlice';
import api from '../../api/axios';
import ProjectTree from './Workspace/ProjectTree';
import SprintTree from './Workspace/SprintTree';
import QuickActions from './Workspace/QuickActions';
import StatusGroupedList from './Workspace/StatusGroupedList';
import WorkspaceBoard from './Workspace/WorkspaceBoard';
import SprintKanban from './Workspace/SprintKanban';
import KBEditorModal from './Workspace/KBEditorModal';
import FolderTree from './Folders/FolderTree';
import BatchSubmitModal from './Workspace/BatchSubmitModal';

// Lazy-load heavier sub-views
const FolderDetailModal = React.lazy(() => import('./Workspace/FolderDetailModal'));

type ViewTab = 'list' | 'board' | 'sprints' | 'backlog' | 'bugs' | 'features' | 'testing' | 'approvals' | 'folders' | 'links' | 'kb' | 'team' | 'activity' | 'leaderboard';

const VIEW_TABS: { key: ViewTab; label: string }[] = [
  { key: 'list', label: 'Overview' },
  { key: 'board', label: 'Board' },
  { key: 'sprints', label: 'Sprints' },
  { key: 'backlog', label: 'Backlog' },
  { key: 'bugs', label: 'Bugs' },
  { key: 'features', label: 'Features' },
  { key: 'testing', label: 'Testing' },
  { key: 'approvals', label: 'Approvals' },
  { key: 'folders', label: 'Folders' },
  { key: 'kb', label: 'KB' },
  { key: 'team', label: 'Team' },
  { key: 'activity', label: 'Activity' },
  { key: 'leaderboard', label: 'SP' },
];

// ─── Task slide-in panel (ClickUp-style) ─────────────────────────────────────
const STATUS_PILLS: Record<string, { bg: string; text: string }> = {
  todo: { bg: 'bg-gray-100', text: 'text-gray-700' },
  in_progress: { bg: 'bg-blue-100', text: 'text-blue-700' },
  blocked: { bg: 'bg-red-100', text: 'text-red-700' },
  review: { bg: 'bg-yellow-100', text: 'text-yellow-700' },
  done: { bg: 'bg-green-100', text: 'text-green-700' },
};
const PRIORITY_ICONS: Record<string, string> = { low: '🟢', medium: '🟡', high: '🟠', critical: '🔴' };

const resolveFileUrl = (url: string) => {
  if (!url) return url;
  if (url.startsWith('http')) return url;
  const base = (process.env.REACT_APP_API_URL || '').replace(/\/api\/?$/, '');
  return base ? `${base}${url}` : url;
};

const TaskPanel: React.FC<{
  taskId: string;
  onClose: () => void;
  onRefresh: () => void;
}> = ({ taskId, onClose, onRefresh }) => {
  const [task, setTask] = useState<any>(null);
  const [comments, setComments] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [newComment, setNewComment] = useState('');
  const [members, setMembers] = useState<any[]>([]);
  const [showAssign, setShowAssign] = useState(false);
  const [newSubtask, setNewSubtask] = useState('');
  const [addingSubtask, setAddingSubtask] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [accessDenied, setAccessDenied] = useState(false);
  const [errorDialog, setErrorDialog] = useState<string | null>(null);
  const [editingSubtaskId, setEditingSubtaskId] = useState<string | null>(null);
  const [editingSubtaskTitle, setEditingSubtaskTitle] = useState('');
  const [showAttachmentModal, setShowAttachmentModal] = useState(false);
  const [attachmentType, setAttachmentType] = useState<'file' | 'link'>('link');
  const [attachmentLink, setAttachmentLink] = useState('');
  const [attachmentName, setAttachmentName] = useState('');
  const [uploadingFile, setUploadingFile] = useState(false);
  const [addingBlocker, setAddingBlocker] = useState(false);
  const [blockerForm, setBlockerForm] = useState({ type: 'external', description: '' });
  const [confirmModal, setConfirmModal] = useState<{ show: boolean; newStatus: string; isDone: boolean; isRevert: boolean }>({ show: false, newStatus: '', isDone: false, isRevert: false });
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const [taskGates, setTaskGates] = useState<any[]>([]);
  const [taskTestCases, setTaskTestCases] = useState<any[]>([]);
  const [expandedGate, setExpandedGate] = useState<string | null>(null);
  const [showCreateTest, setShowCreateTest] = useState<string | null>(null);
  const [newTest, setNewTest] = useState({ title: '', description: '', assignedToUserId: '', assignedToName: '', steps: [{ step: '', expectedResult: '' }] });
  const [executeTestId, setExecuteTestId] = useState<string | null>(null);
  const [executeNotes, setExecuteNotes] = useState('');
  const [approveRejectNotes, setApproveRejectNotes] = useState('');
  const { user: gateUser } = useAppSelector(s => s.auth);
  const gateUserId = gateUser?.id || (gateUser as any)?._id;
  const gateOrgPriority = (gateUser as any)?.organizationRole?.priority || 0;

  const fetchTask = useCallback(async () => {
    try {
      const res = await taskAPI.getTaskById(taskId);
      setTask(res.data.data);
      setAccessDenied(false);
      if (res.data.data?.projectId) {
        projectAPI.getProjectById(res.data.data.projectId).then(r => setMembers(r.data?.data?.members || [])).catch(() => {});
      }
    } catch (err: any) {
      if (err.response?.status === 403) setAccessDenied(true);
    }
    setLoading(false);
  }, [taskId]);

  useEffect(() => { fetchTask(); }, [fetchTask]);
  useEffect(() => { taskAPI.getComments(taskId).then(r => setComments(r.data.data || [])).catch(() => {}); }, [taskId]);
  useEffect(() => {
    refreshGatesTests();
  }, [taskId]);

  const refreshGatesTests = () => {
    stageGateAPI.getTaskGates(taskId).then(r => setTaskGates(r.data.data || [])).catch(() => setTaskGates([]));
    testCaseAPI.getByTask(taskId).then(r => setTaskTestCases(r.data.data || [])).catch(() => setTaskTestCases([]));
  };

  const save = (field: string, val: any) => {
    const onErr = (err: any) => {
      const msg = err.response?.data?.message;
      if (msg) setErrorDialog(msg);
    };
    if (field === 'status') taskAPI.updateStatus(taskId, val).then(() => { fetchTask(); onRefresh(); }).catch(onErr);
    else taskAPI.updateTask(taskId, { [field]: val }).then(() => { fetchTask(); onRefresh(); }).catch(onErr);
  };

  const handleComment = async () => {
    if (!newComment.trim()) return;
    try { await taskAPI.addComment(taskId, { content: newComment.trim() }); setNewComment(''); taskAPI.getComments(taskId).then(r => setComments(r.data.data || [])).catch(() => {}); } catch { /* ignore */ }
  };

  const handleAddSubtask = async () => {
    if (!newSubtask.trim()) return;
    try { await taskAPI.createSubtask(taskId, newSubtask.trim()); setNewSubtask(''); fetchTask(); } catch { /* ignore */ }
  };

  const handleRenameSubtask = async (subtaskId: string, newTitle: string) => {
    if (!newTitle.trim()) return;
    const updatedSubtasks = (task?.subtasks || []).map((st: any) =>
      st._id === subtaskId ? { ...st, title: newTitle.trim() } : st
    );
    setTask((prev: any) => prev ? { ...prev, subtasks: updatedSubtasks } : prev);
    try { await taskAPI.updateTask(taskId, { subtasks: updatedSubtasks } as any); } catch { /* ignore */ }
  };

  const handleFileUpload = async (file: File) => {
    try {
      setUploadingFile(true);
      const formData = new FormData();
      formData.append('file', file);
      const apiBase = process.env.REACT_APP_API_URL || '/api';
      const uploadResponse = await fetch(`${apiBase}/upload`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        body: formData,
      });
      if (!uploadResponse.ok) throw new Error('File upload failed');
      const uploadData = await uploadResponse.json();
      const fileUrl = uploadData.url || uploadData.data?.url;
      if (!fileUrl) throw new Error('No file URL returned');
      let fileType = 'file';
      if (file.type.startsWith('image/')) fileType = 'image';
      else if (file.type.includes('pdf') || file.type.includes('document')) fileType = 'document';
      await taskAPI.addAttachment(taskId, { name: file.name, url: fileUrl, type: fileType });
      await fetchTask();
      setShowAttachmentModal(false);
      setAttachmentLink(''); setAttachmentName(''); setAttachmentType('link');
    } catch (err: any) {
      setErrorDialog(err.message || 'Failed to upload file');
    } finally {
      setUploadingFile(false);
    }
  };

  const handleLinkSubmit = async () => {
    if (!attachmentLink.trim()) return;
    try {
      setUploadingFile(true);
      await taskAPI.addAttachment(taskId, { name: attachmentName.trim() || attachmentLink, url: attachmentLink.trim(), type: 'link' });
      await fetchTask();
      setShowAttachmentModal(false);
      setAttachmentLink(''); setAttachmentName(''); setAttachmentType('link');
    } catch (err: any) {
      setErrorDialog(err.response?.data?.message || 'Failed to add link');
    } finally {
      setUploadingFile(false);
    }
  };

  const handleSubtaskFileUpload = async (subtaskId: string, file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    try {
      const apiBase = process.env.REACT_APP_API_URL || '/api';
      const res = await fetch(`${apiBase}/upload`, { method: 'POST', headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }, body: fd });
      const data = await res.json();
      const fileUrl = data.url || data.data?.url;
      if (!fileUrl) return;
      await taskAPI.addSubtaskAttachment(taskId, subtaskId, { name: file.name, url: fileUrl, type: file.type.startsWith('image') ? 'image' : 'file' });
      fetchTask();
    } catch { /* ignore */ }
  };

  const handleStatusChange = (newStatus: string) => {
    if (!task || task.status === newStatus) return;
    if (newStatus === 'done' || (task.status === 'done' && newStatus !== 'done')) {
      setConfirmModal({ show: true, newStatus, isDone: newStatus === 'done', isRevert: task.status === 'done' });
      return;
    }
    save('status', newStatus);
  };

  const confirmStatusChange = async () => {
    const { newStatus } = confirmModal;
    setConfirmModal({ show: false, newStatus: '', isDone: false, isRevert: false });
    save('status', newStatus);
  };

  const fmtDate = (d?: string) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '';
  const assignees = task?.assignedTo || [];
  const subtasks = task?.subtasks || [];
  const doneSubs = subtasks.filter((s: any) => s.completed).length;
  const subPct = subtasks.length > 0 ? Math.round((doneSubs / subtasks.length) * 100) : 0;
  const statusStyle = STATUS_PILLS[task?.status] || STATUS_PILLS.todo;

  // Single container for all states — no layout shift between loading/denied/loaded
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" onClick={onClose}>
      <div className="absolute inset-0 bg-black/30" style={{ animation: 'backdropFadeIn 300ms ease-out' }} />
      <div className="relative bg-white shadow-2xl h-[90vh] w-[90vw] rounded-2xl flex flex-col border border-gray-200" style={{ animation: 'modalFadeIn 300ms ease-out' }} onClick={e => e.stopPropagation()}>

        {/* Loading or access denied — shown inside the same modal container */}
        {(loading || accessDenied || !task) ? (
          <div className="flex-1 flex items-center justify-center">
            {loading ? (
              <div className="animate-spin h-5 w-5 border-2 border-blue-500 border-t-transparent rounded-full" />
            ) : (
              <div className="flex flex-col items-center text-center max-w-xs">
                <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mb-3">
                  <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" /></svg>
                </div>
                <h3 className="text-sm font-semibold text-gray-900 mb-1">{accessDenied ? 'Private task' : 'Task not found'}</h3>
                <p className="text-xs text-gray-500 mb-4">{accessDenied ? 'This task is private. Ask the task owner to change its visibility to view it.' : 'This task may have been deleted.'}</p>
                <button onClick={onClose} className="text-xs text-blue-600 hover:text-blue-700 font-medium">Close</button>
              </div>
            )}
          </div>
        ) : (<>
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-2.5 border-b border-gray-200 shrink-0 rounded-t-2xl">
          <div className="flex items-center gap-3 text-xs">
            <button onClick={() => setConfirmDelete(true)} className="text-xs text-gray-400 hover:text-red-500 transition-colors mr-2">Delete</button>
            {task && <span className={`px-2.5 py-1 rounded-full font-medium ${statusStyle.bg} ${statusStyle.text} capitalize`}>
              {task.status === 'done' ? '✓ ' : ''}{cap(task.status)}
            </span>}
            {task && <span className="text-gray-400 capitalize">{task.parentType}</span>}
            {task && <><span className="text-gray-300">·</span><span className="text-gray-400">Created {fmtDate(task.createdAt)}</span></>}
            {task && (task.storyPoints ?? 0) > 0 && <><span className="text-gray-300">·</span><span className="text-blue-600 font-medium">{task.storyPoints} SP</span></>}
            {loading && <span className="text-gray-400">Loading...</span>}
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>

        <div className="flex flex-1 overflow-hidden">
          {/* Main content */}
          <div className="flex-1 overflow-y-auto px-6 py-5">
            {/* Title */}
            <input defaultValue={task.title} onBlur={e => { if (e.target.value.trim() !== task.title) save('title', e.target.value.trim()); }}
              className="w-full text-xl font-semibold text-gray-900 border-none focus:outline-none focus:ring-0 p-0 mb-5" />

            {/* Properties grid — 2 columns */}
            <div className="grid grid-cols-2 gap-x-8 gap-y-3.5 mb-6">
              {/* Status */}
              <div className="flex items-center gap-3">
                <span className="text-xs text-gray-400 w-24 flex items-center gap-1.5">
                  <div className={`w-2.5 h-2.5 rounded-full ${task.status === 'done' ? 'bg-green-500' : task.status === 'in_progress' ? 'bg-blue-500' : task.status === 'blocked' ? 'bg-red-500' : task.status === 'review' ? 'bg-yellow-500' : 'bg-gray-400'}`} />
                  Status
                </span>
                <select value={task.status} onChange={e => handleStatusChange(e.target.value)}
                  className={`text-xs rounded-lg px-2.5 py-1 border-0 cursor-pointer focus:outline-none focus:ring-1 focus:ring-blue-400 font-medium ${STATUS_PILLS[task.status]?.bg || 'bg-gray-100'} ${STATUS_PILLS[task.status]?.text || 'text-gray-700'}`}>
                  <option value="todo">To Do</option>
                  <option value="in_progress">In Progress</option>
                  <option value="blocked">Blocked</option>
                  <option value="review">Review</option>
                  <option value="done">Done</option>
                </select>
              </div>

              {/* Assignees */}
              <div className="flex items-center gap-3">
                <span className="text-xs text-gray-400 w-24 flex items-center gap-1.5">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0" /></svg>
                  Assignees
                </span>
                <div className="flex items-center gap-1.5 flex-wrap">
                  {assignees.map((a: any, i: number) => (
                    <span key={i} className="text-[11px] bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full flex items-center gap-1 group/assignee">
                      <span className="w-4 h-4 rounded-full bg-blue-500 text-white text-[8px] flex items-center justify-center">{(a.name || '?')[0]}</span>
                      {a.name?.split(' ')[0]}
                      <button onClick={() => taskAPI.unassignTask(taskId, a.userId).then(() => fetchTask()).catch(() => {})}
                        className="w-3.5 h-3.5 rounded-full hover:bg-blue-200 flex items-center justify-center opacity-0 group-hover/assignee:opacity-100 transition-opacity -mr-0.5"
                        title={`Remove ${a.name}`}>
                        <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                      </button>
                    </span>
                  ))}
                  {assignees.length === 0 && <span className="text-xs text-gray-300">Empty</span>}
                  <div className="relative">
                    <button onClick={() => setShowAssign(!showAssign)} className="text-xs text-gray-400 hover:text-blue-600 transition-colors">+ Add</button>
                    {showAssign && members.length > 0 && (
                      <div className="absolute left-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10 py-1 w-44">
                        {members.filter((m: any) => !assignees.some((a: any) => a.userId === m.userId)).map((m: any) => (
                          <button key={m.userId} onClick={() => { taskAPI.assignTask(taskId, m.userId).then(() => { fetchTask(); setShowAssign(false); }).catch(() => {}); }}
                            className="w-full text-left px-3 py-1.5 text-xs hover:bg-gray-50 truncate">{m.name}</button>
                        ))}
                        {members.filter((m: any) => !assignees.some((a: any) => a.userId === m.userId)).length === 0 && (
                          <p className="px-3 py-1.5 text-xs text-gray-400">All members assigned</p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Dates */}
              <div className="flex items-center gap-3">
                <span className="text-xs text-gray-400 w-24 flex items-center gap-1.5">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25" /></svg>
                  Due Date
                </span>
                <input type="date" value={task.dueDate?.slice?.(0, 10) || ''} onChange={e => save('dueDate', e.target.value || undefined)}
                  className="text-xs border border-gray-200 rounded-lg px-2 py-1 focus:outline-none focus:border-blue-400" />
              </div>

              {/* Priority */}
              <div className="flex items-center gap-3">
                <span className="text-xs text-gray-400 w-24 flex items-center gap-1.5">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M3 4h13M3 8h9m-9 4h6m4 0l4-4m0 0l4 4m-4-4v12" /></svg>
                  Priority
                </span>
                <select value={task.priority || ''} onChange={e => save('priority', e.target.value)}
                  className="text-xs border border-gray-200 rounded-lg px-2.5 py-1 cursor-pointer focus:outline-none focus:ring-1 focus:ring-blue-400">
                  <option value="">None</option>
                  <option value="low">🟢 Low</option>
                  <option value="medium">🟡 Medium</option>
                  <option value="high">🟠 High</option>
                  <option value="critical">🔴 Critical</option>
                </select>
              </div>

              {/* Story Points — read-only, managed by AI/approval flow */}
              <div className="flex items-center gap-3">
                <span className="text-xs text-gray-400 w-24">Story Points</span>
                <span className="text-xs text-blue-600 font-medium">{task.storyPoints || 0} SP</span>
              </div>

              {/* Batch approval traceability */}
              {task.needsBatchApproval && !task.batchId && (
                <div className="flex items-center gap-3">
                  <span className="text-xs text-gray-400 w-24">Approval</span>
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-700 font-medium">Needs batch approval</span>
                </div>
              )}
              {task.batchId && (
                <div className="flex items-center gap-3">
                  <span className="text-xs text-gray-400 w-24">Batch</span>
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-green-100 text-green-700 font-medium">Approved via batch</span>
                </div>
              )}

              {/* Tags */}
              <div className="flex items-center gap-3">
                <span className="text-xs text-gray-400 w-24">Tags</span>
                <div className="flex gap-1 flex-wrap">
                  {(task.tags || []).length > 0 ? task.tags.map((t: string, i: number) => (
                    <span key={i} className="text-[10px] px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full">{t}</span>
                  )) : <span className="text-xs text-gray-300">Empty</span>}
                </div>
              </div>
            </div>

            {/* Description */}
            <div className="mb-6">
              <div className="text-xs font-medium text-gray-500 mb-1.5">Description</div>
              <textarea defaultValue={task.description} onBlur={e => { if (e.target.value !== (task.description || '')) save('description', e.target.value); }}
                placeholder="Add description..."
                className="w-full text-sm text-gray-700 border border-gray-200 rounded-lg px-3 py-2.5 focus:outline-none focus:border-blue-400 resize-none" rows={3} />
            </div>

            {/* Subtasks */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs font-medium text-gray-500 flex items-center gap-2">
                  Subtasks
                  {subtasks.length > 0 && (
                    <span className="text-gray-400 font-normal">({doneSubs}/{subtasks.length})</span>
                  )}
                </div>
              </div>

              {/* Progress bar */}
              {subtasks.length > 0 && (
                <div className="flex items-center gap-2 mb-2">
                  <div className="flex-1 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                    <div className={`h-full rounded-full transition-all ${subPct === 100 ? 'bg-green-500' : 'bg-blue-500'}`} style={{ width: `${subPct}%` }} />
                  </div>
                  <span className="text-[10px] text-gray-400 w-8">{subPct}%</span>
                </div>
              )}

              {/* Subtask list */}
              <div className="space-y-0.5">
                {subtasks.map((st: any) => (
                  <div key={st._id}>
                    <div className="flex items-center gap-2 py-1.5 group/sub hover:bg-gray-50 rounded px-1 -mx-1">
                      <button onClick={() => taskAPI.updateSubtask(taskId, st._id, !st.completed).then(fetchTask).catch(() => {})}
                        className={`w-4 h-4 rounded border-2 shrink-0 transition-all ${st.completed ? 'bg-green-500 border-green-500' : 'border-gray-300 hover:border-green-400'}`} />
                      {editingSubtaskId === st._id ? (
                        <input autoFocus value={editingSubtaskTitle} onChange={e => setEditingSubtaskTitle(e.target.value)}
                          onBlur={() => { handleRenameSubtask(st._id, editingSubtaskTitle); setEditingSubtaskId(null); }}
                          onKeyDown={e => { if (e.key === 'Enter') { handleRenameSubtask(st._id, editingSubtaskTitle); setEditingSubtaskId(null); } if (e.key === 'Escape') setEditingSubtaskId(null); }}
                          className="flex-1 text-sm border border-blue-300 rounded px-1 py-0.5 focus:outline-none focus:ring-1 focus:ring-blue-500" />
                      ) : (
                        <span className={`text-sm flex-1 cursor-pointer ${st.completed ? 'line-through text-gray-400' : 'text-gray-700'}`}
                          onDoubleClick={() => { setEditingSubtaskId(st._id); setEditingSubtaskTitle(st.title); }}
                          title="Double-click to edit">
                          {st.title}
                        </span>
                      )}
                      <label className="opacity-0 group-hover/sub:opacity-100 p-0.5 text-gray-400 hover:text-blue-600 cursor-pointer transition-opacity shrink-0" title="Attach file">
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" /></svg>
                        <input type="file" className="hidden" onChange={e => { const f = e.target.files?.[0]; if (f) handleSubtaskFileUpload(st._id, f); }} />
                      </label>
                      <button onClick={() => taskAPI.deleteSubtask(taskId, st._id).then(fetchTask).catch(() => {})}
                        className="text-gray-300 hover:text-red-500 opacity-0 group-hover/sub:opacity-100 transition-opacity shrink-0">
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                      </button>
                    </div>
                    {/* Subtask attachments */}
                    {(st.attachments ?? []).length > 0 && (
                      <div className="ml-7 mt-1 mb-1 space-y-0.5">
                        {(st.attachments ?? []).map((att: any, idx: number) => (
                          <div key={idx} className="flex items-center gap-2 text-xs group/att">
                            {att.type === 'image' ? (
                              <img src={resolveFileUrl(att.url)} alt={att.name}
                                onClick={() => setLightboxUrl(resolveFileUrl(att.url))}
                                className="w-6 h-6 rounded object-cover border border-gray-200 cursor-pointer hover:opacity-80 transition-opacity" />
                            ) : (
                              <svg className="w-3 h-3 text-gray-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" /></svg>
                            )}
                            <a href={resolveFileUrl(att.url)} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline truncate">{att.name}</a>
                            <button onClick={() => taskAPI.deleteSubtaskAttachment(taskId, st._id, att.url).then(() => fetchTask()).catch(() => {})}
                              className="opacity-0 group-hover/att:opacity-100 text-red-400 hover:text-red-600 shrink-0">
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Add subtask */}
              {addingSubtask ? (
                <div className="flex items-center gap-2 mt-1.5">
                  <input autoFocus value={newSubtask} onChange={e => setNewSubtask(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') handleAddSubtask(); if (e.key === 'Escape') { setAddingSubtask(false); setNewSubtask(''); } }}
                    placeholder="Subtask title" className="flex-1 text-sm py-1 px-0 border-0 border-b border-gray-200 focus:border-blue-500 focus:outline-none bg-transparent" />
                  <button onClick={handleAddSubtask} className="text-xs text-blue-600 hover:text-blue-700">Add</button>
                  <button onClick={() => { setAddingSubtask(false); setNewSubtask(''); }} className="text-xs text-gray-400">Cancel</button>
                </div>
              ) : (
                <button onClick={() => setAddingSubtask(true)} className="text-xs text-gray-400 hover:text-blue-600 mt-1.5 flex items-center gap-1">
                  <span className="text-sm leading-none">+</span> Add subtask
                </button>
              )}
            </div>

            {/* Blockers */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs font-medium text-gray-500 flex items-center gap-2">
                  Blockers
                  {(task.blockers ?? []).length > 0 && <span className="text-gray-400 font-normal">({(task.blockers ?? []).filter((b: any) => !b.resolvedAt).length} active)</span>}
                </div>
              </div>
              {(task.blockers ?? []).length > 0 && (
                <div className="space-y-0.5 mb-2">
                  {(task.blockers ?? []).map((b: any, i: number) => (
                    <div key={i} className="flex items-center gap-2.5 py-1.5 px-1 -mx-1 rounded hover:bg-gray-50">
                      <div className={`w-2 h-2 rounded-full shrink-0 ${b.resolvedAt ? 'bg-gray-300' : 'bg-red-500'}`} />
                      <span className="text-[10px] text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded font-medium shrink-0">{b.type}</span>
                      <span className={`text-sm flex-1 truncate ${b.resolvedAt ? 'text-gray-400 line-through' : 'text-gray-700'}`}>{b.description}</span>
                      {b.resolvedAt ? (
                        <span className="text-[10px] text-gray-400 shrink-0">Resolved</span>
                      ) : (
                        <button onClick={() => taskAPI.resolveBlocker(taskId, i).then(() => fetchTask()).catch(() => {})}
                          className="text-[10px] text-blue-600 hover:text-blue-700 font-medium shrink-0">Resolve</button>
                      )}
                    </div>
                  ))}
                </div>
              )}
              {addingBlocker ? (
                <div className="space-y-2 mt-1.5">
                  <input autoFocus value={blockerForm.description} onChange={e => setBlockerForm(f => ({ ...f, description: e.target.value }))}
                    onKeyDown={e => {
                      if (e.key === 'Enter' && blockerForm.description.trim()) { taskAPI.addBlocker(taskId, blockerForm).then(() => { setBlockerForm({ type: 'external', description: '' }); setAddingBlocker(false); fetchTask(); }).catch(() => {}); }
                      if (e.key === 'Escape') { setAddingBlocker(false); setBlockerForm({ type: 'external', description: '' }); }
                    }}
                    placeholder="Blocker description"
                    className="w-full text-sm py-1 px-0 border-0 border-b border-gray-200 focus:border-blue-500 focus:outline-none bg-transparent" />
                  <div className="flex items-center justify-between">
                    <div className="flex gap-1">
                      {['external', 'task', 'feature', 'bug'].map(t => (
                        <button key={t} onClick={() => setBlockerForm(f => ({ ...f, type: t }))}
                          className={`text-[10px] px-2 py-0.5 rounded-full capitalize ${blockerForm.type === t ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}>
                          {t}
                        </button>
                      ))}
                    </div>
                    <div className="flex gap-2">
                      <button onClick={() => { setAddingBlocker(false); setBlockerForm({ type: 'external', description: '' }); }}
                        className="text-xs text-gray-400">Cancel</button>
                      <button onClick={() => { if (!blockerForm.description.trim()) return; taskAPI.addBlocker(taskId, blockerForm).then(() => { setBlockerForm({ type: 'external', description: '' }); setAddingBlocker(false); fetchTask(); }).catch(() => {}); }}
                        className="text-xs text-blue-600 hover:text-blue-700 font-medium">Add</button>
                    </div>
                  </div>
                </div>
              ) : (
                <button onClick={() => setAddingBlocker(true)} className="text-xs text-gray-400 hover:text-blue-600 mt-1 flex items-center gap-1">
                  <span className="text-sm leading-none">+</span> Add blocker
                </button>
              )}
            </div>

            {/* Stage Gates & Tests */}
            {(taskGates.length > 0 || taskTestCases.length > 0) && (
              <div className="mb-6">
                <div className="text-xs font-medium text-gray-500 mb-2 flex items-center gap-2">
                  Gates & Tests
                  {taskGates.filter((g: any) => g.status === 'pending').length > 0 && (
                    <span className="text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full">
                      {taskGates.filter((g: any) => g.status === 'pending').length} pending
                    </span>
                  )}
                </div>
                <div className="space-y-2">
                  {taskGates.map((g: any) => {
                    const isExpanded = expandedGate === g._id;
                    const gateTests = taskTestCases.filter((tc: any) => tc.stageGateRequestId === g._id);
                    return (
                      <div key={g._id} className={`rounded border overflow-hidden ${
                        g.status === 'pending' ? 'border-amber-200 bg-amber-50/50' :
                        g.status === 'approved' || g.status === 'passed' ? 'border-green-200 bg-green-50/50' :
                        g.status === 'rejected' || g.status === 'failed' ? 'border-red-200 bg-red-50/50' :
                        'border-gray-200 bg-gray-50'
                      }`}>
                        <div className="flex items-center gap-2 text-xs p-2 cursor-pointer" onClick={() => setExpandedGate(isExpanded ? null : g._id)}>
                          <svg className={`w-3 h-3 text-gray-400 transition-transform shrink-0 ${isExpanded ? 'rotate-90' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                          <span className="text-gray-700 capitalize font-medium">{g.gateType}</span>
                          <span className="text-gray-400">→ {g.stage}</span>
                          {g.mandatory && <span className="text-[9px] font-bold bg-red-600 text-white px-1 py-0.5 rounded">REQ</span>}
                          <span className={`ml-auto text-[10px] font-medium px-1.5 py-0.5 rounded-full ${
                            g.status === 'pending' ? 'bg-amber-200 text-amber-800' :
                            g.status === 'approved' || g.status === 'passed' ? 'bg-green-200 text-green-800' :
                            'bg-red-200 text-red-800'
                          }`}>{g.status}</span>
                        </div>

                        {isExpanded && (
                          <div className="border-t px-2 py-2 bg-white/60 space-y-2 text-xs">
                            <div className="text-gray-500">
                              {g.assignedTo?.name && <span>Assigned to <span className="text-gray-700 font-medium">{g.assignedTo.name}</span></span>}
                              {g.requestedBy?.name && <span className="ml-2 text-gray-400">by {g.requestedBy.name}</span>}
                            </div>

                            {g.notes && <div className="text-gray-600 bg-gray-50 rounded p-1.5"><span className="font-medium">Notes:</span> {g.notes}</div>}
                            {g.resolutionNotes && (
                              <div className="text-gray-600 bg-gray-50 rounded p-1.5">
                                <span className="font-medium">Resolution:</span> {g.resolutionNotes}
                                {g.resolvedBy?.name && <span className="text-gray-400"> — {g.resolvedBy.name}</span>}
                              </div>
                            )}

                            {/* Approval gate actions */}
                            {g.gateType === 'approval' && g.status === 'pending' && (() => {
                              const isGateRequester = g.requestedBy?.userId && gateUserId && String(g.requestedBy.userId) === String(gateUserId);
                              if (isGateRequester && gateOrgPriority < 50) return (
                                <div className="text-xs text-gray-400 italic py-1">You cannot approve your own gate request</div>
                              );
                              return (
                              <div className="space-y-1.5">
                                <textarea
                                  value={approveRejectNotes}
                                  onChange={e => setApproveRejectNotes(e.target.value)}
                                  placeholder="Add notes (optional)..."
                                  className="w-full text-xs border border-gray-200 rounded p-1.5 resize-none focus:outline-none focus:ring-1 focus:ring-blue-400"
                                  rows={2}
                                />
                                <div className="flex gap-2">
                                  <button onClick={async () => {
                                    try { await stageGateAPI.approve(g._id, approveRejectNotes || undefined); setApproveRejectNotes(''); refreshGatesTests(); } catch (err: any) { setErrorDialog(err?.response?.data?.message || 'Failed to approve gate'); }
                                  }} className="px-2 py-1 text-[11px] bg-green-600 text-white rounded hover:bg-green-700 font-medium">Approve</button>
                                  <button onClick={async () => {
                                    try { await stageGateAPI.reject(g._id, approveRejectNotes || undefined); setApproveRejectNotes(''); refreshGatesTests(); } catch (err: any) { setErrorDialog(err?.response?.data?.message || 'Failed to reject gate'); }
                                  }} className="px-2 py-1 text-[11px] bg-red-600 text-white rounded hover:bg-red-700 font-medium">Reject</button>
                                </div>
                              </div>
                              );
                            })()}

                            {/* Testing gate: test cases */}
                            {g.gateType === 'testing' && (
                              <div className="space-y-1.5">
                                {gateTests.map((tc: any) => {
                                  const isExec = executeTestId === tc._id;
                                  return (
                                    <div key={tc._id} className={`border rounded p-1.5 ${
                                      tc.status === 'passed' ? 'border-green-200 bg-green-50' :
                                      tc.status === 'failed' ? 'border-red-200 bg-red-50' :
                                      'border-gray-200 bg-white'
                                    }`}>
                                      <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-1.5">
                                          {tc.status === 'passed' ? (
                                            <svg className="w-3.5 h-3.5 text-green-600 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                                          ) : tc.status === 'failed' ? (
                                            <svg className="w-3.5 h-3.5 text-red-600 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                                          ) : (
                                            <div className="w-3.5 h-3.5 rounded-full border-2 border-gray-300 shrink-0" />
                                          )}
                                          <span className="text-gray-800 font-medium truncate">{tc.title}</span>
                                        </div>
                                        {(tc.status === 'pending' || tc.status === 'in_progress' || tc.status === 'failed') && (
                                          <button onClick={() => setExecuteTestId(isExec ? null : tc._id)}
                                            className="text-[10px] text-blue-600 hover:text-blue-700 font-medium shrink-0 ml-1">
                                            {tc.status === 'failed' ? 'Retest' : 'Run'}
                                          </button>
                                        )}
                                      </div>

                                      {tc.executionHistory && tc.executionHistory.length > 0 && !isExec && (
                                        <div className="mt-1 space-y-0.5">
                                          {tc.executionHistory.length > 1 && <div className="text-[9px] font-medium text-gray-400 uppercase tracking-wide">{tc.executionHistory.length} attempts</div>}
                                          {tc.executionHistory.map((run: any, ri: number) => (
                                            <div key={ri} className={`text-[10px] rounded p-1 ${run.status === 'passed' ? 'bg-green-50 border border-green-100' : 'bg-red-50 border border-red-100'}`}>
                                              <span className={`font-medium ${run.status === 'passed' ? 'text-green-700' : 'text-red-700'}`}>{run.status === 'passed' ? 'Pass' : 'Fail'}</span>
                                              <span className="text-gray-400 ml-1">{run.executedBy?.name}{run.executedAt && ` · ${new Date(run.executedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`}</span>
                                              {run.resultNotes && <div className="text-gray-600 mt-0.5">{run.resultNotes}</div>}
                                            </div>
                                          ))}
                                        </div>
                                      )}
                                      {tc.resultNotes && !isExec && (!tc.executionHistory || tc.executionHistory.length === 0) && (
                                        <div className="text-[10px] text-gray-600 mt-1 bg-gray-50 rounded p-1">
                                          {tc.resultNotes}
                                          {tc.executedBy?.name && <span className="text-gray-400"> — {tc.executedBy.name}</span>}
                                        </div>
                                      )}

                                      {tc.steps && tc.steps.length > 0 && isExec && (
                                        <div className="mt-1 ml-5 space-y-0.5">
                                          {tc.steps.map((s: any, si: number) => (
                                            <div key={si} className="text-[10px] text-gray-600">
                                              <span className="text-gray-400">{si + 1}.</span> {s.step}
                                              {s.expectedResult && <span className="text-gray-400"> → {s.expectedResult}</span>}
                                            </div>
                                          ))}
                                        </div>
                                      )}

                                      {isExec && (
                                        <div className="mt-1.5 space-y-1 border-t pt-1.5">
                                          <textarea
                                            value={executeNotes} onChange={e => setExecuteNotes(e.target.value)}
                                            placeholder="Result notes..."
                                            className="w-full text-[11px] border border-gray-200 rounded p-1.5 resize-none focus:outline-none focus:ring-1 focus:ring-blue-400"
                                            rows={2}
                                          />
                                          <div className="flex gap-1.5">
                                            <button onClick={async () => {
                                              try { await testCaseAPI.execute(tc._id, { status: 'passed', resultNotes: executeNotes }); setExecuteTestId(null); setExecuteNotes(''); refreshGatesTests(); } catch (err: any) { setErrorDialog(err?.response?.data?.message || 'Failed to execute test'); }
                                            }} className="px-2 py-0.5 text-[10px] bg-green-600 text-white rounded hover:bg-green-700 font-medium">Pass</button>
                                            <button onClick={async () => {
                                              try { await testCaseAPI.execute(tc._id, { status: 'failed', resultNotes: executeNotes }); setExecuteTestId(null); setExecuteNotes(''); refreshGatesTests(); } catch (err: any) { setErrorDialog(err?.response?.data?.message || 'Failed to execute test'); }
                                            }} className="px-2 py-0.5 text-[10px] bg-red-600 text-white rounded hover:bg-red-700 font-medium">Fail</button>
                                            <button onClick={() => { setExecuteTestId(null); setExecuteNotes(''); }} className="px-2 py-0.5 text-[10px] text-gray-500">Cancel</button>
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}

                                {/* Create test case */}
                                {g.status === 'pending' && (
                                  <>
                                    {showCreateTest === g._id ? (
                                      <div className="border border-blue-200 rounded p-2 bg-blue-50/50 space-y-1.5">
                                        <input value={newTest.title} onChange={e => setNewTest(p => ({ ...p, title: e.target.value }))}
                                          placeholder="Test case title" className="w-full text-xs border border-gray-200 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-400" />
                                        <textarea value={newTest.description} onChange={e => setNewTest(p => ({ ...p, description: e.target.value }))}
                                          placeholder="Description (optional)" className="w-full text-[11px] border border-gray-200 rounded p-1.5 resize-none focus:outline-none focus:ring-1 focus:ring-blue-400" rows={2} />
                                        <div>
                                          <div className="text-[10px] font-medium text-gray-600 mb-0.5">Steps</div>
                                          {newTest.steps.map((s, si) => (
                                            <div key={si} className="flex gap-1 mb-0.5">
                                              <span className="text-[10px] text-gray-400 mt-1 shrink-0">{si + 1}.</span>
                                              <input value={s.step} onChange={e => { const steps = [...newTest.steps]; steps[si] = { ...steps[si], step: e.target.value }; setNewTest(p => ({ ...p, steps })); }}
                                                placeholder="Step" className="flex-1 text-[11px] border border-gray-200 rounded px-1.5 py-0.5 focus:outline-none focus:ring-1 focus:ring-blue-400" />
                                              <input value={s.expectedResult} onChange={e => { const steps = [...newTest.steps]; steps[si] = { ...steps[si], expectedResult: e.target.value }; setNewTest(p => ({ ...p, steps })); }}
                                                placeholder="Expected" className="flex-1 text-[11px] border border-gray-200 rounded px-1.5 py-0.5 focus:outline-none focus:ring-1 focus:ring-blue-400" />
                                              {newTest.steps.length > 1 && (
                                                <button onClick={() => setNewTest(p => ({ ...p, steps: p.steps.filter((_, i) => i !== si) }))} className="text-red-400 hover:text-red-600 text-[10px] px-0.5">x</button>
                                              )}
                                            </div>
                                          ))}
                                          <button onClick={() => setNewTest(p => ({ ...p, steps: [...p.steps, { step: '', expectedResult: '' }] }))}
                                            className="text-[10px] text-blue-600 hover:text-blue-700 font-medium">+ Step</button>
                                        </div>
                                        <select value={newTest.assignedToUserId} onChange={e => {
                                          const u = members.find((m: any) => m.userId === e.target.value);
                                          setNewTest(p => ({ ...p, assignedToUserId: e.target.value, assignedToName: u?.name || '' }));
                                        }} className="w-full text-[11px] border border-gray-200 rounded px-1.5 py-1 focus:outline-none focus:ring-1 focus:ring-blue-400">
                                          <option value="">{g.assignedTo?.name ? `${g.assignedTo.name} (gate assignee)` : 'Assign to...'}</option>
                                          {members.filter((m: any) => m.userId !== g.assignedTo?.userId?.toString()).map((m: any) => <option key={m.userId} value={m.userId}>{m.name}</option>)}
                                        </select>
                                        <div className="flex gap-1.5">
                                          <button onClick={async () => {
                                            const aId = newTest.assignedToUserId || g.assignedTo?.userId?.toString();
                                            const aName = newTest.assignedToName || g.assignedTo?.name || '';
                                            if (!newTest.title || !aId || !task) return;
                                            try {
                                              await testCaseAPI.create({ title: newTest.title, description: newTest.description || undefined, taskId: task._id, projectId: task.projectId, stageGateRequestId: g._id, steps: newTest.steps.filter(s => s.step), assignedToUserId: aId, assignedToName: aName });
                                              setNewTest({ title: '', description: '', assignedToUserId: '', assignedToName: '', steps: [{ step: '', expectedResult: '' }] });
                                              setShowCreateTest(null);
                                              refreshGatesTests();
                                            } catch (err: any) { setErrorDialog(err?.response?.data?.message || 'Failed to create test case'); }
                                          }} disabled={!newTest.title || (!newTest.assignedToUserId && !g.assignedTo?.userId)}
                                            className="px-2 py-1 text-[11px] bg-blue-600 text-white rounded hover:bg-blue-700 font-medium disabled:opacity-50">Create</button>
                                          <button onClick={() => { setShowCreateTest(null); setNewTest({ title: '', description: '', assignedToUserId: '', assignedToName: '', steps: [{ step: '', expectedResult: '' }] }); }}
                                            className="px-2 py-1 text-[11px] text-gray-500">Cancel</button>
                                        </div>
                                      </div>
                                    ) : (
                                      <button onClick={() => setShowCreateTest(g._id)}
                                        className="text-[10px] text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1">
                                        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
                                        Add Test Case
                                      </button>
                                    )}
                                  </>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}

                  {/* Standalone test cases */}
                  {taskTestCases.filter((tc: any) => !tc.stageGateRequestId).map((tc: any) => {
                    const isExec = executeTestId === tc._id;
                    return (
                      <div key={tc._id} className={`border rounded p-2 text-xs ${
                        tc.status === 'passed' ? 'border-green-200 bg-green-50' :
                        tc.status === 'failed' ? 'border-red-200 bg-red-50' :
                        'border-gray-200 bg-gray-50'
                      }`}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-1.5">
                            {tc.status === 'passed' ? (
                              <svg className="w-3.5 h-3.5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                            ) : tc.status === 'failed' ? (
                              <svg className="w-3.5 h-3.5 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                            ) : (
                              <div className="w-3.5 h-3.5 rounded-full border-2 border-gray-300" />
                            )}
                            <span className="text-gray-800 font-medium truncate">{tc.title}</span>
                          </div>
                          {(tc.status === 'pending' || tc.status === 'in_progress' || tc.status === 'failed') && (
                            <button onClick={() => setExecuteTestId(isExec ? null : tc._id)}
                              className="text-[10px] text-blue-600 hover:text-blue-700 font-medium shrink-0">
                              {tc.status === 'failed' ? 'Retest' : 'Run'}
                            </button>
                          )}
                        </div>
                        {tc.executionHistory && tc.executionHistory.length > 0 && !isExec && (
                          <div className="mt-1 space-y-0.5">
                            {tc.executionHistory.length > 1 && <div className="text-[9px] font-medium text-gray-400 uppercase tracking-wide">{tc.executionHistory.length} attempts</div>}
                            {tc.executionHistory.map((run: any, ri: number) => (
                              <div key={ri} className={`text-[10px] rounded p-1 ${run.status === 'passed' ? 'bg-green-50 border border-green-100' : 'bg-red-50 border border-red-100'}`}>
                                <span className={`font-medium ${run.status === 'passed' ? 'text-green-700' : 'text-red-700'}`}>{run.status === 'passed' ? 'Pass' : 'Fail'}</span>
                                <span className="text-gray-400 ml-1">{run.executedBy?.name}{run.executedAt && ` · ${new Date(run.executedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`}</span>
                                {run.resultNotes && <div className="text-gray-600 mt-0.5">{run.resultNotes}</div>}
                              </div>
                            ))}
                          </div>
                        )}
                        {tc.resultNotes && !isExec && (!tc.executionHistory || tc.executionHistory.length === 0) && (
                          <div className="text-[10px] text-gray-600 mt-1 bg-white/60 rounded p-1">
                            {tc.resultNotes}{tc.executedBy?.name && <span className="text-gray-400"> — {tc.executedBy.name}</span>}
                          </div>
                        )}
                        {isExec && (
                          <div className="mt-1.5 space-y-1 border-t pt-1.5">
                            <textarea value={executeNotes} onChange={e => setExecuteNotes(e.target.value)}
                              placeholder="Result notes..." className="w-full text-[11px] border border-gray-200 rounded p-1.5 resize-none focus:outline-none focus:ring-1 focus:ring-blue-400" rows={2} />
                            <div className="flex gap-1.5">
                              <button onClick={async () => {
                                try { await testCaseAPI.execute(tc._id, { status: 'passed', resultNotes: executeNotes }); setExecuteTestId(null); setExecuteNotes(''); refreshGatesTests(); } catch (err: any) { setErrorDialog(err?.response?.data?.message || 'Failed to execute test'); }
                              }} className="px-2 py-0.5 text-[10px] bg-green-600 text-white rounded hover:bg-green-700 font-medium">Pass</button>
                              <button onClick={async () => {
                                try { await testCaseAPI.execute(tc._id, { status: 'failed', resultNotes: executeNotes }); setExecuteTestId(null); setExecuteNotes(''); refreshGatesTests(); } catch (err: any) { setErrorDialog(err?.response?.data?.message || 'Failed to execute test'); }
                              }} className="px-2 py-0.5 text-[10px] bg-red-600 text-white rounded hover:bg-red-700 font-medium">Fail</button>
                              <button onClick={() => { setExecuteTestId(null); setExecuteNotes(''); }} className="px-2 py-0.5 text-[10px] text-gray-500">Cancel</button>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Attachments */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs font-medium text-gray-500 flex items-center gap-2">
                  Attachments
                  {(task.attachments ?? []).length > 0 && <span className="text-gray-400 font-normal">({task.attachments.length})</span>}
                </div>
                <button onClick={() => setShowAttachmentModal(true)} disabled={uploadingFile}
                  className="text-xs text-gray-400 hover:text-blue-600 disabled:text-gray-300">
                  {uploadingFile ? 'Uploading...' : '+ Add'}
                </button>
              </div>
              {(task.attachments ?? []).length > 0 && (
                <div className="space-y-0.5">
                  {(task.attachments ?? []).map((att: any, idx: number) => (
                    <div key={idx} className="group/att">
                      {att.type === 'image' ? (
                        <div className="py-1.5 px-1 -mx-1 rounded hover:bg-gray-50">
                          <div className="flex items-center gap-2 mb-1.5">
                            <svg className="w-3 h-3 text-gray-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909M3.75 21h16.5A2.25 2.25 0 0022.5 18.75V5.25A2.25 2.25 0 0020.25 3H3.75A2.25 2.25 0 001.5 5.25v13.5A2.25 2.25 0 003.75 21z" /></svg>
                            <span className="text-xs text-gray-600 truncate flex-1">{att.name}</span>
                            <button onClick={() => { if (window.confirm('Delete this attachment?')) taskAPI.deleteAttachment(taskId, att.url).then(() => fetchTask()).catch(() => {}); }}
                              className="opacity-0 group-hover/att:opacity-100 text-gray-400 hover:text-red-500 shrink-0 transition-opacity">
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                            </button>
                          </div>
                          <img src={resolveFileUrl(att.url)} alt={att.name}
                            onClick={() => setLightboxUrl(resolveFileUrl(att.url))}
                            className="max-w-full max-h-40 rounded-lg border border-gray-200 object-cover cursor-pointer hover:opacity-90 transition-opacity" />
                        </div>
                      ) : (
                        <div className="flex items-center gap-2.5 py-1.5 px-1 -mx-1 rounded hover:bg-gray-50">
                          <svg className="w-3.5 h-3.5 text-gray-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" /></svg>
                          <a href={resolveFileUrl(att.url)} target="_blank" rel="noopener noreferrer"
                            className="text-sm text-blue-600 hover:underline truncate flex-1">{att.name}</a>
                          <span className="text-[10px] text-gray-400">{att.type}</span>
                          <button onClick={() => { if (window.confirm('Delete this attachment?')) taskAPI.deleteAttachment(taskId, att.url).then(() => fetchTask()).catch(() => {}); }}
                            className="opacity-0 group-hover/att:opacity-100 text-gray-400 hover:text-red-500 shrink-0 transition-opacity">
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
              {(task.attachments ?? []).length === 0 && <p className="text-xs text-gray-300">No attachments</p>}
            </div>
          </div>

          {/* Activity sidebar */}
          <div className="w-72 border-l border-gray-200 bg-gray-50/50 flex flex-col shrink-0 hidden md:flex">
            <div className="px-4 py-2.5 border-b border-gray-100">
              <span className="text-xs font-medium text-gray-500">Activity</span>
              <span className="text-xs text-gray-400 ml-1.5">{comments.length}</span>
            </div>
            <div className="flex-1 overflow-y-auto px-4 py-3 space-y-4">
              {comments.map((c: any) => (
                <div key={c._id}>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="w-5 h-5 rounded-full bg-blue-500 text-white text-[9px] flex items-center justify-center shrink-0">{(c.author?.name || '?')[0]}</span>
                    <span className="text-xs font-medium text-gray-700">{c.author?.name?.split(' ')[0]}</span>
                    <span className="text-[10px] text-gray-400 ml-auto">{fmtDate(c.createdAt)}</span>
                  </div>
                  <p className="text-xs text-gray-600 pl-7 whitespace-pre-wrap">{c.content}</p>
                </div>
              ))}
              {comments.length === 0 && <p className="text-xs text-gray-400 text-center py-6">No comments yet</p>}
            </div>
            <div className="px-4 py-3 border-t border-gray-100">
              <textarea value={newComment} onChange={e => setNewComment(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleComment(); } }}
                placeholder="Write a comment..." className="w-full text-xs border border-gray-200 rounded-lg px-2.5 py-2 focus:outline-none focus:border-blue-400 resize-none" rows={2} />
              <div className="flex justify-end mt-1.5">
                <button onClick={handleComment} className="text-xs text-blue-600 hover:text-blue-700 font-medium">Comment</button>
              </div>
            </div>
          </div>
        </div>
        </>)}

        {/* Error dialog */}
        {errorDialog && (
          <div className="absolute inset-0 bg-black/30 flex items-center justify-center z-10 rounded-2xl" onClick={() => setErrorDialog(null)}>
            <div className="bg-white rounded-xl shadow-xl p-5 w-full max-w-sm mx-4" onClick={e => e.stopPropagation()}>
              <div className="flex items-start gap-3 mb-3">
                <div className="w-8 h-8 rounded-full bg-yellow-100 flex items-center justify-center shrink-0 mt-0.5">
                  <svg className="w-4 h-4 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" /></svg>
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 mb-0.5">Action not allowed</h3>
                  <p className="text-xs text-gray-600">{errorDialog}</p>
                </div>
              </div>
              <div className="flex justify-end">
                <button onClick={() => setErrorDialog(null)} className="px-3 py-1.5 text-xs text-blue-600 hover:bg-blue-50 rounded-lg transition-colors font-medium">OK</button>
              </div>
            </div>
          </div>
        )}

        {/* Delete confirmation modal */}
        {confirmDelete && (
          <div className="absolute inset-0 bg-black/30 flex items-center justify-center z-10" onClick={() => setConfirmDelete(false)}>
            <div className="bg-white rounded-xl shadow-xl p-5 w-full max-w-xs mx-4" onClick={e => e.stopPropagation()}>
              <div className="mb-3">
                <h3 className="text-sm font-semibold text-gray-900">Delete task</h3>
                <p className="text-xs text-gray-500 mt-0.5">This action cannot be undone.</p>
              </div>
              <div className="flex justify-end gap-2">
                <button onClick={() => setConfirmDelete(false)} className="px-3 py-1.5 text-xs text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">Cancel</button>
                <button onClick={() => { taskAPI.deleteTask(taskId).then(() => { onClose(); onRefresh(); }).catch(() => {}); }}
                  className="px-3 py-1.5 text-xs bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors">Delete</button>
              </div>
            </div>
          </div>
        )}

        {/* Attachment modal */}
        {showAttachmentModal && (
          <div className="absolute inset-0 bg-black/30 flex items-center justify-center z-10 rounded-2xl" onClick={() => { setShowAttachmentModal(false); setAttachmentLink(''); setAttachmentName(''); setAttachmentType('link'); }}>
            <div className="bg-white rounded-xl shadow-xl p-5 w-full max-w-xs mx-4" onClick={e => e.stopPropagation()}>
              <div className="mb-3">
                <h3 className="text-sm font-semibold text-gray-900">Add attachment</h3>
                <p className="text-xs text-gray-500 mt-0.5">Upload a file or paste a link.</p>
              </div>
              <div className="flex gap-1 mb-3 bg-gray-100 rounded-lg p-0.5">
                <button onClick={() => setAttachmentType('file')}
                  className={`flex-1 px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${attachmentType === 'file' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
                  File
                </button>
                <button onClick={() => setAttachmentType('link')}
                  className={`flex-1 px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${attachmentType === 'link' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
                  Link
                </button>
              </div>
              {attachmentType === 'file' ? (
                <div>
                  <input ref={fileInputRef} type="file" className="hidden" onChange={e => { const f = e.target.files?.[0]; if (f) handleFileUpload(f); }} />
                  <button onClick={() => fileInputRef.current?.click()} disabled={uploadingFile}
                    className="w-full py-8 border border-dashed border-gray-300 rounded-lg hover:border-gray-400 hover:bg-gray-50 transition-colors disabled:opacity-50">
                    <div className="text-center">
                      <svg className="w-6 h-6 mx-auto text-gray-400 mb-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M12 16.5V9.75m0 0l3 3m-3-3l-3 3M6.75 19.5a4.5 4.5 0 01-1.41-8.775 5.25 5.25 0 0110.233-2.33 3 3 0 013.758 3.848A3.752 3.752 0 0118 19.5H6.75z" /></svg>
                      <p className="text-xs text-gray-500">{uploadingFile ? 'Uploading...' : 'Click to select a file'}</p>
                    </div>
                  </button>
                </div>
              ) : (
                <div className="space-y-2.5">
                  <input type="url" value={attachmentLink} onChange={e => setAttachmentLink(e.target.value)}
                    placeholder="https://..." autoFocus
                    className="w-full text-sm py-1.5 px-0 border-0 border-b border-gray-200 focus:border-blue-500 focus:outline-none bg-transparent" />
                  <input type="text" value={attachmentName} onChange={e => setAttachmentName(e.target.value)}
                    placeholder="Display name (optional)"
                    className="w-full text-sm py-1.5 px-0 border-0 border-b border-gray-200 focus:border-blue-500 focus:outline-none bg-transparent" />
                  <div className="flex justify-end gap-2 pt-1">
                    <button onClick={() => { setShowAttachmentModal(false); setAttachmentLink(''); setAttachmentName(''); }}
                      className="px-3 py-1.5 text-xs text-gray-600 hover:text-gray-800">Cancel</button>
                    <button onClick={handleLinkSubmit} disabled={uploadingFile || !attachmentLink.trim()}
                      className="px-3 py-1.5 text-xs bg-gray-900 text-white rounded-lg hover:bg-gray-800 disabled:opacity-50">Add</button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Status change confirmation modal */}
        {confirmModal.show && task && (
          <div className="absolute inset-0 bg-black/30 flex items-center justify-center z-10 rounded-2xl" onClick={() => setConfirmModal({ show: false, newStatus: '', isDone: false, isRevert: false })}>
            <div className="bg-white rounded-xl shadow-xl p-5 w-full max-w-xs mx-4" onClick={e => e.stopPropagation()}>
              <div className="mb-3">
                <h3 className="text-sm font-semibold text-gray-900">{confirmModal.isDone ? 'Complete task' : 'Revert task'}</h3>
                <p className="text-xs text-gray-500 mt-0.5">
                  {confirmModal.isDone ? `Mark "${task.title}" as done?` : `Move "${task.title}" back to ${confirmModal.newStatus.replace('_', ' ')}?`}
                </p>
              </div>
              {(task.storyPoints || 0) > 0 && (
                <div className="bg-gray-50 rounded-lg px-3 py-2.5 mb-3">
                  <p className="text-xs font-medium text-gray-900">
                    {confirmModal.isDone ? '+' : '-'}{task.storyPoints} SP
                  </p>
                  <p className="text-[10px] text-gray-500 mt-0.5">
                    {confirmModal.isDone ? 'Points will be recorded in your journal.' : 'Points will be re-earned on completion.'}
                  </p>
                </div>
              )}
              <div className="flex justify-end gap-2">
                <button onClick={() => setConfirmModal({ show: false, newStatus: '', isDone: false, isRevert: false })}
                  className="px-3 py-1.5 text-xs text-gray-600 hover:text-gray-800">Cancel</button>
                <button onClick={confirmStatusChange}
                  className="px-3 py-1.5 text-xs bg-gray-900 text-white rounded-lg hover:bg-gray-800 font-medium">
                  {confirmModal.isDone ? 'Mark as Done' : 'Revert'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Image lightbox */}
        {lightboxUrl && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[60] cursor-zoom-out" onClick={() => setLightboxUrl(null)}>
            <button onClick={() => setLightboxUrl(null)}
              className="absolute top-4 right-4 w-8 h-8 rounded-full bg-black/50 text-white flex items-center justify-center hover:bg-black/70 transition-colors">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
            <img src={lightboxUrl} alt="" className="max-w-[90vw] max-h-[90vh] object-contain rounded-lg shadow-2xl" onClick={e => e.stopPropagation()} />
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Shared constants & helpers ──────────────────────────────────────────────
const cap = (s: string) => s ? s.charAt(0).toUpperCase() + s.slice(1).replace(/_/g, ' ') : '';
const SEVERITY_PILL: Record<string, string> = { critical: 'bg-red-500 text-white', high: 'bg-orange-400 text-white', medium: 'bg-yellow-400 text-gray-900', low: 'bg-gray-200 text-gray-600' };
const BUG_STATUS_DOT: Record<string, string> = { reported: 'bg-gray-400', acknowledged: 'bg-blue-400', in_progress: 'bg-blue-500', testing: 'bg-yellow-500', fixed: 'bg-green-500', verified: 'bg-green-600', closed: 'bg-gray-500', wont_fix: 'bg-gray-500', duplicate: 'bg-gray-400' };
const BUG_STATUS_STYLE: Record<string, { bg: string; text: string }> = {
  reported: { bg: 'bg-gray-100', text: 'text-gray-700' }, acknowledged: { bg: 'bg-blue-100', text: 'text-blue-700' },
  in_progress: { bg: 'bg-blue-100', text: 'text-blue-700' }, testing: { bg: 'bg-yellow-100', text: 'text-yellow-700' },
  fixed: { bg: 'bg-green-100', text: 'text-green-700' }, verified: { bg: 'bg-green-100', text: 'text-green-700' },
  closed: { bg: 'bg-gray-100', text: 'text-gray-500' }, wont_fix: { bg: 'bg-gray-100', text: 'text-gray-500' },
  duplicate: { bg: 'bg-gray-100', text: 'text-gray-500' },
};
const BUG_STATUSES = ['reported', 'acknowledged', 'in_progress', 'testing', 'fixed', 'verified', 'closed', 'wont_fix', 'duplicate'];
const TASK_STATUS_DOT: Record<string, string> = { todo: 'bg-gray-400', in_progress: 'bg-blue-500', blocked: 'bg-red-500', review: 'bg-yellow-500', done: 'bg-green-500' };

// ─── Bug modal panel ────────────────────────────────────────────────────────
const BugPanel: React.FC<{
  bugId: string;
  projectMembers?: any[];
  onClose: () => void;
  onRefresh: () => void;
}> = ({ bugId, projectMembers = [], onClose, onRefresh }) => {
  const [bug, setBug] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [confirmDel, setConfirmDel] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const fetchBug = useCallback(async () => {
    try {
      const res = await api.get(`/bugs/${bugId}`);
      setBug(res.data?.data);
    } catch { /* ignore */ }
    setLoading(false);
  }, [bugId]);

  useEffect(() => { fetchBug(); }, [fetchBug]);

  const saveBug = (field: string, val: any) => {
    if (field === 'status') {
      api.put(`/bugs/${bugId}/status`, { status: val }).then(() => { fetchBug(); onRefresh(); }).catch((err: any) => setErrorMsg(err.response?.data?.message || 'Failed'));
    } else {
      api.put(`/bugs/${bugId}`, { [field]: val }).then(() => { fetchBug(); onRefresh(); }).catch((err: any) => setErrorMsg(err.response?.data?.message || 'Failed'));
    }
  };

  const handleAssignBug = async (userId: string, name: string) => {
    try {
      await api.post(`/bugs/${bugId}/assign`, { userId, name });
      fetchBug();
      onRefresh();
    } catch (err: any) { setErrorMsg(err.response?.data?.message || 'Failed to assign'); }
  };

  const handleUnassignBug = async (userId: string) => {
    try {
      await api.post(`/bugs/${bugId}/unassign`, { userId });
      fetchBug();
      onRefresh();
    } catch (err: any) { setErrorMsg(err.response?.data?.message || 'Failed to unassign'); }
  };

  const fmtD = (d?: string) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '';
  const ss = BUG_STATUS_STYLE[bug?.status] || BUG_STATUS_STYLE.reported;

  return (
    <div className="!mt-0 fixed inset-0 z-40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/30" style={{ animation: 'backdropFadeIn 300ms ease-out' }} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-[90vw] h-[90vh] flex flex-col overflow-hidden border border-gray-200" style={{ animation: 'modalFadeIn 300ms ease-out' }} onClick={e => e.stopPropagation()}>

        {loading || !bug ? (
          <div className="flex-1 flex items-center justify-center">
            {loading ? <div className="animate-spin h-5 w-5 border-2 border-red-400 border-t-transparent rounded-full" /> : (
              <div className="text-center"><p className="text-sm text-gray-500">Bug not found</p><button onClick={onClose} className="text-xs text-blue-600 mt-2">Close</button></div>
            )}
          </div>
        ) : (<>

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100 shrink-0">
          <div className="flex items-center gap-2.5 text-xs">
            <button onClick={() => setConfirmDel(true)} className="text-xs text-gray-400 hover:text-red-500 transition-colors mr-2">Delete</button>
            <span className={`px-2.5 py-1 rounded-full font-medium ${ss.bg} ${ss.text}`}>{cap(bug.status)}</span>
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-100 text-red-700 font-semibold">BUG</span>
            {bug.impact?.severity && <span className={`text-[10px] px-2 py-0.5 rounded-full ${SEVERITY_PILL[bug.impact.severity] || 'bg-gray-200 text-gray-600'}`}>{cap(bug.impact.severity)}</span>}
            <span className="text-gray-400">Reported {fmtD(bug.reportedAt)}</span>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5">
          {/* Title */}
          <input defaultValue={bug.title} onBlur={e => { if (e.target.value.trim() !== bug.title) saveBug('title', e.target.value.trim()); }}
            className="w-full text-xl font-semibold text-gray-900 border-none focus:outline-none focus:ring-0 p-0 mb-5" />

          {/* Properties grid */}
          <div className="grid grid-cols-2 gap-x-8 gap-y-3.5 mb-6">
            {/* Severity */}
            <div className="flex items-center gap-3">
              <span className="text-xs text-gray-400 w-24">Severity</span>
              <select value={bug.impact?.severity || 'medium'} onChange={e => saveBug('impact', { ...bug.impact, severity: e.target.value })}
                className="text-xs border border-gray-200 rounded-lg px-2.5 py-1 cursor-pointer focus:outline-none focus:ring-1 focus:ring-red-400">
                <option value="low">🟢 Low</option>
                <option value="medium">🟡 Medium</option>
                <option value="high">🟠 High</option>
                <option value="critical">🔴 Critical</option>
              </select>
            </div>
            {/* Priority */}
            <div className="flex items-center gap-3">
              <span className="text-xs text-gray-400 w-24">Priority</span>
              <select value={bug.priority || 'medium'} onChange={e => saveBug('priority', e.target.value)}
                className="text-xs border border-gray-200 rounded-lg px-2.5 py-1 cursor-pointer focus:outline-none focus:ring-1 focus:ring-red-400">
                <option value="low">🟢 Low</option>
                <option value="medium">🟡 Medium</option>
                <option value="high">🟠 High</option>
                <option value="critical">🔴 Critical</option>
              </select>
            </div>
            {/* Reporter */}
            <div className="flex items-center gap-3">
              <span className="text-xs text-gray-400 w-24">Reporter</span>
              <span className="text-xs text-gray-700">{bug.reportedBy?.name || 'Unknown'}</span>
            </div>
            {/* Assignees */}
            <div className="flex items-start gap-3 col-span-2">
              <span className="text-xs text-gray-400 w-24 mt-0.5">Assigned To</span>
              <div className="flex-1">
                <div className="flex flex-wrap gap-1.5 mb-1.5">
                  {(bug.assignedTo || []).length > 0 ? (bug.assignedTo || []).map((a: any) => (
                    <span key={a.userId} className="inline-flex items-center gap-1 text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full group/assignee">
                      <span className="w-4 h-4 rounded-full bg-blue-500 text-white text-[9px] flex items-center justify-center shrink-0">{a.name?.[0]?.toUpperCase()}</span>
                      {a.name}
                      <button onClick={() => handleUnassignBug(a.userId)} className="ml-0.5 text-blue-400 hover:text-red-500 opacity-0 group-hover/assignee:opacity-100 transition-opacity" title="Remove">
                        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                      </button>
                    </span>
                  )) : <span className="text-xs text-gray-400 italic">Unassigned</span>}
                </div>
                {projectMembers.length > 0 && (
                  <select
                    value=""
                    onChange={e => {
                      const m = projectMembers.find((pm: any) => pm.userId === e.target.value);
                      if (m) handleAssignBug(m.userId, m.name);
                    }}
                    className="text-xs border border-gray-200 rounded-lg px-2 py-1 cursor-pointer focus:outline-none focus:ring-1 focus:ring-red-400"
                  >
                    <option value="">+ Assign member...</option>
                    {projectMembers
                      .filter((m: any) => !(bug.assignedTo || []).some((a: any) => a.userId?.toString() === m.userId))
                      .map((m: any) => <option key={m.userId} value={m.userId}>{m.name}</option>)}
                  </select>
                )}
              </div>
            </div>
            {/* Timestamps */}
            {bug.acknowledgedAt && <div className="flex items-center gap-3"><span className="text-xs text-gray-400 w-24">Acknowledged</span><span className="text-xs text-gray-600">{fmtD(bug.acknowledgedAt)}</span></div>}
            {bug.resolvedAt && <div className="flex items-center gap-3"><span className="text-xs text-gray-400 w-24">Resolved</span><span className="text-xs text-gray-600">{fmtD(bug.resolvedAt)}</span></div>}
            {bug.verifiedAt && <div className="flex items-center gap-3"><span className="text-xs text-gray-400 w-24">Verified</span><span className="text-xs text-gray-600">{fmtD(bug.verifiedAt)}</span></div>}
            {bug.closedAt && <div className="flex items-center gap-3"><span className="text-xs text-gray-400 w-24">Closed</span><span className="text-xs text-gray-600">{fmtD(bug.closedAt)}</span></div>}
          </div>

          {/* Description */}
          <div className="mb-5">
            <div className="text-xs font-medium text-gray-500 mb-1.5">Description</div>
            <textarea defaultValue={bug.description} onBlur={e => { if (e.target.value !== (bug.description || '')) saveBug('description', e.target.value); }}
              placeholder="Add description..."
              className="w-full text-sm text-gray-700 border border-gray-200 rounded-lg px-3 py-2.5 focus:outline-none focus:border-red-400 resize-none" rows={3} />
          </div>

          {/* Impact */}
          {bug.impact?.affectedFeatures?.length > 0 && (
            <div className="mb-5">
              <div className="text-xs font-medium text-gray-500 mb-1.5">Affected Features</div>
              <div className="flex flex-wrap gap-1">
                {bug.impact.affectedFeatures.map((f: string, i: number) => (
                  <span key={i} className="text-[10px] px-2 py-0.5 bg-red-50 text-red-600 rounded-full">{f}</span>
                ))}
              </div>
            </div>
          )}

          {/* Reproduction Steps */}
          {bug.reproductionSteps?.steps?.length > 0 && (
            <div className="mb-5">
              <div className="text-xs font-medium text-gray-500 mb-1.5">Steps to Reproduce</div>
              <ol className="list-decimal list-inside space-y-1">
                {bug.reproductionSteps.steps.map((s: string, i: number) => (
                  <li key={i} className="text-sm text-gray-700">{s}</li>
                ))}
              </ol>
              {bug.reproductionSteps.environment && (
                <p className="text-xs text-gray-400 mt-2">Environment: {bug.reproductionSteps.environment}</p>
              )}
            </div>
          )}

          {/* Resolution */}
          {bug.resolution && (
            <div className="mb-5">
              <div className="text-xs font-medium text-gray-500 mb-1.5">Resolution</div>
              <p className="text-sm text-gray-700">{bug.resolution.description}</p>
              {bug.resolution.fixedBy && <p className="text-xs text-gray-400 mt-1">Fixed by: {bug.resolution.fixedBy.name}</p>}
            </div>
          )}

          {/* Create Test Plan from Bug */}
          {(bug.owningProjectId || bug.reportedInProjectId) && (
            <div className="mb-5">
              <button
                onClick={async () => {
                  try {
                    await qaTestPlanAPI.createFromBug({ bugId: bug._id, projectId: bug.owningProjectId || bug.reportedInProjectId });
                    onRefresh();
                  } catch (err: any) { setErrorMsg(err.response?.data?.message || 'Failed to create test plan'); }
                }}
                className="text-xs bg-purple-50 text-purple-700 border border-purple-200 px-3 py-2 rounded-lg hover:bg-purple-100 transition-colors flex items-center gap-1.5"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9.75 3.104v5.714a2.25 2.25 0 01-.659 1.591L5 14.5M9.75 3.104c-.251.023-.501.05-.75.082m.75-.082a24.301 24.301 0 014.5 0m0 0v5.714c0 .597.237 1.17.659 1.591L19.8 15.3M14.25 3.104c.251.023.501.05.75.082M19.8 15.3l-1.57.393A9.065 9.065 0 0112 15a9.065 9.065 0 00-6.23.693L5 14.5m14.8.8l1.402 1.402c1.232 1.232.65 3.318-1.067 3.611A48.309 48.309 0 0112 21c-2.773 0-5.491-.235-8.135-.687-1.718-.293-2.3-2.379-1.067-3.61L5 14.5" />
                </svg>
                Create Test Plan from Bug
              </button>
            </div>
          )}

          {/* Status History */}
          {bug.statusHistory?.length > 0 && (
            <div className="mb-5">
              <div className="text-xs font-medium text-gray-500 mb-2">Status History</div>
              <div className="space-y-1.5">
                {[...bug.statusHistory].reverse().map((h: any, i: number) => (
                  <div key={i} className="flex items-center gap-2 text-xs">
                    <span className="text-gray-400 w-28 shrink-0">{new Date(h.changedAt).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                    <span className="text-gray-500">{h.changedByName}</span>
                    <span className="text-gray-400">moved</span>
                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${BUG_STATUS_STYLE[h.from]?.bg || 'bg-gray-100'} ${BUG_STATUS_STYLE[h.from]?.text || 'text-gray-600'}`}>{cap(h.from)}</span>
                    <svg className="w-3 h-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" /></svg>
                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${BUG_STATUS_STYLE[h.to]?.bg || 'bg-gray-100'} ${BUG_STATUS_STYLE[h.to]?.text || 'text-gray-600'}`}>{cap(h.to)}</span>
                    {h.notes && <span className="text-gray-400 truncate max-w-[200px]" title={h.notes}>— {h.notes}</span>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Status stepper — sticky bottom */}
        {(() => {
          const flow = ['reported', 'acknowledged', 'in_progress', 'testing', 'fixed', 'verified', 'closed'];
          const currentIdx = flow.indexOf(bug.status);
          const isTerminal = bug.status === 'wont_fix' || bug.status === 'duplicate';
          const nextStatus = !isTerminal && currentIdx < flow.length - 1 ? flow[currentIdx + 1] : null;
          const isClosed = bug.status === 'closed';
          return (
            <div className="border-t border-gray-200 bg-gray-50 px-5 py-3 shrink-0 rounded-b-2xl">
              {/* Action button */}
              <div className="flex items-center gap-2 mb-3">
                {nextStatus && (
                  <button onClick={() => saveBug('status', nextStatus)}
                    className="flex-1 bg-blue-600 text-white text-xs font-medium py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-1.5">
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" /></svg>
                    Mark as {cap(nextStatus)}
                  </button>
                )}
                {isClosed && (
                  <div className="flex-1 bg-green-50 text-green-700 text-xs font-medium py-2 rounded-lg flex items-center justify-center gap-1.5">
                    <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" /></svg>
                    Bug Closed
                  </div>
                )}
                {isTerminal && (
                  <div className="flex-1 bg-gray-100 text-gray-600 text-xs font-medium py-2 rounded-lg flex items-center justify-center gap-1.5">
                    {cap(bug.status)}
                    <button onClick={() => saveBug('status', 'reported')} className="ml-2 text-[10px] text-blue-600 hover:text-blue-700 underline">Reopen</button>
                  </div>
                )}
                {['wont_fix', 'duplicate'].map(s => (
                  bug.status !== s && !isClosed && (
                    <button key={s} onClick={() => saveBug('status', s)}
                      className="text-[10px] px-3 py-2 rounded-lg bg-gray-100 text-gray-500 hover:bg-gray-200 transition-colors whitespace-nowrap">
                      {cap(s)}
                    </button>
                  )
                ))}
              </div>

              {/* Progress stepper */}
              <div className="flex items-center gap-1">
                {flow.map((s, i) => {
                  const isPast = i < currentIdx;
                  const isCurrent = i === currentIdx && !isTerminal;
                  const dot = BUG_STATUS_DOT[s] || 'bg-gray-300';
                  return (
                    <React.Fragment key={s}>
                      {i > 0 && <div className={`flex-1 h-0.5 ${isPast ? 'bg-green-400' : 'bg-gray-200'}`} />}
                      <div className="flex flex-col items-center gap-0.5 px-0.5">
                        <div className={`w-4 h-4 rounded-full flex items-center justify-center text-[8px] font-bold border-2 transition-all ${
                          isCurrent ? `${dot} border-transparent text-white ring-2 ring-offset-1 ring-blue-400` :
                          isPast ? 'bg-green-500 border-green-500 text-white' :
                          'bg-white border-gray-300 text-gray-400'
                        }`}>
                          {isPast ? '✓' : i + 1}
                        </div>
                        <span className={`text-[8px] whitespace-nowrap ${isCurrent ? 'text-gray-900 font-semibold' : isPast ? 'text-green-600' : 'text-gray-400'}`}>{cap(s)}</span>
                      </div>
                    </React.Fragment>
                  );
                })}
              </div>
            </div>
          );
        })()}

        </>)}

        {/* Error dialog */}
        {errorMsg && (
          <div className="absolute inset-0 bg-black/30 flex items-center justify-center z-10 rounded-2xl" onClick={() => setErrorMsg(null)}>
            <div className="bg-white rounded-xl shadow-xl p-5 w-full max-w-sm mx-4" onClick={e => e.stopPropagation()}>
              <h3 className="text-sm font-semibold text-gray-900 mb-1">Error</h3>
              <p className="text-xs text-gray-600 mb-3">{errorMsg}</p>
              <div className="flex justify-end"><button onClick={() => setErrorMsg(null)} className="text-xs text-blue-600 font-medium">OK</button></div>
            </div>
          </div>
        )}

        {/* Delete confirmation */}
        {confirmDel && (
          <div className="absolute inset-0 bg-black/30 flex items-center justify-center z-10 rounded-2xl" onClick={() => setConfirmDel(false)}>
            <div className="bg-white rounded-xl shadow-xl p-5 w-full max-w-xs mx-4" onClick={e => e.stopPropagation()}>
              <div className="mb-3">
                <h3 className="text-sm font-semibold text-gray-900">Delete Bug</h3>
                <p className="text-xs text-gray-500 mt-0.5">This action cannot be undone.</p>
              </div>
              <div className="flex justify-end gap-2">
                <button onClick={() => setConfirmDel(false)} className="px-3 py-1.5 text-xs text-gray-600 hover:bg-gray-100 rounded-lg">Cancel</button>
                <button onClick={() => { api.delete(`/bugs/${bugId}`).then(() => { onClose(); onRefresh(); }).catch(() => {}); }}
                  className="px-3 py-1.5 text-xs bg-red-600 text-white rounded-lg hover:bg-red-700">Delete</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Feature slide-in panel ──────────────────────────────────────────────────
const FEAT_STATUSES = ['planning', 'development', 'testing', 'done', 'deployed', 'archived'];
const FEAT_STATUS_STYLE: Record<string, { bg: string; text: string }> = {
  planning: { bg: 'bg-gray-100', text: 'text-gray-700' }, development: { bg: 'bg-blue-100', text: 'text-blue-700' },
  testing: { bg: 'bg-yellow-100', text: 'text-yellow-700' }, done: { bg: 'bg-green-100', text: 'text-green-700' },
  deployed: { bg: 'bg-emerald-100', text: 'text-emerald-700' }, archived: { bg: 'bg-gray-100', text: 'text-gray-500' },
};

const FeaturePanel: React.FC<{
  featureId: string;
  onClose: () => void;
  onRefresh: () => void;
  onOpenTask: (taskId: string) => void;
}> = ({ featureId, onClose, onRefresh, onOpenTask }) => {
  const [feature, setFeature] = useState<any>(null);
  const [featureTasks, setFeatureTasks] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [addingTask, setAddingTask] = useState(false);
  const [newTaskTitle, setNewTaskTitle] = useState('');
  const [confirmDel, setConfirmDel] = useState(false);
  // Approval flow
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiResult, setAiResult] = useState<any>(null);
  const [approvalMsg, setApprovalMsg] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [editFeatureSP, setEditFeatureSP] = useState(0);
  const [editTasks, setEditTasks] = useState<any[]>([]);
  const [disputeText, setDisputeText] = useState('');

  const fetchFeature = useCallback(async () => {
    try {
      const res = await api.get(`/features/${featureId}?includeTasks=true`);
      const data = res.data?.data;
      setFeature(data?.feature || data);
      setFeatureTasks(data?.tasks || []);
    } catch { /* ignore */ }
    setLoading(false);
  }, [featureId]);

  useEffect(() => { fetchFeature(); }, [fetchFeature]);

  const saveFeature = (field: string, val: any) => {
    if (field === 'startDate' || field === 'endDate') {
      const dates: any = {};
      if (field === 'startDate') dates.startDate = val || undefined;
      if (field === 'endDate') dates.estimatedEndDate = val || undefined;
      featureAPI.update(featureId, { dates }).then(() => { fetchFeature(); onRefresh(); }).catch(() => {});
    } else {
      featureAPI.update(featureId, { [field]: val }).then(() => { fetchFeature(); onRefresh(); }).catch(() => {});
    }
  };

  const handleAddTask = async () => {
    if (!newTaskTitle.trim()) return;
    const title = newTaskTitle.trim();

    // Optimistic: add immediately to local list
    const tempTask = { _id: `temp_${Date.now()}`, title, status: 'todo', parentType: 'feature', parentId: featureId, assignedTo: [], storyPoints: 0 };
    setFeatureTasks(prev => [...prev, tempTask]);
    setNewTaskTitle('');

    try {
      await taskAPI.createTask({ title, description: title, status: 'todo', parentType: 'feature', parentId: featureId } as any);
    } catch { /* ignore */ }

    // Silent refresh — optimistic task gets replaced by real data
    fetchFeature();
    onRefresh();
  };

  // Check if tasks changed since last AI estimation (compare IDs, not just count)
  const tasksMatchAi = useCallback(() => {
    const storedAi = feature?.aiEstimation;
    if (!storedAi?.tasks?.length) return false;
    const aiTaskIds = new Set(storedAi.tasks.map((t: any) => t.taskId));
    const currentTaskIds = featureTasks.filter((t: any) => !t._id.startsWith('temp_')).map((t: any) => t._id);
    if (currentTaskIds.length !== aiTaskIds.size) return false;
    return currentTaskIds.every((id: string) => aiTaskIds.has(id));
  }, [feature?.aiEstimation, featureTasks]);

  const handleOpenApproval = async () => {
    if (featureTasks.length === 0) {
      setApprovalMsg('Add at least one task before submitting');
      return;
    }
    setApprovalMsg('');
    setEditFeatureSP(feature?.storyPoints ?? 0);
    setEditTasks(featureTasks.map((t: any) => ({ ...t, _editSP: t.storyPoints ?? 0 })));
    setDisputeText('');
    setShowApprovalModal(true);

    // Use cached AI estimation if tasks haven't changed
    const storedAi = feature?.aiEstimation;
    if (tasksMatchAi() && (feature?.storyPoints ?? 0) > 0) {
      setAiResult(storedAi);
      setEditFeatureSP(storedAi.featurePoints);
      setEditTasks(prev => prev.map((t: any) => {
        const at = (storedAi.tasks ?? []).find((a: any) => a.taskId === t._id);
        return at ? { ...t, _editSP: at.points, _aiReasoning: at.reasoning } : t;
      }));
      return;
    }

    // Tasks changed — run AI estimation
    setAiLoading(true); setAiResult(null);
    try {
      const res = await aiAPI.estimateStoryPoints(featureId, true);
      const d = res.data;
      if (d.fallback) {
        setApprovalMsg(d.message || 'AI unavailable');
      } else if (d.data) {
        setAiResult(d.data);
        setEditFeatureSP(d.data.featurePoints);
        setEditTasks(prev => prev.map((t: any) => {
          const at = d.data.tasks.find((a: any) => a.taskId === t._id);
          return at ? { ...t, _editSP: at.points, _aiReasoning: at.reasoning } : t;
        }));
      }
    } catch { setApprovalMsg('AI estimation failed'); }
    setAiLoading(false);
  };

  const handleConfirmSubmit = async () => {
    setSubmitting(true);
    try {
      // Save edited SPs if manual override
      if (feature?.manualOverride) {
        for (const t of editTasks) {
          if (t._editSP !== undefined && t._editSP !== (t.storyPoints ?? 0)) {
            await taskAPI.updateTask(t._id, { storyPoints: t._editSP } as any);
          }
        }
        if (editFeatureSP !== (feature.storyPoints ?? 0)) {
          await featureAPI.update(featureId, { storyPoints: editFeatureSP } as any);
        }
      }

      // If disputing
      if (!feature?.manualOverride && disputeText?.trim()) {
        await featureAPI.submitForApproval(featureId);
        await featureAPI.dispute(featureId, { reason: disputeText.trim() });
      } else {
        await featureAPI.submitForApproval(featureId);
      }

      setShowApprovalModal(false);
      setAiResult(null);
      fetchFeature();
      onRefresh();
    } catch (err: any) {
      setApprovalMsg(err.response?.data?.message || 'Failed to submit');
    }
    setSubmitting(false);
  };

  const doneTasks = featureTasks.filter((t: any) => t.status === 'done').length;
  const pct = featureTasks.length > 0 ? Math.round((doneTasks / featureTasks.length) * 100) : 0;
  const as = feature?.approvalStatus;
  const ss = FEAT_STATUS_STYLE[feature?.status] || FEAT_STATUS_STYLE.planning;

  return (
    <div className="!mt-0 fixed inset-0 z-40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/30" style={{ animation: 'backdropFadeIn 300ms ease-out' }} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-[90vw] h-[90vh] flex flex-col overflow-hidden border border-gray-200" style={{ animation: 'modalFadeIn 300ms ease-out' }} onClick={e => e.stopPropagation()}>

        {loading || !feature ? (
          <div className="flex items-center justify-center py-20">
            {loading ? <div className="animate-spin h-5 w-5 border-2 border-blue-500 border-t-transparent rounded-full" /> : (
              <div className="text-center"><p className="text-sm text-gray-500">Feature not found</p><button onClick={onClose} className="text-xs text-blue-600 mt-2">Close</button></div>
            )}
          </div>
        ) : (<>

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100 shrink-0">
          <div className="flex items-center gap-2.5 text-xs">
            <button onClick={() => setConfirmDel(true)} className="text-xs text-gray-400 hover:text-red-500 transition-colors mr-2">Delete</button>
            <span className={`px-2.5 py-1 rounded-full font-medium ${ss.bg} ${ss.text} capitalize`}>{feature.status}</span>
            <svg className="w-3.5 h-3.5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z" /></svg>
            <span className="text-gray-400">Feature</span>
            {(feature.storyPoints ?? 0) > 0 && <><span className="text-gray-300">·</span><span className="text-blue-600 font-medium">{feature.storyPoints} SP</span></>}
            {as && as !== 'not_required' && (
              <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${
                as === 'approved' ? 'bg-green-100 text-green-700' : as === 'pending_approval' ? 'bg-yellow-100 text-yellow-700'
                : as === 'rejected' ? 'bg-red-100 text-red-700' : as === 'disputed' ? 'bg-orange-100 text-orange-700' : ''
              }`}>{as === 'approved' ? '✓ Approved' : as === 'pending_approval' ? '⏳ Pending' : as === 'rejected' ? '✗ Rejected' : '⚠ Disputed'}</span>
            )}
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5">
          {/* Name */}
          <input defaultValue={feature.name} onBlur={e => { if (e.target.value.trim() !== feature.name) saveFeature('name', e.target.value.trim()); }}
            className="w-full text-xl font-semibold text-gray-900 border-none focus:outline-none focus:ring-0 p-0 mb-5" />

          {/* Properties grid */}
          <div className="grid grid-cols-2 gap-x-8 gap-y-3.5 mb-6">
            {/* Status */}
            <div className="flex items-center gap-3">
              <span className="text-xs text-gray-400 w-24">Status</span>
              <select value={feature.status} onChange={e => saveFeature('status', e.target.value)}
                className={`text-xs rounded-lg px-2.5 py-1 border-0 cursor-pointer focus:outline-none focus:ring-1 focus:ring-blue-400 font-medium ${ss.bg} ${ss.text}`}>
                {FEAT_STATUSES.map(s => <option key={s} value={s} className="capitalize">{s}</option>)}
              </select>
            </div>
            {/* Priority */}
            <div className="flex items-center gap-3">
              <span className="text-xs text-gray-400 w-24">Priority</span>
              <select value={feature.priority || ''} onChange={e => saveFeature('priority', e.target.value)}
                className="text-xs border border-gray-200 rounded-lg px-2.5 py-1 cursor-pointer focus:outline-none focus:ring-1 focus:ring-blue-400">
                <option value="">None</option>
                <option value="low">🟢 Low</option>
                <option value="medium">🟡 Medium</option>
                <option value="high">🟠 High</option>
                <option value="critical">🔴 Critical</option>
              </select>
            </div>
            {/* Start Date */}
            <div className="flex items-center gap-3">
              <span className="text-xs text-gray-400 w-24">Start Date</span>
              <input type="date" value={feature.dates?.startDate?.slice?.(0, 10) || ''} onChange={e => saveFeature('startDate', e.target.value)}
                className="text-xs border border-gray-200 rounded-lg px-2 py-1 focus:outline-none focus:border-blue-400" />
            </div>
            {/* End Date */}
            <div className="flex items-center gap-3">
              <span className="text-xs text-gray-400 w-24">End Date</span>
              <input type="date" value={feature.dates?.estimatedEndDate?.slice?.(0, 10) || ''} onChange={e => saveFeature('endDate', e.target.value)}
                className="text-xs border border-gray-200 rounded-lg px-2 py-1 focus:outline-none focus:border-blue-400" />
            </div>
            {/* Story Points */}
            <div className="flex items-center gap-3">
              <span className="text-xs text-gray-400 w-24">Story Points</span>
              <span className="text-xs text-blue-600 font-medium">{feature.storyPoints || 0} SP</span>
            </div>
            {/* Approval */}
            <div className="flex items-center gap-3">
              <span className="text-xs text-gray-400 w-24">Approval</span>
              <span className="text-xs text-gray-600">{cap(as || 'not required')}</span>
            </div>
          </div>

          {/* Description */}
          <div className="mb-6">
            <div className="text-xs font-medium text-gray-500 mb-1.5">Description</div>
            <textarea defaultValue={feature.description} onBlur={e => { if (e.target.value !== (feature.description || '')) saveFeature('description', e.target.value); }}
              placeholder="Add description..."
              className="w-full text-sm text-gray-700 border border-gray-200 rounded-lg px-3 py-2.5 focus:outline-none focus:border-blue-400 resize-none" rows={3} />
          </div>

          {/* Tasks */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs font-medium text-gray-500 flex items-center gap-2">
                Tasks {featureTasks.length > 0 && <span className="text-gray-400 font-normal">({doneTasks}/{featureTasks.length})</span>}
              </div>
            </div>

            {/* Progress */}
            {featureTasks.length > 0 && (
              <div className="flex items-center gap-2 mb-2">
                <div className="flex-1 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                  <div className={`h-full rounded-full transition-all ${pct === 100 ? 'bg-green-500' : 'bg-blue-500'}`} style={{ width: `${pct}%` }} />
                </div>
                <span className="text-[10px] text-gray-400 w-8">{pct}%</span>
              </div>
            )}

            {/* Task list */}
            <div className="space-y-0.5">
              {featureTasks.map((t: any) => (
                <div key={t._id} className="flex items-center gap-2 py-1.5 group/ft hover:bg-gray-50 rounded px-1 -mx-1">
                  <button onClick={() => taskAPI.updateStatus(t._id, t.status === 'done' ? 'todo' : 'done').then(() => { fetchFeature(); onRefresh(); }).catch(() => {})}
                    className={`w-4 h-4 rounded border-2 shrink-0 transition-all ${t.status === 'done' ? 'bg-green-500 border-green-500' : 'border-gray-300 hover:border-green-400'}`} />
                  <span onClick={() => onOpenTask(t._id)} className={`text-sm flex-1 cursor-pointer hover:text-blue-600 ${t.status === 'done' ? 'line-through text-gray-400' : 'text-gray-700'}`}>{t.title}</span>
                  {t.priority && <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${SEVERITY_PILL[t.priority] || 'bg-gray-200 text-gray-600'}`}>{t.priority}</span>}
                  {(t.storyPoints ?? 0) > 0 && <span className="text-[10px] text-blue-600 font-medium">{t.storyPoints} SP</span>}
                  <select value={t.status} onChange={e => taskAPI.updateStatus(t._id, e.target.value).then(() => { fetchFeature(); onRefresh(); }).catch(() => {})}
                    className="text-[10px] border-0 bg-transparent text-gray-400 cursor-pointer focus:outline-none opacity-0 group-hover/ft:opacity-100 transition-opacity capitalize">
                    <option value="todo">To Do</option><option value="in_progress">In Progress</option><option value="blocked">Blocked</option><option value="review">Review</option><option value="done">Done</option>
                  </select>
                </div>
              ))}
            </div>

            {/* Add task */}
            {addingTask ? (
              <div className="flex items-center gap-2 mt-1.5">
                <input autoFocus value={newTaskTitle} onChange={e => setNewTaskTitle(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') handleAddTask(); if (e.key === 'Escape') { setAddingTask(false); setNewTaskTitle(''); } }}
                  placeholder="Task title" className="flex-1 text-sm py-1 px-0 border-0 border-b border-gray-200 focus:border-blue-500 focus:outline-none bg-transparent" />
                <button onClick={handleAddTask} className="text-xs text-blue-600 hover:text-blue-700">Add</button>
                <button onClick={() => { setAddingTask(false); setNewTaskTitle(''); }} className="text-xs text-gray-400">Cancel</button>
              </div>
            ) : (
              <button onClick={() => setAddingTask(true)} className="text-xs text-gray-400 hover:text-blue-600 mt-1.5 flex items-center gap-1">
                <span className="text-sm leading-none">+</span> Add task
              </button>
            )}
          </div>

          {approvalMsg && (
            <div className="mb-6 bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-xs text-yellow-700">{approvalMsg}</div>
          )}
        </div>

        {/* Approval footer — sticky bottom */}
        {(() => {
          const canSubmit = !as || as === 'not_required' || as === 'rejected';
          return (
            <div className="border-t border-gray-200 bg-gray-50 px-5 py-3 shrink-0 rounded-b-2xl">
              {as === 'approved' && (
                <div className="flex items-center gap-2 text-xs text-green-700 bg-green-50 rounded-lg px-3 py-2">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" /></svg>
                  Feature Approved — {feature.storyPoints || 0} SP
                </div>
              )}
              {as === 'pending_approval' && (
                <div className="flex items-center gap-2 text-xs text-yellow-700 bg-yellow-50 rounded-lg px-3 py-2">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" /></svg>
                  Pending Approval — waiting for admin review
                </div>
              )}
              {as === 'disputed' && (
                <div className="flex items-center gap-2 text-xs text-orange-700 bg-orange-50 rounded-lg px-3 py-2">Disputed — waiting for admin review</div>
              )}
              {as === 'rejected' && feature.rejectionReason && (
                <div className="flex items-start gap-2 text-xs text-red-700 bg-red-50 rounded-lg px-3 py-2 mb-2">Rejected: {feature.rejectionReason}</div>
              )}
              {canSubmit && (
                <button onClick={handleOpenApproval} className="w-full bg-yellow-500 text-white text-xs font-medium py-2.5 rounded-lg hover:bg-yellow-600 transition-colors flex items-center justify-center gap-1.5">
                  <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20"><path d="M10 2a1 1 0 011 1v1.323l3.954 1.582 1.599-.8a1 1 0 01.894 1.79l-1.233.616 1.738 5.42a1 1 0 01-.285 1.05A3.989 3.989 0 0115 15a3.989 3.989 0 01-2.667-1.019 1 1 0 01-.285-1.05l1.715-5.349L10 6.158 6.237 7.582l1.715 5.349a1 1 0 01-.285 1.05A3.989 3.989 0 015 15a3.989 3.989 0 01-2.667-1.019 1 1 0 01-.285-1.05l1.738-5.42-1.233-.617a1 1 0 01.894-1.788l1.599.799L9 4.323V3a1 1 0 011-1z" /></svg>
                  Submit for Approval
                </button>
              )}
            </div>
          );
        })()}

        {/* ── Approval modal with sliders ─────────────────────── */}
        {showApprovalModal && (() => {
          const isAiLocked = !feature.manualOverride;
          const currentSP = editFeatureSP;
          const taskSPSum = editTasks.filter((t: any) => !t._excluded).reduce((s: number, t: any) => s + (t._editSP ?? t.storyPoints ?? 0), 0);
          const overBudget = currentSP > 0 && taskSPSum > currentSP;
          const ready = currentSP > 0 && editTasks.length > 0 && !overBudget;

          return (
            <div className="absolute inset-0 bg-black/40 flex items-center justify-center z-20 rounded-2xl" onClick={() => setShowApprovalModal(false)}>
              <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl mx-4 max-h-[80vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
                <div className="p-5 overflow-y-auto flex-1 space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold text-gray-900">Submit for Approval</h3>
                    {!aiLoading && !isAiLocked && !tasksMatchAi() && (
                      <button onClick={async () => {
                        setAiLoading(true);
                        try {
                          const res = await aiAPI.estimateStoryPoints(featureId, true);
                          if (res.data?.data) {
                            setAiResult(res.data.data); setEditFeatureSP(res.data.data.featurePoints);
                            setEditTasks(prev => prev.map((t: any) => { const at = res.data.data.tasks.find((a: any) => a.taskId === t._id); return at ? { ...t, _editSP: at.points, _aiReasoning: at.reasoning } : t; }));
                          }
                        } catch { /* ignore */ }
                        setAiLoading(false);
                      }} className="text-xs text-blue-600 border border-blue-200 px-2.5 py-1 rounded-lg hover:bg-blue-50 flex items-center gap-1">
                        <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20"><path d="M10 2a1 1 0 011 1v1.323l3.954 1.582 1.599-.8a1 1 0 01.894 1.79l-1.233.616 1.738 5.42a1 1 0 01-.285 1.05A3.989 3.989 0 0115 15a3.989 3.989 0 01-2.667-1.019 1 1 0 01-.285-1.05l1.715-5.349L10 6.158 6.237 7.582l1.715 5.349a1 1 0 01-.285 1.05A3.989 3.989 0 015 15a3.989 3.989 0 01-2.667-1.019 1 1 0 01-.285-1.05l1.738-5.42-1.233-.617a1 1 0 01.894-1.788l1.599.799L9 4.323V3a1 1 0 011-1z" /></svg>
                        Re-estimate with AI
                      </button>
                    )}
                  </div>

                  {/* AI status */}
                  {aiLoading && (
                    <div className="flex items-center gap-2 bg-blue-50 border border-blue-100 rounded-lg p-3">
                      <svg className="w-4 h-4 text-blue-500 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>
                      <span className="text-xs text-blue-700">AI is estimating story points...</span>
                    </div>
                  )}
                  {!aiLoading && aiResult && (
                    <div className="flex items-center gap-2 bg-green-50 border border-green-100 rounded-lg p-3">
                      <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20"><path d="M10 2a1 1 0 011 1v1.323l3.954 1.582 1.599-.8a1 1 0 01.894 1.79l-1.233.616 1.738 5.42a1 1 0 01-.285 1.05A3.989 3.989 0 0115 15a3.989 3.989 0 01-2.667-1.019 1 1 0 01-.285-1.05l1.715-5.349L10 6.158 6.237 7.582l1.715 5.349a1 1 0 01-.285 1.05A3.989 3.989 0 015 15a3.989 3.989 0 01-2.667-1.019 1 1 0 01-.285-1.05l1.738-5.42-1.233-.617a1 1 0 01.894-1.788l1.599.799L9 4.323V3a1 1 0 011-1z" /></svg>
                      <span className="text-xs text-green-700">AI estimated {aiResult.featurePoints} SP{isAiLocked ? ' — scores are locked' : ' — you can adjust below'}</span>
                    </div>
                  )}

                  {/* Feature SP */}
                  <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-700">Feature Story Points</span>
                      <div className="flex items-center gap-2">
                        <input type="number" min={0} value={editFeatureSP || ''} onChange={e => setEditFeatureSP(Number(e.target.value) || 0)}
                          disabled={isAiLocked} className={`w-20 border border-gray-300 rounded px-2 py-1 text-sm text-right focus:outline-none focus:ring-1 focus:ring-blue-500 ${isAiLocked ? 'bg-gray-100 cursor-not-allowed' : ''}`} />
                        <span className="text-xs text-gray-400">SP</span>
                      </div>
                    </div>
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-gray-500">Tasks: {taskSPSum} / {currentSP} SP</span>
                      {overBudget && <span className="text-red-600 font-medium">Over by {taskSPSum - currentSP}</span>}
                      {!overBudget && taskSPSum === currentSP && currentSP > 0 && <span className="text-green-600">All assigned</span>}
                      {!overBudget && taskSPSum < currentSP && currentSP > 0 && <span className="text-gray-400">{currentSP - taskSPSum} remaining</span>}
                    </div>
                    {currentSP > 0 && (
                      <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
                        <div className={`h-full rounded-full transition-all ${overBudget ? 'bg-red-500' : taskSPSum === currentSP ? 'bg-green-500' : 'bg-blue-500'}`} style={{ width: `${Math.min(100, currentSP > 0 ? (taskSPSum / currentSP) * 100 : 0)}%` }} />
                      </div>
                    )}
                  </div>

                  {/* Tasks with sliders */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-700">Tasks ({editTasks.length}) {isAiLocked && <span className="text-xs text-blue-500 font-normal ml-1">— AI assigned (read-only)</span>}</span>
                      {!isAiLocked && currentSP > 0 && (
                        <button onClick={() => {
                          const lockedSum = editTasks.reduce((s: number, t: any) => s + (t._locked || t._excluded ? (t._editSP ?? 0) : 0), 0);
                          const pool = Math.max(0, currentSP - lockedSum);
                          const unlocked = editTasks.filter((t: any) => !t._locked && !t._excluded);
                          if (!unlocked.length) return;
                          const per = Math.floor(pool / unlocked.length); const rem = pool % unlocked.length; let idx = 0;
                          setEditTasks(prev => prev.map((t: any) => { if (t._excluded) return { ...t, _editSP: 0 }; if (t._locked) return t; return { ...t, _editSP: per + (idx++ < rem ? 1 : 0) }; }));
                        }} className="text-xs text-blue-600 border border-blue-200 px-2 py-0.5 rounded hover:bg-blue-50">Distribute Equally</button>
                      )}
                    </div>
                    <div className="space-y-1 max-h-60 overflow-y-auto">
                      {editTasks.map((t: any, i: number) => {
                        const sp = t._editSP ?? t.storyPoints ?? 0;
                        const locked = !!t._locked;
                        const excluded = !!t._excluded;
                        const pctVal = currentSP > 0 ? Math.round((sp / currentSP) * 100) : 0;
                        const othersLocked = editTasks.reduce((s: number, pt: any) => pt._id === t._id ? s : (pt._locked || pt._excluded ? s + (pt._editSP ?? 0) : s), 0);
                        const sliderMax = Math.max(0, currentSP - othersLocked);

                        return (
                          <div key={t._id} className={`py-2 px-2 rounded ${excluded ? 'opacity-40' : ''}`}>
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-xs text-gray-400 w-4 text-right shrink-0">{i + 1}.</span>
                              {!isAiLocked && <input type="checkbox" checked={!excluded} onChange={() => setEditTasks(prev => prev.map((pt: any) => pt._id === t._id ? { ...pt, _excluded: !excluded, _editSP: !excluded ? 0 : pt._editSP, _locked: false } : pt))} className="w-3.5 h-3.5 text-blue-600 rounded shrink-0" />}
                              {!excluded && !isAiLocked && (
                                <button onClick={() => setEditTasks(prev => prev.map((pt: any) => pt._id === t._id ? { ...pt, _locked: !locked } : pt))} className={`shrink-0 ${locked ? 'text-amber-500' : 'text-gray-300 hover:text-gray-500'}`}>
                                  <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">{locked ? <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" /> : <path d="M10 2a5 5 0 00-5 5v2a2 2 0 00-2 2v5a2 2 0 002 2h10a2 2 0 002-2v-5a2 2 0 00-2-2H7V7a3 3 0 015.905-.75 1 1 0 001.937-.5A5.002 5.002 0 0010 2z" />}</svg>
                                </button>
                              )}
                              <span className={`text-xs truncate flex-1 ${t.status === 'done' ? 'text-gray-400 line-through' : 'text-gray-700'}`}>{t.title}</span>
                              <span className={`text-xs font-medium w-10 text-right ${locked ? 'text-amber-600' : 'text-blue-600'}`}>{sp}</span>
                            </div>
                            {!excluded && currentSP > 0 && (
                              <div className="flex items-center gap-2 ml-10">
                                <input type="range" min={0} max={sliderMax} value={Math.min(sp, sliderMax)} disabled={locked || isAiLocked}
                                  onChange={e => {
                                    const val = Math.min(Number(e.target.value), sliderMax);
                                    setEditTasks(prev => prev.map((pt: any) => pt._id === t._id ? { ...pt, _editSP: val } : pt));
                                  }}
                                  className={`flex-1 h-1.5 cursor-pointer ${locked || isAiLocked ? 'accent-gray-400 opacity-60' : 'accent-blue-600'}`} />
                                <span className="text-xs text-gray-400 w-7 text-right">{pctVal}%</span>
                              </div>
                            )}
                            {t._aiReasoning && (
                              <div className="ml-10 mt-0.5 flex items-start gap-1">
                                <svg className="w-3 h-3 text-blue-400 mt-0.5 shrink-0" fill="currentColor" viewBox="0 0 20 20"><path d="M10 2a1 1 0 011 1v1.323l3.954 1.582 1.599-.8a1 1 0 01.894 1.79l-1.233.616 1.738 5.42a1 1 0 01-.285 1.05A3.989 3.989 0 0115 15a3.989 3.989 0 01-2.667-1.019 1 1 0 01-.285-1.05l1.715-5.349L10 6.158 6.237 7.582l1.715 5.349a1 1 0 01-.285 1.05A3.989 3.989 0 015 15a3.989 3.989 0 01-2.667-1.019 1 1 0 01-.285-1.05l1.738-5.42-1.233-.617a1 1 0 01.894-1.788l1.599.799L9 4.323V3a1 1 0 011-1z" /></svg>
                                <span className="text-xs text-blue-500 italic">{t._aiReasoning}</span>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Dispute (when AI locked) */}
                  {isAiLocked && (
                    <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 space-y-2">
                      <label className="flex items-start gap-2 cursor-pointer">
                        <input type="checkbox" checked={!!disputeText} onChange={e => setDisputeText(e.target.checked ? ' ' : '')} className="mt-0.5 w-3.5 h-3.5 text-orange-600 rounded" />
                        <span className="text-xs text-orange-700">I disagree with the AI estimation</span>
                      </label>
                      {disputeText && (
                        <textarea value={disputeText.trim() ? disputeText : ''} onChange={e => setDisputeText(e.target.value)}
                          placeholder="Explain why..." rows={2} className="w-full border border-orange-300 rounded px-3 py-1.5 text-xs resize-none focus:outline-none focus:ring-1 focus:ring-orange-500" />
                      )}
                    </div>
                  )}

                  {approvalMsg && <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-xs text-yellow-700">{approvalMsg}</div>}
                </div>

                {/* Footer */}
                <div className="flex justify-end gap-2 px-5 py-3 border-t border-gray-100 bg-gray-50/50 shrink-0">
                  <button onClick={() => setShowApprovalModal(false)} className="px-4 py-2 text-xs text-gray-600 hover:text-gray-800">Cancel</button>
                  <button disabled={submitting || !ready || aiLoading} onClick={handleConfirmSubmit}
                    className={`px-5 py-2 rounded-lg text-xs font-medium disabled:opacity-50 ${disputeText?.trim() ? 'bg-orange-500 text-white hover:bg-orange-600' : 'bg-yellow-500 text-white hover:bg-yellow-600'}`}>
                    {submitting ? 'Submitting...' : disputeText?.trim() ? 'Submit with Dispute' : isAiLocked ? 'Accept & Submit' : 'Submit for Approval'}
                  </button>
                </div>
              </div>
            </div>
          );
        })()}

        </>)}

        {/* Delete confirmation */}
        {confirmDel && (
          <div className="absolute inset-0 bg-black/30 flex items-center justify-center z-10 rounded-2xl" onClick={() => setConfirmDel(false)}>
            <div className="bg-white rounded-xl shadow-xl p-5 w-full max-w-xs mx-4" onClick={e => e.stopPropagation()}>
              <div className="mb-3">
                <h3 className="text-sm font-semibold text-gray-900">Delete feature</h3>
                <p className="text-xs text-gray-500 mt-0.5">This will also remove all tasks in this feature. This action cannot be undone.</p>
              </div>
              <div className="flex justify-end gap-2">
                <button onClick={() => setConfirmDel(false)} className="px-3 py-1.5 text-xs text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">Cancel</button>
                <button onClick={() => { featureAPI.delete(featureId).then(() => { onClose(); onRefresh(); }).catch(() => {}); }}
                  className="px-3 py-1.5 text-xs bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors">Delete</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Inline quick-edit popover (shared by features tab) ──────────────────────
const useOutsideClick = (ref: React.RefObject<HTMLDivElement | null>, onClose: () => void) => {
  useEffect(() => {
    const h = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) onClose(); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [ref, onClose]);
};

const FEATURE_STATUSES = ['planning', 'development', 'testing', 'done', 'deployed', 'archived'];
const PRIORITIES = ['low', 'medium', 'high', 'critical'];
const TASK_STATUSES = ['todo', 'in_progress', 'blocked', 'review', 'done'];
const TASK_STATUS_LABELS: Record<string, string> = { todo: 'To Do', in_progress: 'In Progress', blocked: 'Blocked', review: 'Review', done: 'Done' };
const labelCls = 'text-[10px] font-medium text-gray-400 uppercase tracking-wide mb-1';

const FeatureQuickEdit: React.FC<{ feature: any; onClose: () => void; onSaved: () => void }> = ({ feature, onClose, onSaved }) => {
  const ref = React.useRef<HTMLDivElement>(null);
  useOutsideClick(ref, onClose);
  const [form, setForm] = useState({
    name: feature.name || '', description: feature.description || '',
    priority: feature.priority || 'medium', status: feature.status || 'planning',
    startDate: feature.dates?.startDate?.slice?.(0, 10) || '',
    endDate: feature.dates?.estimatedEndDate?.slice?.(0, 10) || '',
    storyPoints: feature.storyPoints || 0,
  });
  const [confirmDel, setConfirmDel] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiMsg, setAiMsg] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  const save = (field: string, val: any) => {
    if (field === 'startDate' || field === 'endDate') {
      const dates: any = {};
      if (field === 'startDate') dates.startDate = val || undefined;
      if (field === 'endDate') dates.estimatedEndDate = val || undefined;
      featureAPI.update(feature._id, { dates }).then(onSaved).catch(() => {});
    } else {
      featureAPI.update(feature._id, { [field]: val }).then(onSaved).catch(() => {});
    }
  };

  const handleAiEstimate = async () => {
    setAiLoading(true); setAiMsg('');
    try {
      const res = await aiAPI.estimateStoryPoints(feature._id, true);
      const d = res.data;
      if (d.fallback) setAiMsg(d.message || 'AI unavailable');
      else if (d.data) {
        setForm(f => ({ ...f, storyPoints: d.data.featurePoints }));
        setAiMsg(`AI estimated ${d.data.featurePoints} SP across ${d.data.tasks?.length || 0} tasks`);
      }
    } catch { setAiMsg('AI estimation failed'); }
    setAiLoading(false);
    onSaved();
  };

  // AI estimation result for display
  const [aiResult, setAiResult] = useState<{ featurePoints: number; tasks: { taskId: string; points: number; reasoning: string }[] } | null>(null);
  const [readyToSubmit, setReadyToSubmit] = useState(false);

  const handleSubmitForApproval = async () => {
    setAiMsg(''); setAiResult(null); setReadyToSubmit(false);

    // Fetch current tasks to check count
    let currentTaskCount = 0;
    try {
      const tasksRes = await featureAPI.getTasks(feature._id);
      const featureTasks = tasksRes.data?.data?.tasks || tasksRes.data?.data || [];
      currentTaskCount = Array.isArray(featureTasks) ? featureTasks.length : 0;
    } catch { /* ignore */ }

    if (currentTaskCount === 0) {
      setAiMsg('Add at least one task before submitting');
      return;
    }

    // Check if stored AI estimation matches current task count — use cache
    const storedAi = feature.aiEstimation;
    if (storedAi?.tasks?.length > 0 && storedAi.tasks.length === currentTaskCount && (feature.storyPoints ?? 0) > 0) {
      setAiResult(storedAi);
      setForm(f => ({ ...f, storyPoints: storedAi.featurePoints }));
      setAiMsg(`AI estimated ${storedAi.featurePoints} SP (cached) — confirm to submit`);
      setReadyToSubmit(true);
      return;
    }

    // Task count changed or no SP — run AI estimation
    setAiLoading(true);
    try {
      const force = storedAi?.tasks?.length !== currentTaskCount;
      const res = await aiAPI.estimateStoryPoints(feature._id, force);
      const d = res.data;
      if (d.fallback) {
        setAiMsg(d.message || 'AI unavailable — add story points manually');
        setAiLoading(false);
        return;
      }
      if (d.data) {
        setAiResult(d.data);
        setForm(f => ({ ...f, storyPoints: d.data.featurePoints }));
        setAiMsg(`AI assigned ${d.data.featurePoints} SP across ${d.data.tasks?.length || 0} tasks — review then submit`);
        setReadyToSubmit(true);
      }
    } catch {
      setAiMsg('AI estimation failed — add story points manually');
    }
    setAiLoading(false);
  };

  const handleConfirmSubmit = async () => {
    setActionLoading(true);
    try {
      await featureAPI.submitForApproval(feature._id);
      onSaved();
    } catch (err: any) {
      setAiMsg(err.response?.data?.message || 'Failed to submit');
    }
    setActionLoading(false);
  };

  const as = feature.approvalStatus;
  const canSubmit = !as || as === 'not_required' || as === 'rejected';
  const isEditable = feature.manualOverride || !as || as === 'not_required' || as === 'rejected';

  return (
    <div ref={ref} className="absolute right-0 top-full mt-1 z-50 bg-white border border-gray-200 rounded-xl shadow-xl p-3 w-80" onClick={e => e.stopPropagation()}>
      {/* Approval status banner */}
      {as && as !== 'not_required' && (
        <div className={`mb-2 px-2.5 py-1.5 rounded-lg text-[10px] flex items-center gap-1.5 ${
          as === 'approved' ? 'bg-green-50 text-green-700' : as === 'pending_approval' ? 'bg-yellow-50 text-yellow-700'
          : as === 'rejected' ? 'bg-red-50 text-red-700' : as === 'disputed' ? 'bg-orange-50 text-orange-700' : 'bg-gray-50 text-gray-600'
        }`}>
          {as === 'approved' && '✓ Approved'}
          {as === 'pending_approval' && '⏳ Pending approval'}
          {as === 'rejected' && `✗ Rejected${feature.rejectionReason ? ': ' + feature.rejectionReason : ''}`}
          {as === 'disputed' && '⚠ Disputed'}
        </div>
      )}

      <div className="mb-2"><div className={labelCls}>Name</div>
        <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
          onBlur={() => form.name.trim() && form.name !== feature.name && save('name', form.name.trim())}
          onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.blur(); }}
          className="w-full text-xs border border-gray-200 rounded-lg px-2.5 py-1.5 focus:outline-none focus:border-blue-400" />
      </div>
      <div className="mb-2"><div className={labelCls}>Description</div>
        <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
          onBlur={() => form.description !== (feature.description || '') && save('description', form.description)}
          rows={2} placeholder="Add description..."
          className="w-full text-xs border border-gray-200 rounded-lg px-2.5 py-1.5 focus:outline-none focus:border-blue-400 resize-none" />
      </div>
      <div className="grid grid-cols-2 gap-2 mb-2">
        <div><div className={labelCls}>Priority</div>
          <div className="flex flex-wrap gap-1">
            {PRIORITIES.map(p => (
              <button key={p} onClick={() => { setForm(f => ({ ...f, priority: p })); save('priority', p); }}
                className={`text-[10px] px-1.5 py-0.5 rounded capitalize ${form.priority === p ? 'bg-blue-100 text-blue-700 font-medium' : 'text-gray-400 hover:text-gray-600'}`}>{p}</button>
            ))}
          </div>
        </div>
        <div><div className={labelCls}>Status</div>
          <div className="flex flex-wrap gap-1">
            {FEATURE_STATUSES.map(s => (
              <button key={s} onClick={() => { setForm(f => ({ ...f, status: s })); save('status', s); }}
                className={`text-[10px] px-1.5 py-0.5 rounded capitalize ${form.status === s ? 'bg-blue-100 text-blue-700 font-medium' : 'text-gray-400 hover:text-gray-600'}`}>{s}</button>
            ))}
          </div>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2 mb-2">
        <div><div className={labelCls}>Start Date</div>
          <input type="date" value={form.startDate} onChange={e => { setForm(f => ({ ...f, startDate: e.target.value })); save('startDate', e.target.value); }}
            className="w-full text-xs border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:border-blue-400" />
        </div>
        <div><div className={labelCls}>End Date</div>
          <input type="date" value={form.endDate} onChange={e => { setForm(f => ({ ...f, endDate: e.target.value })); save('endDate', e.target.value); }}
            className="w-full text-xs border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:border-blue-400" />
        </div>
      </div>

      {/* Story Points + AI */}
      <div className="mb-2 bg-gray-50 rounded-lg p-2.5">
        <div className="flex items-center justify-between mb-1.5">
          <div className={labelCls + ' mb-0'}>Story Points</div>
          <div className="flex items-center gap-1.5">
            <input type="number" min={0} value={form.storyPoints || ''} onChange={e => setForm(f => ({ ...f, storyPoints: Number(e.target.value) || 0 }))}
              onBlur={() => form.storyPoints !== (feature.storyPoints || 0) && save('storyPoints', form.storyPoints)}
              disabled={!isEditable} className={`w-14 text-xs border border-gray-200 rounded px-1.5 py-1 text-right focus:outline-none focus:border-blue-400 ${!isEditable ? 'bg-gray-100 cursor-not-allowed' : ''}`} />
            <span className="text-[10px] text-gray-400">SP</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={handleAiEstimate} disabled={aiLoading} className="text-[10px] text-blue-600 hover:text-blue-700 flex items-center gap-1 disabled:text-gray-400">
            {aiLoading ? (
              <><svg className="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg> Estimating...</>
            ) : (
              <><svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20"><path d="M10 2a1 1 0 011 1v1.323l3.954 1.582 1.599-.8a1 1 0 01.894 1.79l-1.233.616 1.738 5.42a1 1 0 01-.285 1.05A3.989 3.989 0 0115 15a3.989 3.989 0 01-2.667-1.019 1 1 0 01-.285-1.05l1.715-5.349L10 6.158 6.237 7.582l1.715 5.349a1 1 0 01-.285 1.05A3.989 3.989 0 015 15a3.989 3.989 0 01-2.667-1.019 1 1 0 01-.285-1.05l1.738-5.42-1.233-.617a1 1 0 01.894-1.788l1.599.799L9 4.323V3a1 1 0 011-1z" /></svg> AI Estimate</>
            )}
          </button>
          {!isEditable && <span className="text-[10px] text-gray-400 italic">Locked — needs manual override</span>}
        </div>
        {aiMsg && <p className="text-[10px] text-blue-600 mt-1">{aiMsg}</p>}
      </div>

      {/* AI estimation result — tasks breakdown */}
      {aiResult && (
        <div className="mb-2 bg-green-50 rounded-lg p-2.5 space-y-1.5">
          <div className="flex items-center gap-1.5">
            <svg className="w-3.5 h-3.5 text-green-600" fill="currentColor" viewBox="0 0 20 20"><path d="M10 2a1 1 0 011 1v1.323l3.954 1.582 1.599-.8a1 1 0 01.894 1.79l-1.233.616 1.738 5.42a1 1 0 01-.285 1.05A3.989 3.989 0 0115 15a3.989 3.989 0 01-2.667-1.019 1 1 0 01-.285-1.05l1.715-5.349L10 6.158 6.237 7.582l1.715 5.349a1 1 0 01-.285 1.05A3.989 3.989 0 015 15a3.989 3.989 0 01-2.667-1.019 1 1 0 01-.285-1.05l1.738-5.42-1.233-.617a1 1 0 01.894-1.788l1.599.799L9 4.323V3a1 1 0 011-1z" /></svg>
            <span className="text-[10px] text-green-700 font-medium">Feature: {aiResult.featurePoints} SP total</span>
          </div>
          <div className="space-y-0.5 max-h-32 overflow-y-auto">
            {aiResult.tasks.map((t, i) => (
              <div key={i} className="flex items-start gap-1.5 text-[10px]">
                <span className="text-green-600 font-medium shrink-0 w-6 text-right">{t.points}</span>
                <span className="text-gray-600 truncate flex-1" title={t.reasoning}>{t.reasoning}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Approval actions */}
      {canSubmit && (
        <div className="mb-2">
          {readyToSubmit ? (
            <button onClick={handleConfirmSubmit} disabled={actionLoading}
              className="w-full text-[10px] bg-green-600 text-white py-1.5 rounded-lg hover:bg-green-700 disabled:opacity-50 font-medium">
              {actionLoading ? 'Submitting...' : 'Confirm & Submit for Approval'}
            </button>
          ) : (
            <button onClick={handleSubmitForApproval} disabled={actionLoading || aiLoading}
              className="w-full text-[10px] bg-yellow-500 text-white py-1.5 rounded-lg hover:bg-yellow-600 disabled:opacity-50 font-medium">
              {aiLoading ? 'Estimating with AI...' : actionLoading ? 'Submitting...' : 'Submit for Approval'}
            </button>
          )}
        </div>
      )}

      {/* Delete */}
      <div className="pt-2 border-t border-gray-100">
        {confirmDel ? (
          <div className="flex items-center justify-between">
            <span className="text-[10px] text-red-600">Delete feature?</span>
            <div className="flex gap-1.5">
              <button onClick={() => featureAPI.delete(feature._id).then(onSaved).catch(() => {})} className="text-[10px] bg-red-600 text-white px-2 py-0.5 rounded hover:bg-red-700">Delete</button>
              <button onClick={() => setConfirmDel(false)} className="text-[10px] text-gray-500 px-2 py-0.5 rounded hover:bg-gray-100">Cancel</button>
            </div>
          </div>
        ) : (
          <button onClick={() => setConfirmDel(true)} className="text-[10px] text-gray-400 hover:text-red-600 flex items-center gap-1.5">
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" /></svg>
            Delete feature
          </button>
        )}
      </div>
    </div>
  );
};

const TaskQuickEditMini: React.FC<{ task: any; onClose: () => void; onSaved: () => void }> = ({ task, onClose, onSaved }) => {
  const ref = React.useRef<HTMLDivElement>(null);
  useOutsideClick(ref, onClose);
  const [form, setForm] = useState({
    title: task.title || '', description: task.description || '',
    priority: task.priority || '', status: task.status || 'todo',
    dueDate: task.dueDate?.slice?.(0, 10) || '', storyPoints: task.storyPoints || '',
  });
  const [confirmDel, setConfirmDel] = useState(false);

  const save = (field: string, val: any) => {
    if (field === 'status') taskAPI.updateStatus(task._id, val).then(onSaved).catch(() => {});
    else taskAPI.updateTask(task._id, { [field]: val }).then(onSaved).catch(() => {});
  };

  return (
    <div ref={ref} className="absolute right-0 top-full mt-1 z-50 bg-white border border-gray-200 rounded-xl shadow-xl p-3 w-72" onClick={e => e.stopPropagation()}>
      <div className="mb-2"><div className={labelCls}>Title</div>
        <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
          onBlur={() => form.title.trim() && form.title !== task.title && save('title', form.title.trim())}
          onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.blur(); }}
          className="w-full text-xs border border-gray-200 rounded-lg px-2.5 py-1.5 focus:outline-none focus:border-blue-400" />
      </div>
      <div className="mb-2"><div className={labelCls}>Description</div>
        <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
          onBlur={() => form.description !== (task.description || '') && save('description', form.description)}
          rows={2} placeholder="Add description..."
          className="w-full text-xs border border-gray-200 rounded-lg px-2.5 py-1.5 focus:outline-none focus:border-blue-400 resize-none" />
      </div>
      <div className="grid grid-cols-2 gap-2 mb-2">
        <div><div className={labelCls}>Priority</div>
          <div className="flex flex-wrap gap-1">
            {PRIORITIES.map(p => (
              <button key={p} onClick={() => { setForm(f => ({ ...f, priority: p })); save('priority', p); }}
                className={`text-[10px] px-1.5 py-0.5 rounded capitalize ${form.priority === p ? 'bg-blue-100 text-blue-700 font-medium' : 'text-gray-400 hover:text-gray-600'}`}>{p}</button>
            ))}
          </div>
        </div>
        <div><div className={labelCls}>Status</div>
          <div className="flex flex-wrap gap-1">
            {TASK_STATUSES.map(s => (
              <button key={s} onClick={() => { setForm(f => ({ ...f, status: s })); save('status', s); }}
                className={`text-[10px] px-1.5 py-0.5 rounded ${form.status === s ? 'bg-blue-100 text-blue-700 font-medium' : 'text-gray-400 hover:text-gray-600'}`}>{TASK_STATUS_LABELS[s] || s}</button>
            ))}
          </div>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2 mb-2">
        <div><div className={labelCls}>Due Date</div>
          <input type="date" value={form.dueDate} onChange={e => { setForm(f => ({ ...f, dueDate: e.target.value })); save('dueDate', e.target.value || undefined); }}
            className="w-full text-xs border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:border-blue-400" />
        </div>
        <div><div className={labelCls}>Story Points</div>
          <input type="number" min={0} value={form.storyPoints} onChange={e => setForm(f => ({ ...f, storyPoints: e.target.value }))}
            onBlur={() => { const v = form.storyPoints ? Number(form.storyPoints) : 0; if (v !== (task.storyPoints || 0)) save('storyPoints', v); }}
            className="w-full text-xs border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:border-blue-400" placeholder="0" />
        </div>
      </div>
      <div className="pt-2 border-t border-gray-100">
        {confirmDel ? (
          <div className="flex items-center justify-between">
            <span className="text-[10px] text-red-600">Delete task?</span>
            <div className="flex gap-1.5">
              <button onClick={() => taskAPI.deleteTask(task._id).then(onSaved).catch(() => {})} className="text-[10px] bg-red-600 text-white px-2 py-0.5 rounded hover:bg-red-700">Delete</button>
              <button onClick={() => setConfirmDel(false)} className="text-[10px] text-gray-500 px-2 py-0.5 rounded hover:bg-gray-100">Cancel</button>
            </div>
          </div>
        ) : (
          <button onClick={() => setConfirmDel(true)} className="text-[10px] text-gray-400 hover:text-red-600 flex items-center gap-1.5">
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" /></svg>
            Delete task
          </button>
        )}
      </div>
    </div>
  );
};

// ─── Features tab — expandable with inline create ────────────────────────────
const FeaturesTabView: React.FC<{
  features: any[];
  tasks: any[];
  projectId: string;
  onNavigate: (featureId: string) => void;
  onTaskClick: (taskId: string) => void;
  onRefresh: () => void;
}> = ({ features, tasks, projectId, onNavigate, onTaskClick, onRefresh }) => {
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [addingFeature, setAddingFeature] = useState(false);
  const [addingTaskFor, setAddingTaskFor] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [inputValue, setInputValue] = useState('');
  const [featureDesc, setFeatureDesc] = useState('');
  const [featurePriority, setFeaturePriority] = useState('medium');
  const [featureStartDate, setFeatureStartDate] = useState('');
  const [featureEndDate, setFeatureEndDate] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const toggle = (id: string) => setExpanded(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const featureTasks = useMemo(() => {
    const m: Record<string, any[]> = {};
    for (const t of tasks) {
      if (t.parentType === 'feature' && t.parentId) {
        if (!m[t.parentId]) m[t.parentId] = [];
        m[t.parentId].push(t);
      }
    }
    return m;
  }, [tasks]);

  const resetFeatureForm = () => { setInputValue(''); setFeatureDesc(''); setFeaturePriority('medium'); setFeatureStartDate(''); setFeatureEndDate(''); };

  const handleCreateFeature = async () => {
    if (!inputValue.trim() || !featureDesc.trim() || !featureStartDate || !featureEndDate || submitting) return;
    setSubmitting(true);
    try {
      await featureAPI.create({ name: inputValue.trim(), description: featureDesc.trim(), projectId, priority: featurePriority, status: 'planning', dates: { startDate: featureStartDate, estimatedEndDate: featureEndDate } } as any);
      resetFeatureForm();
      setAddingFeature(false);
      onRefresh();
    } catch { /* ignore */ }
    setSubmitting(false);
  };

  const handleCreateTask = async (featureId: string) => {
    if (!inputValue.trim() || submitting) return;
    setSubmitting(true);
    try {
      await taskAPI.createTask({ title: inputValue.trim(), description: inputValue.trim(), status: 'todo', parentType: 'feature', parentId: featureId } as any);
      setInputValue('');
      setAddingTaskFor(null);
      onRefresh();
    } catch { /* ignore */ }
    setSubmitting(false);
  };

  return (
    <div>
      {/* Add feature — always at top */}
      <div className="px-4 py-2 border-b border-gray-100">
        {addingFeature ? (
          <div className="space-y-1.5">
            <input autoFocus value={inputValue} onChange={e => setInputValue(e.target.value)}
              onKeyDown={e => { if (e.key === 'Escape') { setAddingFeature(false); resetFeatureForm(); } }}
              placeholder="Feature name *" className="w-full text-sm py-1 px-0 border-0 border-b border-gray-200 focus:border-blue-500 focus:outline-none bg-transparent" />
            <input value={featureDesc} onChange={e => setFeatureDesc(e.target.value)}
              onKeyDown={e => { if (e.key === 'Escape') { setAddingFeature(false); resetFeatureForm(); } }}
              placeholder="Description *" className="w-full text-xs py-1 px-0 border-0 border-b border-gray-200 focus:border-blue-500 focus:outline-none bg-transparent text-gray-600" />
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1.5">
                <span className="text-[10px] text-gray-400">Start *</span>
                <input type="date" value={featureStartDate} onChange={e => setFeatureStartDate(e.target.value)}
                  className="text-[10px] border border-gray-200 rounded px-1.5 py-0.5 focus:outline-none focus:border-blue-400" />
              </div>
              <span className="text-gray-300">→</span>
              <div className="flex items-center gap-1.5">
                <span className="text-[10px] text-gray-400">End *</span>
                <input type="date" value={featureEndDate} onChange={e => setFeatureEndDate(e.target.value)}
                  min={featureStartDate || undefined}
                  className="text-[10px] border border-gray-200 rounded px-1.5 py-0.5 focus:outline-none focus:border-blue-400" />
              </div>
            </div>
            <div className="flex items-center gap-3">
              {(['low', 'medium', 'high', 'critical'] as const).map(p => (
                <label key={p} className="flex items-center gap-1 cursor-pointer">
                  <input type="radio" name="feature-priority" checked={featurePriority === p} onChange={() => setFeaturePriority(p)}
                    className="w-3 h-3 text-blue-600 focus:ring-blue-500/30" />
                  <span className={`text-[10px] capitalize ${featurePriority === p ? 'text-gray-800 font-medium' : 'text-gray-400'}`}>{p}</span>
                </label>
              ))}
              <div className="flex-1" />
              <button onClick={handleCreateFeature} disabled={submitting || !inputValue.trim() || !featureDesc.trim() || !featureStartDate || !featureEndDate} className="text-xs text-blue-600 hover:text-blue-700 disabled:text-gray-300">Add</button>
              <button onClick={() => { setAddingFeature(false); resetFeatureForm(); }} className="text-xs text-gray-400">Cancel</button>
            </div>
          </div>
        ) : (
          <button onClick={() => { setAddingFeature(true); setAddingTaskFor(null); resetFeatureForm(); }} className="text-xs text-gray-400 hover:text-blue-600 flex items-center gap-1">
            <span className="text-sm leading-none">+</span> Add feature
          </button>
        )}
      </div>

      {features.length === 0 && !addingFeature && (
        <p className="text-center py-12 text-sm text-gray-400">No features yet</p>
      )}
      {features.map(f => {
        const isOpen = expanded.has(f._id);
        const fTasks = featureTasks[f._id] || [];
        const doneTasks = fTasks.filter((t: any) => t.status === 'done').length;
        const fmtD = (d?: string) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '';
        return (
          <div key={f._id} className="border-b border-gray-100">
            {/* Feature row */}
            <div className="flex items-center gap-2 px-4 py-2.5 hover:bg-gray-50 transition-colors group/feat relative">
              <button onClick={() => toggle(f._id)} className="shrink-0 w-4 h-4 flex items-center justify-center">
                <svg className={`w-3 h-3 text-gray-400 transition-transform ${isOpen ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
              </button>
              <svg className="w-3.5 h-3.5 text-blue-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z" /></svg>
              <span className="text-sm text-gray-800 truncate flex-1 cursor-pointer hover:text-blue-700" onClick={() => onNavigate(f._id)}>{f.name}</span>
              {fTasks.length > 0 && <span className="text-xs text-gray-400 shrink-0">{doneTasks}/{fTasks.length}</span>}
              {f.storyPoints > 0 && <span className="text-xs text-blue-600 font-medium shrink-0">{f.storyPoints} SP</span>}
              {f.dates?.startDate && <span className="text-[10px] text-gray-400 shrink-0 hidden sm:inline">{fmtD(f.dates.startDate)}</span>}
              {f.dates?.estimatedEndDate && <span className="text-[10px] text-gray-400 shrink-0 hidden sm:inline">→ {fmtD(f.dates.estimatedEndDate)}</span>}
              <span className="text-xs text-gray-400 shrink-0">{cap(f.status)}</span>
              {/* Approval status icon */}
              <span className="shrink-0" title={
                f.approvalStatus === 'approved' ? 'Approved' : f.approvalStatus === 'pending_approval' ? 'Pending approval'
                : f.approvalStatus === 'rejected' ? 'Rejected' : f.approvalStatus === 'disputed' ? 'Disputed' : 'Not submitted'
              }>
                {f.approvalStatus === 'approved' && <svg className="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" /></svg>}
                {f.approvalStatus === 'pending_approval' && <svg className="w-4 h-4 text-yellow-500" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" /></svg>}
                {f.approvalStatus === 'rejected' && <svg className="w-4 h-4 text-red-500" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" /></svg>}
                {f.approvalStatus === 'disputed' && <svg className="w-4 h-4 text-orange-500" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" /></svg>}
                {(!f.approvalStatus || f.approvalStatus === 'not_required') && <div className="w-4 h-4 rounded-full border-2 border-dashed border-gray-300" />}
              </span>
              {/* Quick edit */}
              <button onClick={() => setEditingId(editingId === f._id ? null : f._id)} className="opacity-0 group-hover/feat:opacity-100 transition-opacity text-gray-400 hover:text-blue-600 shrink-0">
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10" /></svg>
              </button>
              {/* Feature quick-edit popover */}
              {editingId === f._id && <FeatureQuickEdit feature={f} onClose={() => setEditingId(null)} onSaved={() => { setEditingId(null); onRefresh(); }} />}
            </div>

            {/* Expanded tasks */}
            {isOpen && (
              <div className="bg-gray-50/50">
                {fTasks.length === 0 && !addingTaskFor && (
                  <p className="text-xs text-gray-400 px-4 py-2 pl-10">No tasks</p>
                )}
                {fTasks.map((t: any) => (
                  <div key={t._id} className="flex items-center gap-2 px-4 py-2 pl-10 hover:bg-gray-100/50 cursor-pointer transition-colors group/task relative">
                    <button className={`w-3.5 h-3.5 rounded-full border-2 shrink-0 transition-all ${t.status === 'done' ? 'bg-green-500 border-green-500' : 'border-gray-300 hover:border-green-400'}`}
                      onClick={e => { e.stopPropagation(); taskAPI.updateStatus(t._id, t.status === 'done' ? 'todo' : 'done').then(onRefresh).catch(() => {}); }} />
                    <span onClick={() => onTaskClick(t._id)} className={`text-xs truncate flex-1 ${t.status === 'done' ? 'line-through text-gray-400' : 'text-gray-700'}`}>{t.title}</span>
                    {t.priority && <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${SEVERITY_PILL[t.priority] || 'bg-gray-200 text-gray-600'}`}>{t.priority}</span>}
                    {t.storyPoints > 0 && <span className="text-[10px] text-blue-600 font-medium">{t.storyPoints} SP</span>}
                    {t.dueDate && <span className="text-[10px] text-gray-400">{fmtD(t.dueDate)}</span>}
                    <span className="text-[10px] text-gray-400">{cap(t.status || '')}</span>
                    {/* Task quick-edit */}
                    <button onClick={() => setEditingId(editingId === t._id ? null : t._id)} className="opacity-0 group-hover/task:opacity-100 transition-opacity text-gray-400 hover:text-blue-600 shrink-0">
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10" /></svg>
                    </button>
                    {editingId === t._id && <TaskQuickEditMini task={t} onClose={() => setEditingId(null)} onSaved={() => { setEditingId(null); onRefresh(); }} />}
                  </div>
                ))}
                {/* Add task under feature */}
                <div className="pl-10 pr-4 py-1">
                  {addingTaskFor === f._id ? (
                    <div className="flex items-center gap-2">
                      <input autoFocus value={inputValue} onChange={e => setInputValue(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') handleCreateTask(f._id); if (e.key === 'Escape') { setAddingTaskFor(null); setInputValue(''); } }}
                        placeholder="Task name" className="flex-1 text-xs py-1 px-0 border-0 border-b border-gray-200 focus:border-blue-500 focus:outline-none bg-transparent" />
                      <button onClick={() => handleCreateTask(f._id)} disabled={submitting} className="text-xs text-blue-600 hover:text-blue-700">Add</button>
                      <button onClick={() => { setAddingTaskFor(null); setInputValue(''); }} className="text-xs text-gray-400">Cancel</button>
                    </div>
                  ) : (
                    <button onClick={() => { setAddingTaskFor(f._id); setAddingFeature(false); setInputValue(''); }} className="text-xs text-gray-400 hover:text-blue-600 py-0.5 flex items-center gap-1">
                      <span className="text-sm leading-none">+</span> Add task
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        );
      })}

    </div>
  );
};

// ─── Bugs tab — with inline create ───────────────────────────────────────────
const BugsTabView: React.FC<{
  bugs: any[];
  projectId: string;
  onNavigate: (bugId: string) => void;
  onRefresh: () => void;
}> = ({ bugs, projectId, onNavigate, onRefresh }) => {
  const [addingBug, setAddingBug] = useState(false);
  const [bugForm, setBugForm] = useState({ title: '', description: '', severity: 'medium', affectedFeatures: '', steps: '', environment: '' });
  const [submitting, setSubmitting] = useState(false);

  const setBug = (field: string, val: string) => setBugForm(f => ({ ...f, [field]: val }));

  const handleCreate = async () => {
    const { title, description, severity, steps } = bugForm;
    if (!title.trim() || !description.trim() || !steps.trim() || submitting) return;
    setSubmitting(true);
    try {
      await api.post('/bugs', {
        title: title.trim(),
        description: description.trim(),
        impact: {
          severity,
          affectedFeatures: bugForm.affectedFeatures ? bugForm.affectedFeatures.split(',').map(s => s.trim()).filter(Boolean) : [],
        },
        reproductionSteps: {
          steps: steps.split('\n').map(s => s.trim()).filter(Boolean),
          environment: bugForm.environment || undefined,
        },
        reportedInProjectId: projectId,
      });
      setBugForm({ title: '', description: '', severity: 'medium', affectedFeatures: '', steps: '', environment: '' });
      setAddingBug(false);
      onRefresh();
    } catch { /* ignore */ }
    setSubmitting(false);
  };

  const resetBugForm = () => { setAddingBug(false); setBugForm({ title: '', description: '', severity: 'medium', affectedFeatures: '', steps: '', environment: '' }); };

  return (
    <div>
      {/* Add bug — always at top */}
      <div className="px-4 py-2 border-b border-gray-100">
        {addingBug ? (
          <div className="space-y-1.5">
            <input autoFocus value={bugForm.title} onChange={e => setBug('title', e.target.value)}
              onKeyDown={e => { if (e.key === 'Escape') resetBugForm(); }}
              placeholder="Bug title *" className="w-full text-sm py-1 px-0 border-0 border-b border-gray-200 focus:border-red-400 focus:outline-none bg-transparent" />
            <input value={bugForm.description} onChange={e => setBug('description', e.target.value)}
              onKeyDown={e => { if (e.key === 'Escape') resetBugForm(); }}
              placeholder="What happened? *" className="w-full text-xs py-1 px-0 border-0 border-b border-gray-200 focus:border-red-400 focus:outline-none bg-transparent text-gray-600" />
            <textarea value={bugForm.steps} onChange={e => setBug('steps', e.target.value)}
              onKeyDown={e => { if (e.key === 'Escape') resetBugForm(); }}
              placeholder="Steps to reproduce (one per line) *" rows={2}
              className="w-full text-xs py-1 px-0 border-0 border-b border-gray-200 focus:border-red-400 focus:outline-none bg-transparent text-gray-600 resize-none" />
            <input value={bugForm.environment} onChange={e => setBug('environment', e.target.value)}
              onKeyDown={e => { if (e.key === 'Escape') resetBugForm(); }}
              placeholder="Environment (e.g. Chrome 120, macOS)" className="w-full text-xs py-1 px-0 border-0 border-b border-gray-200 focus:border-red-400 focus:outline-none bg-transparent text-gray-500" />
            <div className="flex items-center gap-3">
              {(['low', 'medium', 'high', 'critical'] as const).map(s => (
                <label key={s} className="flex items-center gap-1 cursor-pointer">
                  <input type="radio" name="bug-severity" checked={bugForm.severity === s} onChange={() => setBug('severity', s)}
                    className="w-3 h-3 text-red-600 focus:ring-red-500/30" />
                  <span className={`text-[10px] capitalize ${bugForm.severity === s ? 'text-gray-800 font-medium' : 'text-gray-400'}`}>{s}</span>
                </label>
              ))}
              <div className="flex-1" />
              <button onClick={handleCreate} disabled={submitting || !bugForm.title.trim() || !bugForm.description.trim() || !bugForm.steps.trim()} className="text-xs text-red-600 hover:text-red-700 disabled:text-gray-300">Report</button>
              <button onClick={resetBugForm} className="text-xs text-gray-400">Cancel</button>
            </div>
          </div>
        ) : (
          <button onClick={() => setAddingBug(true)} className="text-xs text-gray-400 hover:text-red-600 flex items-center gap-1">
            <span className="text-sm leading-none">+</span> Report bug
          </button>
        )}
      </div>

      {bugs.length === 0 && !addingBug && (
        <p className="text-center py-12 text-sm text-gray-400">No bugs reported</p>
      )}
      {bugs.map((b: any) => (
        <div
          key={b._id}
          onClick={() => onNavigate(b._id)}
          className="flex items-center gap-3 px-4 py-2.5 border-b border-gray-100 hover:bg-gray-50 cursor-pointer transition-colors"
        >
          <div className={`w-2 h-2 rounded-full shrink-0 ${BUG_STATUS_DOT[b.status] || 'bg-gray-400'}`} />
          <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-100 text-red-700 font-semibold shrink-0">BUG</span>
          <span className="text-sm text-gray-800 truncate flex-1">{b.title}</span>
          {b.impact?.severity && <span className={`text-[10px] px-2 py-0.5 rounded-full ${SEVERITY_PILL[b.impact.severity] || 'bg-gray-200 text-gray-600'}`}>{b.impact.severity}</span>}
          <span className="text-xs text-gray-400 shrink-0">{cap(b.status || '')}</span>
          <svg className="w-3.5 h-3.5 text-gray-300 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
        </div>
      ))}
    </div>
  );
};

// ─── Team tab — inline member list + add ─────────────────────────────────────
const TeamTabView: React.FC<{ project: any; onRefresh: () => void }> = ({ project, onRefresh }) => {
  const [adding, setAdding] = useState(false);
  const [search, setSearch] = useState('');
  const [users, setUsers] = useState<any[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const members = project.members || [];
  const owner = project.owner;

  const openAdd = async () => {
    setAdding(true);
    if (users.length === 0) {
      setLoadingUsers(true);
      try {
        const res = await projectAPI.getProjectUsers();
        setUsers(Array.isArray(res.data?.data) ? res.data.data : Array.isArray(res.data) ? res.data : []);
      } catch { /* ignore */ }
      setLoadingUsers(false);
    }
  };

  const existingIds = new Set([owner?.userId, ...members.map((m: any) => m.userId)]);
  const filtered = users.filter((u: any) =>
    u._id && !existingIds.has(u._id) &&
    (!search || u.name?.toLowerCase().includes(search.toLowerCase()) || u.email?.toLowerCase().includes(search.toLowerCase()))
  );

  const handleAdd = async (userId: string) => {
    setUsers(prev => prev.filter(u => u._id !== userId));
    try {
      await projectAPI.addMember(project._id, userId);
    } catch (err: any) { setError(extractErrMsg(err, 'Failed to add member')); }
    onRefresh();
  };

  const handleRemove = async (memberId: string) => {
    try {
      await projectAPI.removeMember(project._id, memberId);
    } catch (err: any) { setError(extractErrMsg(err, 'Failed to remove member')); }
    onRefresh();
  };

  return (
    <div>
      <ErrorToast message={error} onDismiss={() => setError(null)} />
      {/* Add member — top */}
      <div className="px-4 py-2 border-b border-gray-100">
        {adding ? (
          <div className="space-y-1.5">
            <input autoFocus value={search} onChange={e => setSearch(e.target.value)}
              onKeyDown={e => { if (e.key === 'Escape') { setAdding(false); setSearch(''); } }}
              placeholder="Search by name or email..." className="w-full text-xs py-1 px-0 border-0 border-b border-gray-200 focus:border-blue-500 focus:outline-none bg-transparent" />
            {loadingUsers ? (
              <div className="py-2 text-xs text-gray-400">Loading users...</div>
            ) : filtered.length === 0 ? (
              <div className="py-2 text-xs text-gray-400">{search ? 'No matching users' : 'No available users'}</div>
            ) : (
              <div className="max-h-40 overflow-y-auto space-y-0.5">
                {filtered.slice(0, 10).map((u: any) => (
                  <div key={u._id} className="flex items-center gap-2 py-1.5 px-1 hover:bg-gray-50 rounded cursor-pointer" onClick={() => handleAdd(u._id)}>
                    <div className="w-5 h-5 rounded-full bg-blue-500 text-white text-[9px] flex items-center justify-center shrink-0">{(u.name || '?')[0]}</div>
                    <span className="text-xs text-gray-700 flex-1 truncate">{u.name}</span>
                    <span className="text-[10px] text-gray-400 truncate">{u.email}</span>
                    <span className="text-[10px] text-blue-600">Add</span>
                  </div>
                ))}
              </div>
            )}
            <button onClick={() => { setAdding(false); setSearch(''); }} className="text-xs text-gray-400 mt-1">Cancel</button>
          </div>
        ) : (
          <button onClick={openAdd} className="text-xs text-gray-400 hover:text-blue-600 flex items-center gap-1">
            <span className="text-sm leading-none">+</span> Add member
          </button>
        )}
      </div>

      {/* Owner */}
      {owner && (
        <div className="flex items-center gap-2.5 px-4 py-2.5 border-b border-gray-100 bg-blue-50/30">
          <div className="w-6 h-6 rounded-full bg-blue-600 text-white text-[10px] flex items-center justify-center shrink-0 font-medium">{(owner.name || '?')[0]}</div>
          <span className="text-xs text-gray-800 flex-1">{owner.name}</span>
          <span className="text-[10px] text-blue-600 font-medium">Owner</span>
        </div>
      )}

      {/* Members */}
      {members.length === 0 && (
        <p className="text-center py-8 text-xs text-gray-400">No additional team members</p>
      )}
      {members.map((m: any, i: number) => (
        <div key={i} className="flex items-center gap-2.5 px-4 py-2.5 border-b border-gray-100 hover:bg-gray-50 transition-colors group/mem">
          <div className="w-6 h-6 rounded-full bg-blue-500 text-white text-[10px] flex items-center justify-center shrink-0">{(m.name || '?')[0]}</div>
          <span className="text-xs text-gray-800 flex-1">{m.name}</span>
          <span className="text-[10px] text-gray-400">{cap(m.role || 'member')}</span>
          <button onClick={() => handleRemove(m.userId)} className="text-[10px] text-gray-300 hover:text-red-500 opacity-0 group-hover/mem:opacity-100 transition-opacity">Remove</button>
        </div>
      ))}
    </div>
  );
};

// ─── SP tab — leaderboard + recent entries ───────────────────────────────────
const SPTabView: React.FC<{ projectId: string }> = ({ projectId }) => {
  const { user } = useAppSelector(s => s.auth);
  const currentUserId = user?.id || user?._id;
  const [summary, setSummary] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const perPage = 10;

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const res = await storyPointAPI.getTeamTaskSummary(projectId);
      setSummary(res.data?.data || null);
    } catch { /* ignore */ }
    setLoading(false);
  }, [projectId]);

  useEffect(() => { fetch(); }, [fetch]);

  const fmtD = (d?: string) => {
    if (!d) return '';
    const date = new Date(d);
    if (isNaN(date.getTime())) return '';
    const diffMs = Date.now() - date.getTime();
    const days = Math.floor(diffMs / 86400000);
    if (days === 0) return 'Today';
    if (days === 1) return 'Yesterday';
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const members = summary?.members || [];
  const entries = summary?.recentEntries || [];
  const pagedEntries = entries.slice((page - 1) * perPage, page * perPage);
  const totalPages = Math.ceil(entries.length / perPage);

  if (loading) return <div className="flex items-center justify-center py-12"><div className="animate-spin h-5 w-5 border-2 border-blue-500 border-t-transparent rounded-full" /></div>;

  return (
    <div>
      {/* Stats row */}
      {(() => {
        const totalEarned = members.reduce((s: number, m: any) => s + (m.earnedSP || 0), 0);
        const totalUnclaimed = members.reduce((s: number, m: any) => s + (m.unclaimedSP || 0), 0);
        return (
          <div className="flex items-center gap-4 px-4 py-3 border-b border-gray-100 flex-wrap">
            <div className="flex items-center gap-1.5">
              <span className="text-lg font-bold text-gray-900">{summary?.totalSP || 0}</span>
              <span className="text-[10px] text-gray-400">Total SP</span>
            </div>
            <div className="w-px h-4 bg-gray-200" />
            <div className="flex items-center gap-1.5">
              <span className="text-lg font-bold text-green-600">{totalEarned}</span>
              <span className="text-[10px] text-gray-400">Claimed</span>
            </div>
            <div className="w-px h-4 bg-gray-200" />
            <div className="flex items-center gap-1.5">
              <span className="text-lg font-bold text-yellow-600">{totalUnclaimed}</span>
              <span className="text-[10px] text-gray-400">Unclaimed</span>
            </div>
            <div className="w-px h-4 bg-gray-200" />
            <div className="flex items-center gap-1.5">
              <span className="text-lg font-bold text-blue-600">{summary?.totalTasks || 0}</span>
              <span className="text-[10px] text-gray-400">Tasks</span>
            </div>
            <div className="w-px h-4 bg-gray-200" />
            <div className="flex items-center gap-1.5">
              <span className="text-lg font-bold text-gray-700">{members.length}</span>
              <span className="text-[10px] text-gray-400">Members</span>
            </div>
          </div>
        );
      })()}

      {/* Members */}
      {members.length > 0 && (
        <>
          <div className="px-4 py-2 border-b border-gray-100">
            <span className="text-[10px] font-medium text-gray-400 uppercase tracking-wide">Team Members</span>
          </div>
          {members.map((m: any) => {
            const pct = m.totalSP > 0 ? Math.round((m.earnedSP / m.totalSP) * 100) : 0;
            const isMe = m.userId === currentUserId;
            return (
              <div key={m.userId} className={`flex items-center gap-2.5 px-4 py-2 border-b border-gray-100 ${isMe ? 'bg-blue-50/30' : 'hover:bg-gray-50'} transition-colors`}>
                <div className="w-5 h-5 rounded-full bg-blue-500 text-white text-[9px] flex items-center justify-center shrink-0">{(m.name || '?')[0]}</div>
                <span className="text-xs text-gray-800 flex-1">{m.name}{isMe && <span className="text-[10px] text-blue-600 ml-1">You</span>}</span>
                <span className="text-[10px] text-gray-400">{m.completedTasks}/{m.totalTasks} tasks</span>
                <span className="text-[10px] text-green-600 font-medium" title="Claimed">{m.earnedSP}</span>
                {(m.unclaimedSP || 0) > 0 && <span className="text-[10px] text-yellow-600" title="Unclaimed">+{m.unclaimedSP}</span>}
                <div className="w-16 h-1 bg-gray-200 rounded-full overflow-hidden shrink-0">
                  <div className={`h-full rounded-full ${pct === 100 ? 'bg-green-500' : 'bg-blue-500'}`} style={{ width: `${pct}%` }} />
                </div>
                <span className="text-[10px] text-gray-400 w-6 text-right">{pct}%</span>
              </div>
            );
          })}
        </>
      )}

      {/* Recent entries — paginated */}
      {entries.length > 0 && (
        <>
          <div className="px-4 py-2 border-b border-gray-100 flex items-center justify-between">
            <span className="text-[10px] font-medium text-gray-400 uppercase tracking-wide">Recent Points</span>
            {totalPages > 1 && (
              <div className="flex items-center gap-1.5">
                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="text-[10px] text-gray-400 hover:text-gray-600 disabled:opacity-30">← Prev</button>
                <span className="text-[10px] text-gray-400">{page}/{totalPages}</span>
                <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="text-[10px] text-gray-400 hover:text-gray-600 disabled:opacity-30">Next →</button>
              </div>
            )}
          </div>
          {pagedEntries.map((e: any, i: number) => {
            const earnedDate = e.earnedAt || e.createdAt;
            const fullDate = earnedDate ? new Date(earnedDate) : null;
            const fullDateStr = fullDate && !isNaN(fullDate.getTime()) ? fullDate.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : '';
            const tooltipText = `${e.reason || 'Task completed'}\n${fullDateStr}`;
            return (
              <div key={i} className="flex items-center gap-2.5 px-4 py-2 border-b border-gray-100 hover:bg-gray-50 transition-colors group/sp cursor-default"
                title={tooltipText}>
                <div className="w-5 h-5 rounded-full bg-blue-500 text-white text-[9px] flex items-center justify-center shrink-0">{(e.userName || '?')[0]}</div>
                <span className="text-xs text-gray-700 font-medium">{e.userName}</span>
                <span className="text-xs text-gray-400 truncate flex-1">{e.reason || 'Task completed'}</span>
                <span className="text-xs text-green-600 font-medium shrink-0">+{e.points} SP</span>
                <span className="text-[10px] text-gray-400 shrink-0">{fmtD(earnedDate)}</span>
              </div>
            );
          })}
        </>
      )}

      {members.length === 0 && entries.length === 0 && (
        <p className="text-center py-12 text-xs text-gray-400">No story point data yet</p>
      )}
    </div>
  );
};

// ─── Activity tab — inline comments ──────────────────────────────────────────
const ActivityTabView: React.FC<{ projectId: string }> = ({ projectId }) => {
  const [comments, setComments] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [newComment, setNewComment] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const fetchComments = useCallback(async () => {
    try {
      const res = await projectAPI.getComments(projectId);
      setComments(Array.isArray(res.data?.data) ? res.data.data : []);
    } catch { setComments([]); }
    setLoading(false);
  }, [projectId]);

  useEffect(() => { fetchComments(); }, [fetchComments]);

  const handleAdd = async () => {
    if (!newComment.trim() || submitting) return;
    setSubmitting(true);
    try {
      await projectAPI.addComment(projectId, { content: newComment.trim() });
      setNewComment('');
      fetchComments();
    } catch { /* ignore */ }
    setSubmitting(false);
  };

  const fmtD = (d: string) => {
    const date = new Date(d);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const mins = Math.floor(diffMs / 60000);
    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  return (
    <div>
      {/* Add comment — top */}
      <div className="px-4 py-2.5 border-b border-gray-100">
        <div className="flex items-start gap-2">
          <textarea value={newComment} onChange={e => setNewComment(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleAdd(); } }}
            placeholder="Write a comment..." rows={2}
            className="flex-1 text-xs border border-gray-200 rounded-lg px-2.5 py-1.5 focus:outline-none focus:border-blue-400 resize-none" />
          <button onClick={handleAdd} disabled={submitting || !newComment.trim()} className="text-xs text-blue-600 hover:text-blue-700 font-medium disabled:text-gray-300 mt-1">
            {submitting ? 'Posting...' : 'Post'}
          </button>
        </div>
      </div>

      {/* Comments list */}
      {loading ? (
        <div className="flex items-center justify-center py-12"><div className="animate-spin h-5 w-5 border-2 border-blue-500 border-t-transparent rounded-full" /></div>
      ) : comments.length === 0 ? (
        <p className="text-center py-12 text-xs text-gray-400">No comments yet</p>
      ) : comments.map((c: any) => (
        <div key={c._id} className="px-4 py-2.5 border-b border-gray-100 hover:bg-gray-50/50 transition-colors">
          <div className="flex items-center gap-2 mb-1">
            <div className="w-5 h-5 rounded-full bg-blue-500 text-white text-[9px] flex items-center justify-center shrink-0">{(c.author?.name || c.user?.name || '?')[0]}</div>
            <span className="text-xs font-medium text-gray-700">{c.author?.name || c.user?.name || 'Unknown'}</span>
            <span className="text-[10px] text-gray-400 ml-auto">{fmtD(c.createdAt)}</span>
          </div>
          <p className="text-xs text-gray-600 pl-7 whitespace-pre-wrap">{c.content}</p>
        </div>
      ))}
    </div>
  );
};

// ─── KB tab — inline docs list + create modal ───────────────────────────────
const KB_VIS_COLORS: Record<string, string> = { private: 'bg-gray-100 text-gray-600', department: 'bg-blue-100 text-blue-700', team: 'bg-blue-100 text-blue-700', public: 'bg-green-100 text-green-700' };
const timeAgo = (d: string) => { const days = Math.floor((Date.now() - new Date(d).getTime()) / 86400000); return days === 0 ? 'Today' : days === 1 ? 'Yesterday' : days < 30 ? `${days}d ago` : new Date(d).toLocaleDateString(); };

const KBTabView: React.FC<{ projectId: string }> = ({ projectId }) => {
  const [docs, setDocs] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ title: '', visibility: 'private' });
  const [submitting, setSubmitting] = useState(false);
  const [openDocId, setOpenDocId] = useState<string | null>(null);

  const fetchDocs = useCallback(async () => {
    try {
      const data = await knowledgeBaseAPI.getDocsByProject(projectId);
      setDocs(Array.isArray(data) ? data : []);
    } catch { setDocs([]); }
    setLoading(false);
  }, [projectId]);

  useEffect(() => { fetchDocs(); }, [fetchDocs]);

  const handleCreate = async () => {
    if (!form.title.trim() || submitting) return;
    setSubmitting(true);
    try {
      const randomIcons = ['📄', '💡', '📚', '📌'];
      const doc = await knowledgeBaseAPI.create({
        title: form.title.trim(),
        visibility: form.visibility,
        icon: randomIcons[Math.floor(Math.random() * randomIcons.length)],
        linkedProjects: [projectId],
        blocks: [{ id: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`, type: 'paragraph', content: '' }],
      });
      setForm({ title: '', visibility: 'private' });
      setCreating(false);
      fetchDocs();
      if (doc?._id) setOpenDocId(doc._id);
    } catch { /* ignore */ }
    setSubmitting(false);
  };

  return (
    <div>
      {/* Create doc — top */}
      <div className="px-4 py-2 border-b border-gray-100">
        {creating ? (
          <div className="space-y-1.5">
            <input autoFocus value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
              onKeyDown={e => { if (e.key === 'Enter') handleCreate(); if (e.key === 'Escape') { setCreating(false); setForm({ title: '', visibility: 'private' }); } }}
              placeholder="Document title *" className="w-full text-sm py-1 px-0 border-0 border-b border-gray-200 focus:border-blue-500 focus:outline-none bg-transparent" />
            <div className="flex items-center gap-3">
              {(['private', 'department', 'team', 'public'] as const).map(v => (
                <label key={v} className="flex items-center gap-1 cursor-pointer">
                  <input type="radio" name="kb-vis" checked={form.visibility === v} onChange={() => setForm(f => ({ ...f, visibility: v }))}
                    className="w-3 h-3 text-blue-600 focus:ring-blue-500/30" />
                  <span className={`text-[10px] capitalize ${form.visibility === v ? 'text-gray-800 font-medium' : 'text-gray-400'}`}>{v}</span>
                </label>
              ))}
              <div className="flex-1" />
              <button onClick={handleCreate} disabled={submitting || !form.title.trim()} className="text-xs text-blue-600 hover:text-blue-700 disabled:text-gray-300">Create</button>
              <button onClick={() => { setCreating(false); setForm({ title: '', visibility: 'private' }); }} className="text-xs text-gray-400">Cancel</button>
            </div>
          </div>
        ) : (
          <button onClick={() => setCreating(true)} className="text-xs text-gray-400 hover:text-blue-600 flex items-center gap-1">
            <span className="text-sm leading-none">+</span> New document
          </button>
        )}
      </div>

      {/* Docs list */}
      {loading ? (
        <div className="flex items-center justify-center py-12"><div className="animate-spin h-5 w-5 border-2 border-blue-500 border-t-transparent rounded-full" /></div>
      ) : docs.length === 0 && !creating ? (
        <p className="text-center py-12 text-sm text-gray-400">No documents yet</p>
      ) : docs.map((doc: any) => (
        <div key={doc._id} onClick={() => setOpenDocId(doc._id)}
          className="flex items-center gap-3 px-4 py-2.5 border-b border-gray-100 hover:bg-gray-50 cursor-pointer transition-colors">
          <span className="text-base shrink-0">{doc.icon || '📄'}</span>
          <span className="text-sm text-gray-800 truncate flex-1">{doc.title || 'Untitled'}</span>
          <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${KB_VIS_COLORS[doc.visibility] || 'bg-gray-100 text-gray-600'}`}>{cap(doc.visibility)}</span>
          <span className="text-xs text-gray-400 shrink-0">{timeAgo(doc.updatedAt)}</span>
          {(doc.tags ?? []).slice(0, 2).map((t: string) => (
            <span key={t} className="text-[10px] px-1.5 py-0.5 bg-gray-100 text-gray-500 rounded-full shrink-0 hidden sm:inline">{t}</span>
          ))}
          <svg className="w-3.5 h-3.5 text-gray-300 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
        </div>
      ))}

      {/* KB editor modal */}
      {openDocId && (
        <div className="!mt-0 fixed inset-0 z-50 flex items-center justify-center p-4" onClick={() => { setOpenDocId(null); fetchDocs(); }}>
          <div className="absolute inset-0 bg-black/30" style={{ animation: 'backdropFadeIn 300ms ease-out' }} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-[90vw] h-[90vh] flex flex-col overflow-hidden border border-gray-200" style={{ animation: 'modalFadeIn 300ms ease-out' }} onClick={e => e.stopPropagation()}>
            <KBEditorModal docId={openDocId} onClose={() => { setOpenDocId(null); fetchDocs(); }} />
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Sprint tab with expandable feature lists ──────────────────────────────
const SPRINT_DOT: Record<string, string> = { planned: 'bg-gray-400', active: 'bg-green-500', paused: 'bg-yellow-500', completed: 'bg-blue-500', cancelled: 'bg-red-400' };
const SPRINT_PILL: Record<string, string> = { active: 'bg-green-100 text-green-700', completed: 'bg-blue-100 text-blue-700', paused: 'bg-yellow-100 text-yellow-700', cancelled: 'bg-red-100 text-red-700' };

const SprintTabView: React.FC<{
  sprints: any[];
  projectId: string;
  onOpenSprint: (sprint: any) => void;
  onNavigateFeature: (featureId: string) => void;
}> = ({ sprints, projectId, onOpenSprint, onNavigateFeature }) => {
  const [expandedSprints, setExpandedSprints] = useState<Set<string>>(new Set());
  const [sprintFeatures, setSprintFeatures] = useState<Record<string, any[]>>({});

  const toggleSprint = (sid: string) => {
    setExpandedSprints(prev => {
      const n = new Set(prev); n.has(sid) ? n.delete(sid) : n.add(sid); return n;
    });
    // Fetch features on first expand
    if (!sprintFeatures[sid]) {
      featureAPI.getByProject(projectId).then(res => {
        const all = Array.isArray(res.data.data) ? res.data.data : [];
        setSprintFeatures(prev => ({ ...prev, [sid]: all.filter((f: any) => f.sprintId === sid) }));
      }).catch(() => setSprintFeatures(prev => ({ ...prev, [sid]: [] })));
    }
  };

  if (sprints.length === 0) return <p className="text-center py-12 text-sm text-gray-400">No sprints yet</p>;

  return (
    <div>
      {sprints.map(sprint => {
        const isOpen = expandedSprints.has(sprint._id);
        const feats = sprintFeatures[sprint._id] || [];
        return (
          <div key={sprint._id} className="border-b border-gray-100">
            {/* Sprint row */}
            <div className="flex items-center gap-2 px-4 py-2.5 hover:bg-gray-50 transition-colors">
              <button onClick={() => toggleSprint(sprint._id)} className="shrink-0 w-4 h-4 flex items-center justify-center">
                <svg className={`w-3 h-3 text-gray-400 transition-transform ${isOpen ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
              </button>
              <div className={`w-2 h-2 rounded-full shrink-0 ${SPRINT_DOT[sprint.status] || 'bg-gray-400'}`} />
              <span className="text-sm text-gray-800 truncate flex-1 cursor-pointer hover:text-blue-600" onClick={() => onOpenSprint(sprint)}>{sprint.name}</span>
              <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${SPRINT_PILL[sprint.status] || 'bg-gray-100 text-gray-600'}`}>{sprint.status}</span>
              <span className="text-xs text-gray-400 shrink-0">{sprint.stats?.completedTasks ?? 0}/{sprint.stats?.totalTasks ?? 0}</span>
              {sprint.timeline?.startDate && (
                <span className="text-xs text-gray-400 shrink-0 hidden sm:inline">{new Date(sprint.timeline.startDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
              )}
              <button onClick={() => onOpenSprint(sprint)} className="text-xs text-blue-600 hover:text-blue-700 shrink-0">Open</button>
            </div>

            {/* Expanded features */}
            {isOpen && (
              <div className="bg-gray-50/50">
                {feats.length === 0 ? (
                  <p className="text-xs text-gray-400 px-4 py-2 pl-10">No features in this sprint</p>
                ) : feats.map((f: any) => (
                  <div
                    key={f._id}
                    onClick={() => onNavigateFeature(f._id)}
                    className="flex items-center gap-2 px-4 py-2 pl-10 hover:bg-gray-100/50 cursor-pointer transition-colors"
                  >
                    <svg className="w-3 h-3 text-blue-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z" /></svg>
                    <span className="text-xs text-gray-700 truncate flex-1">{f.name}</span>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${
                      f.approvalStatus === 'approved' ? 'bg-green-100 text-green-700' : f.approvalStatus === 'pending_approval' ? 'bg-yellow-100 text-yellow-700' : 'bg-gray-100 text-gray-500'
                    }`}>{cap(f.approvalStatus || 'draft')}</span>
                    {f.storyPoints > 0 && <span className="text-[10px] text-blue-600 font-medium shrink-0">{f.storyPoints} SP</span>}
                    <span className="text-[10px] text-gray-400 capitalize shrink-0">{f.status}</span>
                    <svg className="w-3 h-3 text-gray-300 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

// ─── Error Toast (auto-dismiss) ──────────────────────────────────────────────
const ErrorToast: React.FC<{ message: string | null; onDismiss: () => void }> = ({ message, onDismiss }) => {
  useEffect(() => { if (message) { const t = setTimeout(onDismiss, 5000); return () => clearTimeout(t); } }, [message, onDismiss]);
  if (!message) return null;
  return (
    <div className="sticky top-0 z-10 mx-4 mt-2 mb-1 flex items-center gap-2 px-3 py-2 bg-red-50 border border-red-200 rounded-lg text-xs text-red-700 animate-in fade-in">
      <svg className="w-4 h-4 shrink-0 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" /></svg>
      <span className="flex-1">{message}</span>
      <button onClick={onDismiss} className="text-red-400 hover:text-red-600 shrink-0">
        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
      </button>
    </div>
  );
};

const extractErrMsg = (err: any, fallback = 'Something went wrong') =>
  err?.response?.data?.message || err?.message || fallback;

// ─── QA Testing Tab View (project-scoped, grouped by feature) ────────────────

const GATE_STATUS_STYLE: Record<string, { bg: string; text: string; dot: string }> = {
  pending: { bg: 'bg-amber-100', text: 'text-amber-700', dot: 'bg-amber-400' },
  passed: { bg: 'bg-green-100', text: 'text-green-700', dot: 'bg-green-500' },
  approved: { bg: 'bg-green-100', text: 'text-green-700', dot: 'bg-green-500' },
  failed: { bg: 'bg-red-100', text: 'text-red-700', dot: 'bg-red-500' },
  rejected: { bg: 'bg-red-100', text: 'text-red-700', dot: 'bg-red-500' },
  skipped: { bg: 'bg-gray-100', text: 'text-gray-600', dot: 'bg-gray-400' },
};

const QA_PLAN_STATUS: Record<string, { bg: string; text: string; label: string }> = {
  testing: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Testing' },
  submitted: { bg: 'bg-amber-100', text: 'text-amber-700', label: 'Submitted' },
  approved: { bg: 'bg-green-100', text: 'text-green-700', label: 'Approved' },
  rejected: { bg: 'bg-red-100', text: 'text-red-700', label: 'Rejected' },
};

const TestingTabView: React.FC<{ projectId: string; features: any[]; bugs?: any[]; tasks?: any[]; onTaskClick: (taskId: string) => void; onRefresh: () => void }> = ({ projectId, features, bugs = [], tasks = [], onTaskClick, onRefresh }) => {
  const [plans, setPlans] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedPlans, setExpandedPlans] = useState<Set<string>>(new Set());
  const [selectedPlan, setSelectedPlan] = useState<any>(null);
  const [showPicker, setShowPicker] = useState(false);
  const [pickerTab, setPickerTab] = useState<'features' | 'bugs' | 'tasks'>('features');
  const [pickerSearch, setPickerSearch] = useState('');
  const [adding, setAdding] = useState<string | null>(null);
  const [projectMembers, setProjectMembers] = useState<any[]>([]);
  const [error, setError] = useState<string | null>(null);

  const fetchPlans = useCallback(async () => {
    try {
      const res = await qaTestPlanAPI.getProjectPlans(projectId);
      setPlans(res.data.data || []);
    } catch { setPlans([]); }
    setLoading(false);
  }, [projectId]);

  useEffect(() => { fetchPlans(); }, [fetchPlans]);

  useEffect(() => {
    projectAPI.getProjectById(projectId).then(res => {
      setProjectMembers(res.data?.data?.members || []);
    }).catch(() => {});
  }, [projectId]);

  const existingFeatureIds = React.useMemo(() => new Set(plans.map((p: any) => p.sourceFeatureId?.toString()).filter(Boolean)), [plans]);
  const existingBugIds = React.useMemo(() => new Set(plans.map((p: any) => p.sourceBugId?.toString()).filter(Boolean)), [plans]);

  const availableFeatures = React.useMemo(() =>
    features.filter((f: any) => !existingFeatureIds.has(f._id?.toString()))
      .filter((f: any) => !pickerSearch || f.name?.toLowerCase().includes(pickerSearch.toLowerCase())),
    [features, existingFeatureIds, pickerSearch]
  );

  const availableBugs = React.useMemo(() =>
    bugs.filter((b: any) => !existingBugIds.has(b._id?.toString()))
      .filter((b: any) => !pickerSearch || b.title?.toLowerCase().includes(pickerSearch.toLowerCase())),
    [bugs, existingBugIds, pickerSearch]
  );

  const standaloneTasks = React.useMemo(() =>
    tasks.filter((t: any) => !t.parentId || t.parentType === 'project')
      .filter((t: any) => !pickerSearch || t.title?.toLowerCase().includes(pickerSearch.toLowerCase())),
    [tasks, pickerSearch]
  );

  const handleAddFeature = async (featureId: string) => {
    setAdding(featureId);
    try {
      await qaTestPlanAPI.createFromFeature({ featureId, projectId });
      await fetchPlans();
    } catch (err: any) { setError(extractErrMsg(err, 'Failed to create test plan')); }
    setAdding(null);
  };

  const handleAddBug = async (bugId: string) => {
    setAdding(bugId);
    try {
      await qaTestPlanAPI.createFromBug({ bugId, projectId });
      await fetchPlans();
    } catch (err: any) { setError(extractErrMsg(err, 'Failed to create test plan from bug')); }
    setAdding(null);
  };

  const handleAddTask = async (task: any) => {
    setAdding(task._id);
    try {
      await qaTestPlanAPI.createBlank({ projectId, name: task.title });
      const plansRes = await qaTestPlanAPI.getProjectPlans(projectId);
      const newPlans = plansRes.data.data || [];
      const newPlan = newPlans.find((p: any) => p.name === task.title);
      if (newPlan) {
        await qaTestPlanAPI.addItem(newPlan._id, { title: task.title, description: task.description || undefined, sourceTaskId: task._id });
      }
      await fetchPlans();
    } catch (err: any) { setError(extractErrMsg(err, 'Failed to create test plan from task')); }
    setAdding(null);
  };

  const handleAddAll = async () => {
    setShowPicker(false);
    const toAdd = features.filter((f: any) => !existingFeatureIds.has(f._id?.toString()));
    for (const f of toAdd) {
      try { await qaTestPlanAPI.createFromFeature({ featureId: f._id, projectId }); } catch (err: any) { setError(extractErrMsg(err, 'Failed to create test plan')); }
    }
    await fetchPlans();
  };

  const handleDeletePlan = async (planId: string) => {
    try {
      await qaTestPlanAPI.deletePlan(planId);
      setPlans(prev => prev.filter(p => p._id !== planId));
      if (selectedPlan?._id === planId) setSelectedPlan(null);
    } catch (err: any) { setError(extractErrMsg(err, 'Failed to delete plan')); }
  };

  const togglePlan = (id: string) => setExpandedPlans(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const getPlanProgress = (plan: any) => {
    const items = plan.items || [];
    const total = items.length;
    const passed = items.filter((it: any) => it.status === 'passed').length;
    const failed = items.filter((it: any) => it.status === 'failed').length;
    const tested = passed + failed;
    return { total, passed, failed, tested, pct: total > 0 ? Math.round((passed / total) * 100) : 0 };
  };

  const ItemColHeader = () => (
    <div className="grid grid-cols-12 gap-1 px-5 py-2 text-xs text-gray-400 font-medium select-none border-b border-gray-100">
      <div className="col-span-5 flex items-center gap-1">Item</div>
      <div className="col-span-3">Notes</div>
      <div className="col-span-2">Tested By</div>
      <div className="col-span-2">Result</div>
    </div>
  );

  const ItemRow = ({ item, plan: p }: { item: any; plan: any }) => {
    const statusColor = item.status === 'passed' ? 'bg-green-500' : item.status === 'failed' ? 'bg-red-500' : 'bg-gray-300';
    return (
      <div className="grid grid-cols-12 gap-1 py-2.5 text-sm hover:bg-gray-50/80 cursor-pointer transition-colors"
        style={{ paddingLeft: '48px', paddingRight: '20px' }}
        onClick={() => item.sourceTaskId ? onTaskClick(item.sourceTaskId) : setSelectedPlan(p)}>
        <div className="col-span-5 flex items-center gap-2.5 min-w-0">
          <div className={`w-2 h-2 rounded-full shrink-0 ${statusColor}`} />
          <span className={`truncate ${item.status === 'passed' ? 'text-gray-400 line-through' : 'text-gray-800'}`}>{item.title}</span>
          {item.sourceTaskId && (
            <span title="Linked to source task">
              <svg className="w-3 h-3 text-blue-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M13.19 8.688a4.5 4.5 0 011.242 7.244l-4.5 4.5a4.5 4.5 0 01-6.364-6.364l1.757-1.757m9.86-2.04a4.5 4.5 0 00-1.242-7.244l-4.5-4.5a4.5 4.5 0 00-6.364 6.364L4.343 8.05" />
              </svg>
            </span>
          )}
          {item.sourceBugId && (
            <span className="text-[10px] px-1 py-0.5 rounded bg-red-100 text-red-700 font-semibold shrink-0">BUG</span>
          )}
        </div>
        <div className="col-span-3 flex items-center">
          <span className="text-xs text-gray-400 truncate">{item.testNotes || '—'}</span>
        </div>
        <div className="col-span-2 flex items-center">
          {item.testedBy?.name ? (
            <div className="flex items-center gap-1">
              <div className="w-5 h-5 rounded-full bg-blue-500 text-white text-[10px] flex items-center justify-center">{item.testedBy.name[0]?.toUpperCase()}</div>
              <span className="text-xs text-gray-500">{item.testedBy.name.split(' ')[0]}</span>
            </div>
          ) : <span className="text-xs text-gray-400">—</span>}
        </div>
        <div className="col-span-2 flex items-center">
          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
            item.status === 'passed' ? 'bg-green-100 text-green-700' :
            item.status === 'failed' ? 'bg-red-100 text-red-700' :
            'bg-gray-100 text-gray-600'
          }`}>{item.status}</span>
        </div>
      </div>
    );
  };

  return (
    <div>
      <ErrorToast message={error} onDismiss={() => setError(null)} />
      {loading ? (
        <div className="flex items-center justify-center h-48">
          <div className="animate-spin h-5 w-5 border-2 border-blue-500 border-t-transparent rounded-full" />
        </div>
      ) : (
        <div>
          <div className="flex items-center gap-2 px-5 py-2.5 border-b border-gray-100">
            <div className="relative">
              <button onClick={() => { setShowPicker(!showPicker); setPickerSearch(''); }}
                className="text-xs font-medium text-blue-600 hover:text-blue-700 px-3 py-1.5 rounded-lg hover:bg-blue-50 flex items-center gap-1.5 transition-colors">
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" /></svg>
                Add to Testing
              </button>
              {showPicker && (
                <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg z-[60] w-80" onClick={e => e.stopPropagation()}>
                  <div className="flex border-b border-gray-100">
                    {(['features', 'bugs', 'tasks'] as const).map(tab => (
                      <button key={tab} onClick={() => setPickerTab(tab)}
                        className={`flex-1 text-xs py-2 font-medium capitalize transition-colors ${pickerTab === tab ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-400 hover:text-gray-600'}`}>
                        {tab} {tab === 'features' ? `(${availableFeatures.length})` : tab === 'bugs' ? `(${availableBugs.length})` : `(${standaloneTasks.length})`}
                      </button>
                    ))}
                  </div>
                  <div className="p-2 border-b border-gray-100">
                    <input value={pickerSearch} onChange={e => setPickerSearch(e.target.value)}
                      placeholder={`Search ${pickerTab}...`}
                      className="w-full text-sm border border-gray-200 rounded-lg px-2.5 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-400"
                      autoFocus />
                  </div>
                  <div className="max-h-52 overflow-y-auto">
                    {pickerTab === 'features' && (availableFeatures.length === 0 ? (
                      <div className="px-3 py-4 text-xs text-gray-400 text-center">
                        {features.length === existingFeatureIds.size ? 'All features added' : 'No matches'}
                      </div>
                    ) : availableFeatures.map((f: any) => (
                      <button key={f._id} onClick={() => handleAddFeature(f._id)} disabled={adding === f._id}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-blue-50 flex items-center gap-2 transition-colors disabled:opacity-50">
                        {adding === f._id ? <div className="w-4 h-4 animate-spin border-2 border-blue-500 border-t-transparent rounded-full shrink-0" />
                          : <svg className="w-4 h-4 text-blue-500 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" /></svg>}
                        <span className="text-gray-700 truncate">{f.name}</span>
                        <span className="text-[10px] text-gray-400 ml-auto shrink-0">{f.storyPoints || 0} SP</span>
                      </button>
                    )))}
                    {pickerTab === 'bugs' && (availableBugs.length === 0 ? (
                      <div className="px-3 py-4 text-xs text-gray-400 text-center">No bugs available</div>
                    ) : availableBugs.map((b: any) => (
                      <button key={b._id} onClick={() => handleAddBug(b._id)} disabled={adding === b._id}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-red-50 flex items-center gap-2 transition-colors disabled:opacity-50">
                        {adding === b._id ? <div className="w-4 h-4 animate-spin border-2 border-red-500 border-t-transparent rounded-full shrink-0" />
                          : <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-100 text-red-700 font-semibold shrink-0">BUG</span>}
                        <span className="text-gray-700 truncate">{b.title}</span>
                        <span className={`text-[10px] ml-auto shrink-0 ${b.impact?.severity === 'critical' ? 'text-red-500' : b.impact?.severity === 'high' ? 'text-orange-500' : 'text-gray-400'}`}>{b.impact?.severity || ''}</span>
                      </button>
                    )))}
                    {pickerTab === 'tasks' && (standaloneTasks.length === 0 ? (
                      <div className="px-3 py-4 text-xs text-gray-400 text-center">No standalone tasks</div>
                    ) : standaloneTasks.map((t: any) => (
                      <button key={t._id} onClick={() => handleAddTask(t)} disabled={adding === t._id}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-green-50 flex items-center gap-2 transition-colors disabled:opacity-50">
                        {adding === t._id ? <div className="w-4 h-4 animate-spin border-2 border-green-500 border-t-transparent rounded-full shrink-0" />
                          : <svg className="w-4 h-4 text-green-500 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" /></svg>}
                        <span className="text-gray-700 truncate">{t.title}</span>
                        <span className="text-[10px] text-gray-400 ml-auto shrink-0">{t.storyPoints || 0} SP</span>
                      </button>
                    )))}
                  </div>
                  <div className="p-2 border-t border-gray-100 flex gap-2">
                    {pickerTab === 'features' && availableFeatures.length > 1 && (
                      <button onClick={handleAddAll} className="text-xs text-blue-600 hover:text-blue-700 font-medium px-2 py-1 rounded hover:bg-blue-50">Add All Features</button>
                    )}
                    <button onClick={() => setShowPicker(false)} className="text-xs text-gray-500 hover:text-gray-700 px-2 py-1 rounded hover:bg-gray-50 ml-auto">Done</button>
                  </div>
                </div>
              )}
            </div>
            {plans.length > 0 && (
              <span className="text-xs text-gray-400">{plans.length} plan{plans.length !== 1 ? 's' : ''}</span>
            )}
          </div>

          {plans.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-48 text-gray-400">
              <svg className="w-10 h-10 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div className="text-sm font-medium">No test plans</div>
              <div className="text-xs mt-1">Add features, bugs, or tasks to create test plans</div>
            </div>
          ) : (
            <div>
              {plans.map((plan: any) => {
                const isExpanded = expandedPlans.has(plan._id);
                const prog = getPlanProgress(plan);
                const ps = QA_PLAN_STATUS[plan.status] || QA_PLAN_STATUS.testing;

                return (
                  <div key={plan._id} className="mb-1">
                    <div className="flex items-center gap-2 py-2 px-5 cursor-pointer select-none hover:bg-gray-50/50 group" onClick={() => setSelectedPlan(plan)}>
                      <svg className={`w-3 h-3 text-gray-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}
                        onClick={e => { e.stopPropagation(); togglePlan(plan._id); }}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                      </svg>
                      <svg className="w-3.5 h-3.5 text-blue-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z" />
                      </svg>
                      <span className="text-xs font-bold text-gray-700 capitalize">{plan.name}</span>
                      {plan.sourceBugId && <span className="text-[10px] px-1 py-0.5 rounded bg-red-100 text-red-700 font-semibold shrink-0">BUG</span>}
                      <span className="text-xs text-gray-400">{prog.total} item{prog.total !== 1 ? 's' : ''}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${ps.bg} ${ps.text}`}>{ps.label}</span>
                      {plan.items?.some((it: any) => it.stageGateRequestId) && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-50 text-amber-600 font-medium">gate-linked</span>
                      )}

                      {prog.total > 0 && (
                        <div className="flex items-center gap-1.5 w-24">
                          <div className="flex-1 h-1 bg-gray-200 rounded-full overflow-hidden flex">
                            {prog.passed > 0 && <div className="h-full bg-green-500" style={{ width: `${(prog.passed / prog.total) * 100}%` }} />}
                            {prog.failed > 0 && <div className="h-full bg-red-500" style={{ width: `${(prog.failed / prog.total) * 100}%` }} />}
                          </div>
                          <span className="text-xs text-gray-400" style={{ fontVariantNumeric: 'tabular-nums' }}>{prog.tested}/{prog.total}</span>
                        </div>
                      )}

                      <div className="ml-auto flex items-center gap-1">
                        <button onClick={e => { e.stopPropagation(); togglePlan(plan._id); }}
                          className="text-xs text-gray-500 hover:text-gray-700 font-medium px-2 py-1 rounded-lg hover:bg-gray-100 opacity-0 group-hover:opacity-100 transition-opacity">
                          {isExpanded ? 'Collapse' : 'Expand'}
                        </button>
                        {plan.status === 'testing' && (
                          <button onClick={e => { e.stopPropagation(); handleDeletePlan(plan._id); }}
                            className="text-gray-300 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-all" title="Delete plan">
                            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                          </button>
                        )}
                      </div>
                    </div>

                    {isExpanded && plan.items.length > 0 && (
                      <>
                        <ItemColHeader />
                        {plan.items.map((item: any) => <ItemRow key={item._id} item={item} plan={plan} />)}
                      </>
                    )}
                    {isExpanded && plan.items.length === 0 && (
                      <div className="px-12 py-4 text-xs text-gray-400 text-center">No items — open the plan to add test items</div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {showPicker && <div className="fixed inset-0 z-[55]" onClick={() => setShowPicker(false)} />}

      {selectedPlan && (
        <QAPlanDetailPanel
          plan={selectedPlan}
          projectId={projectId}
          projectMembers={projectMembers}
          onClose={() => setSelectedPlan(null)}
          onRefresh={() => { fetchPlans(); onRefresh(); }}
          onTaskClick={(taskId) => { setSelectedPlan(null); onTaskClick(taskId); }}
        />
      )}
    </div>
  );
};

// ─── QA Plan Detail Panel ─────────────────────────────────────────────────────
const QAPlanDetailPanel: React.FC<{
  plan: any;
  projectId: string;
  projectMembers: any[];
  onClose: () => void;
  onRefresh: () => void;
  onTaskClick?: (taskId: string) => void;
}> = ({ plan: initialPlan, projectId, projectMembers, onClose, onRefresh, onTaskClick }) => {
  const { user: currentUser } = useAppSelector(s => s.auth);
  const currentUserId = currentUser?.id || currentUser?._id;
  const orgPriority = (currentUser as any)?.organizationRole?.priority || 0;
  const [plan, setPlan] = useState<any>(initialPlan);
  const isCreator = plan?.createdBy?.userId === currentUserId;
  const canApproveOthers = !isCreator || orgPriority >= 50;
  const [editingName, setEditingName] = useState(false);
  const [nameVal, setNameVal] = useState(plan.name);
  const [showAddItem, setShowAddItem] = useState(false);
  const [addMode, setAddMode] = useState<'new' | 'task'>('new');
  const [newItemTitle, setNewItemTitle] = useState('');
  const [newItemDesc, setNewItemDesc] = useState('');
  const [projectTasks, setProjectTasks] = useState<any[]>([]);
  const [taskSearch, setTaskSearch] = useState('');
  const [loadingTasks, setLoadingTasks] = useState(false);
  const [addingTaskId, setAddingTaskId] = useState<string | null>(null);
  const [testingItemId, setTestingItemId] = useState<string | null>(null);
  const [testNotes, setTestNotes] = useState('');
  const [testSteps, setTestSteps] = useState<Array<{ step: string; expectedResult: string; actualResult: string; passed: boolean | null }>>([]);
  const [historyItemId, setHistoryItemId] = useState<string | null>(null);
  const [historyRecords, setHistoryRecords] = useState<any[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [commentRecordId, setCommentRecordId] = useState<string | null>(null);
  const [editingItemId, setEditingItemId] = useState<string | null>(null);
  const [editItemTitle, setEditItemTitle] = useState('');
  const [showSubmit, setShowSubmit] = useState(false);
  const [submitSP, setSubmitSP] = useState('');
  const [approvalNotes, setApprovalNotes] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiResult, setAiResult] = useState<any>(null);
  const [aiMsg, setAiMsg] = useState('');
  const [editItemSPs, setEditItemSPs] = useState<Record<string, number>>({});

  const refreshPlan = useCallback(async () => {
    try {
      const res = await qaTestPlanAPI.getPlan(plan._id);
      setPlan(res.data.data);
    } catch {}
  }, [plan._id]);

  const items = plan.items || [];
  const passed = items.filter((it: any) => it.status === 'passed').length;
  const failed = items.filter((it: any) => it.status === 'failed').length;
  const pending = items.filter((it: any) => it.status === 'pending').length;
  const ps = QA_PLAN_STATUS[plan.status] || QA_PLAN_STATUS.testing;
  const canEdit = plan.status === 'testing' || plan.status === 'rejected';

  const handleSaveName = async () => {
    if (!nameVal.trim()) return;
    try {
      await qaTestPlanAPI.updatePlan(plan._id, { name: nameVal.trim() });
      setPlan((p: any) => ({ ...p, name: nameVal.trim() }));
      setEditingName(false);
      onRefresh();
    } catch (err: any) { setError(extractErrMsg(err, 'Failed to save name')); }
  };

  const handleAddItem = async () => {
    if (!newItemTitle.trim()) return;
    try {
      const res = await qaTestPlanAPI.addItem(plan._id, { title: newItemTitle.trim(), description: newItemDesc.trim() || undefined });
      setPlan(res.data.data);
      setNewItemTitle('');
      setNewItemDesc('');
      setShowAddItem(false);
      onRefresh();
    } catch (err: any) { setError(extractErrMsg(err, 'Failed to add item')); }
  };

  const fetchProjectTasks = async () => {
    if (projectTasks.length > 0) return;
    setLoadingTasks(true);
    try {
      const res = await projectAPI.getTasks(projectId);
      setProjectTasks(res.data.data || []);
    } catch (err: any) { setError(extractErrMsg(err, 'Failed to load tasks')); }
    setLoadingTasks(false);
  };

  const handleAddTaskAsItem = async (task: any) => {
    setAddingTaskId(task._id);
    try {
      const res = await qaTestPlanAPI.addItem(plan._id, { title: task.title, description: task.description || undefined, sourceTaskId: task._id });
      setPlan(res.data.data);
      onRefresh();
    } catch (err: any) { setError(extractErrMsg(err, 'Failed to add task')); }
    setAddingTaskId(null);
  };

  const existingSourceTaskIds = new Set(items.filter((it: any) => it.sourceTaskId).map((it: any) => it.sourceTaskId.toString()));
  const filteredTasks = projectTasks
    .filter((t: any) => !existingSourceTaskIds.has(t._id.toString()))
    .filter((t: any) => !taskSearch || t.title.toLowerCase().includes(taskSearch.toLowerCase()));

  const handleRemoveItem = async (itemId: string) => {
    try {
      const res = await qaTestPlanAPI.removeItem(plan._id, itemId);
      setPlan(res.data.data);
      onRefresh();
    } catch (err: any) { setError(extractErrMsg(err, 'Failed to remove item')); }
  };

  const handleTestItem = async (itemId: string, status: 'passed' | 'failed') => {
    try {
      const stepsPayload = testSteps.filter(s => s.step.trim()).map(s => ({
        step: s.step, expectedResult: s.expectedResult, actualResult: s.actualResult || undefined, passed: s.passed ?? undefined,
      }));
      await qaTestPlanAPI.createTestRecord(plan._id, itemId, {
        status,
        notes: testNotes || undefined,
        steps: stepsPayload.length > 0 ? stepsPayload : undefined,
      });
      const res = await qaTestPlanAPI.getPlan(plan._id);
      setPlan(res.data.data);
      setTestingItemId(null);
      setTestNotes('');
      setTestSteps([]);
      onRefresh();
    } catch (err: any) { setError(extractErrMsg(err, 'Failed to record test result')); }
  };

  const loadHistory = async (itemId: string) => {
    if (historyItemId === itemId) { setHistoryItemId(null); return; }
    setHistoryItemId(itemId);
    setLoadingHistory(true);
    try {
      const res = await qaTestPlanAPI.getItemHistory(plan._id, itemId);
      setHistoryRecords(res.data.data || []);
    } catch (err: any) { setError(extractErrMsg(err, 'Failed to load history')); }
    setLoadingHistory(false);
  };

  const handleAddComment = async (recordId: string) => {
    if (!commentText.trim()) return;
    try {
      const res = await qaTestPlanAPI.addRecordComment(recordId, commentText.trim());
      setHistoryRecords(prev => prev.map(r => r._id === recordId ? res.data.data : r));
      setCommentText('');
      setCommentRecordId(null);
    } catch (err: any) { setError(extractErrMsg(err, 'Failed to add comment')); }
  };

  const addTestStep = () => setTestSteps(prev => [...prev, { step: '', expectedResult: '', actualResult: '', passed: null }]);
  const removeTestStep = (idx: number) => setTestSteps(prev => prev.filter((_, i) => i !== idx));
  const updateTestStep = (idx: number, field: string, val: any) => setTestSteps(prev => prev.map((s, i) => i === idx ? { ...s, [field]: val } : s));

  const handleEditItem = async (itemId: string) => {
    if (!editItemTitle.trim()) return;
    try {
      const res = await qaTestPlanAPI.updateItem(plan._id, itemId, { title: editItemTitle.trim() });
      setPlan(res.data.data);
      setEditingItemId(null);
      onRefresh();
    } catch (err: any) { setError(extractErrMsg(err, 'Failed to edit item')); }
  };

  const handleOpenSubmit = async () => {
    setShowSubmit(true);
    setAiMsg('');

    // Use cached AI estimation if items haven't changed
    const stored = plan.aiEstimation;
    if (stored?.tasks?.length === items.length && (plan.storyPoints ?? 0) > 0) {
      setAiResult(stored);
      setSubmitSP(String(stored.featurePoints));
      const spMap: Record<string, number> = {};
      for (const t of stored.tasks) spMap[t.itemId] = t.points;
      setEditItemSPs(spMap);
      return;
    }

    // Run AI estimation
    setAiLoading(true);
    setAiResult(null);
    try {
      const res = await aiAPI.estimateQAPlan(plan._id, true);
      const d = res.data;
      if (d.fallback) {
        setAiMsg(d.message || 'AI unavailable — enter SP manually');
      } else if (d.data) {
        setAiResult(d.data);
        setSubmitSP(String(d.data.featurePoints));
        const spMap: Record<string, number> = {};
        for (const t of d.data.tasks) spMap[t.itemId] = t.points;
        setEditItemSPs(spMap);
        // Refresh plan to pick up stored SP
        try {
          const pr = await qaTestPlanAPI.getPlan(plan._id);
          setPlan(pr.data.data);
        } catch {}
      }
    } catch (err: any) {
      setAiMsg('AI estimation failed — enter SP manually');
    }
    setAiLoading(false);
  };

  const handleSubmit = async () => {
    const spVal = submitSP ? parseInt(submitSP, 10) : undefined;
    if (!spVal || spVal <= 0) {
      setError('Story points are required before submitting');
      return;
    }
    try {
      const res = await qaTestPlanAPI.submit(plan._id, { storyPoints: spVal });
      setPlan(res.data.data);
      setShowSubmit(false);
      onRefresh();
    } catch (err: any) { setError(extractErrMsg(err, 'Failed to submit')); }
  };

  const handleApprove = async () => {
    try {
      const res = await qaTestPlanAPI.approve(plan._id, approvalNotes || undefined);
      setPlan(res.data.data);
      onRefresh();
    } catch (err: any) { setError(extractErrMsg(err, 'Failed to approve')); }
  };

  const handleReject = async () => {
    try {
      const res = await qaTestPlanAPI.reject(plan._id, approvalNotes || undefined);
      setPlan(res.data.data);
      onRefresh();
    } catch (err: any) { setError(extractErrMsg(err, 'Failed to reject')); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" onClick={onClose}>
      <div className="absolute inset-0 bg-black/30" />
      <div className="relative bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden" style={{ width: '90vw', maxWidth: '780px', height: '85vh' }}
        onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="flex items-center gap-3 px-6 py-4 border-b border-gray-100 shrink-0">
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
          <div className="flex-1 min-w-0">
            {editingName ? (
              <div className="flex items-center gap-2">
                <input value={nameVal} onChange={e => setNameVal(e.target.value)} autoFocus
                  onKeyDown={e => { if (e.key === 'Enter') handleSaveName(); if (e.key === 'Escape') { setEditingName(false); setNameVal(plan.name); } }}
                  className="text-base font-semibold text-gray-900 border border-gray-200 rounded-lg px-2 py-0.5 focus:outline-none focus:ring-1 focus:ring-blue-400 flex-1" />
                <button onClick={handleSaveName} className="text-xs text-blue-600 font-medium">Save</button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <h3 className="text-base font-semibold text-gray-900 truncate">{plan.name}</h3>
                {canEdit && (
                  <button onClick={() => { setEditingName(true); setNameVal(plan.name); }} className="text-gray-300 hover:text-gray-500">
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931z" /></svg>
                  </button>
                )}
                <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${ps.bg} ${ps.text}`}>{ps.label}</span>
              </div>
            )}
            <div className="flex items-center gap-3 mt-0.5 text-xs text-gray-400">
              <span>QA Test Plan</span>
              <span>by {plan.createdBy?.name}</span>
              {plan.storyPoints > 0 && <span className="text-blue-600 font-medium">{plan.storyPoints} SP</span>}
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto">
          <ErrorToast message={error} onDismiss={() => setError(null)} />
          {/* Summary bar */}
          <div className="px-6 py-3 border-b border-gray-100 flex items-center justify-between">
            <div className="flex items-center gap-4 text-xs">
              <span className="font-bold text-gray-700">Items</span>
              <span className="text-gray-400">{items.length} total</span>
              {passed > 0 && <span className="text-green-600 font-medium">{passed} passed</span>}
              {failed > 0 && <span className="text-red-600 font-medium">{failed} failed</span>}
              {pending > 0 && <span className="text-amber-600 font-medium">{pending} pending</span>}
            </div>
            {canEdit && (
              <button onClick={() => setShowAddItem(true)}
                className="text-xs text-blue-600 hover:text-blue-700 font-medium px-3 py-1.5 rounded-lg hover:bg-blue-50 flex items-center gap-1">
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
                Add Item
              </button>
            )}
          </div>

          {/* Progress bar */}
          {items.length > 0 && (
            <div className="px-6 py-2 border-b border-gray-100">
              <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden flex">
                {passed > 0 && <div className="h-full bg-green-500 transition-all" style={{ width: `${(passed / items.length) * 100}%` }} />}
                {failed > 0 && <div className="h-full bg-red-500 transition-all" style={{ width: `${(failed / items.length) * 100}%` }} />}
                {pending > 0 && <div className="h-full bg-amber-300 transition-all" style={{ width: `${(pending / items.length) * 100}%` }} />}
              </div>
            </div>
          )}

          {/* Items list */}
          {items.length === 0 && !showAddItem ? (
            <div className="flex flex-col items-center justify-center py-12 text-gray-400">
              <div className="text-sm font-medium">No test items</div>
              <div className="text-xs mt-1">Add items to start testing</div>
              {canEdit && (
                <button onClick={() => setShowAddItem(true)}
                  className="mt-3 text-xs text-blue-600 font-medium px-4 py-2 rounded-lg hover:bg-blue-50 border border-blue-200">
                  Add First Item
                </button>
              )}
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {items.map((item: any) => {
                const isTesting = testingItemId === item._id;
                const isEditing = editingItemId === item._id;

                return (
                  <div key={item._id} className="px-6 py-3 hover:bg-gray-50/50 transition-colors group/item">
                    <div className="flex items-center gap-3">
                      {item.status === 'passed' ? (
                        <div className="w-5 h-5 rounded-full bg-green-500 flex items-center justify-center shrink-0">
                          <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                        </div>
                      ) : item.status === 'failed' ? (
                        <div className="w-5 h-5 rounded-full bg-red-500 flex items-center justify-center shrink-0">
                          <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                        </div>
                      ) : (
                        <div className="w-5 h-5 rounded-full border-2 border-gray-300 shrink-0" />
                      )}
                      <div className="flex-1 min-w-0">
                        {isEditing ? (
                          <div className="flex items-center gap-2">
                            <input value={editItemTitle} onChange={e => setEditItemTitle(e.target.value)} autoFocus
                              onKeyDown={e => { if (e.key === 'Enter') handleEditItem(item._id); if (e.key === 'Escape') setEditingItemId(null); }}
                              className="text-sm border border-gray-200 rounded-lg px-2 py-0.5 flex-1 focus:outline-none focus:ring-1 focus:ring-blue-400" />
                            <button onClick={() => handleEditItem(item._id)} className="text-xs text-blue-600 font-medium">Save</button>
                            <button onClick={() => setEditingItemId(null)} className="text-xs text-gray-400">Cancel</button>
                          </div>
                        ) : (
                          <div className="text-sm text-gray-800 font-medium truncate">{item.title}</div>
                        )}
                        {item.description && !isEditing && <div className="text-xs text-gray-400 mt-0.5 truncate">{item.description}</div>}
                        {!isEditing && (item.sourceTaskId || item.stageGateRequestId) && (
                          <div className="flex items-center gap-1.5 mt-0.5">
                            {item.sourceTaskId && (
                              <button onClick={() => onTaskClick?.(item.sourceTaskId)}
                                className="text-[10px] text-blue-500 hover:text-blue-700 flex items-center gap-0.5 hover:underline">
                                <svg className="w-2.5 h-2.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M13.19 8.688a4.5 4.5 0 011.242 7.244l-4.5 4.5a4.5 4.5 0 01-6.364-6.364l1.757-1.757m9.86-2.04a4.5 4.5 0 00-1.242-7.244l-4.5-4.5a4.5 4.5 0 00-6.364 6.364L4.343 8.05" /></svg>
                                View source task
                              </button>
                            )}
                            {item.stageGateRequestId && (
                              <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-50 text-amber-600 font-medium">gate</span>
                            )}
                          </div>
                        )}
                      </div>
                      {item.testedBy?.name && (
                        <div className="flex items-center gap-1 shrink-0">
                          <div className="w-5 h-5 rounded-full bg-blue-500 text-white text-[10px] flex items-center justify-center">{item.testedBy.name[0]?.toUpperCase()}</div>
                          <span className="text-xs text-gray-500">{item.testedBy.name.split(' ')[0]}</span>
                        </div>
                      )}
                      <div className="flex items-center gap-1 shrink-0">
                        <button onClick={() => loadHistory(item._id)}
                          className={`text-xs font-medium px-2 py-1 rounded-lg transition-colors ${historyItemId === item._id ? 'bg-gray-200 text-gray-700' : 'text-gray-500 hover:bg-gray-100'}`}>
                          History
                        </button>
                        {canEdit && (
                          <button onClick={() => { setTestingItemId(isTesting ? null : item._id); setTestNotes(''); setTestSteps([]); setEditingItemId(null); }}
                            className={`text-xs font-medium px-2 py-1 rounded-lg transition-colors ${isTesting ? 'bg-blue-100 text-blue-700' : 'text-blue-600 hover:bg-blue-50'}`}>
                            {item.status === 'pending' ? 'Test' : 'Retest'}
                          </button>
                        )}
                        {canEdit && !isEditing && (
                          <button onClick={() => { setEditingItemId(item._id); setEditItemTitle(item.title); setTestingItemId(null); }}
                            className="text-gray-300 hover:text-gray-500 opacity-0 group-hover/item:opacity-100 transition-all">
                            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931z" /></svg>
                          </button>
                        )}
                        {canEdit && (
                          <button onClick={() => handleRemoveItem(item._id)}
                            className="text-gray-300 hover:text-red-400 opacity-0 group-hover/item:opacity-100 transition-all">
                            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" /></svg>
                          </button>
                        )}
                      </div>
                    </div>

                    {item.testNotes && !isTesting && (
                      <div className="mt-2 ml-8 text-xs text-gray-600 bg-gray-50 rounded-lg p-2.5">
                        <span className="font-medium text-gray-500">Notes:</span> {item.testNotes}
                        {item.testedAt && <span className="text-gray-400"> — {new Date(item.testedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>}
                      </div>
                    )}

                    {isTesting && (
                      <div className="mt-2 ml-8 bg-white border border-gray-200 rounded-lg p-3 space-y-3">
                        <textarea value={testNotes} onChange={e => setTestNotes(e.target.value)}
                          placeholder="Test notes — describe what you found..."
                          className="w-full text-sm border border-gray-200 rounded-lg p-2.5 resize-none focus:outline-none focus:ring-1 focus:ring-blue-400 bg-white" rows={2} />

                        {/* Test steps (optional) */}
                        <div>
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">Test Steps (optional)</span>
                            <button onClick={addTestStep} className="text-[10px] text-blue-600 font-medium hover:text-blue-700">+ Add Step</button>
                          </div>
                          {testSteps.map((s, idx) => (
                            <div key={idx} className="flex gap-1.5 items-start mb-1.5">
                              <span className="text-[10px] text-gray-400 mt-2 w-4 shrink-0 text-right">{idx + 1}.</span>
                              <input value={s.step} onChange={e => updateTestStep(idx, 'step', e.target.value)} placeholder="Step..."
                                className="flex-1 text-xs border border-gray-200 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-400" />
                              <input value={s.expectedResult} onChange={e => updateTestStep(idx, 'expectedResult', e.target.value)} placeholder="Expected result..."
                                className="flex-1 text-xs border border-gray-200 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-400" />
                              <input value={s.actualResult} onChange={e => updateTestStep(idx, 'actualResult', e.target.value)} placeholder="Actual result..."
                                className="flex-1 text-xs border border-gray-200 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-400" />
                              <button onClick={() => updateTestStep(idx, 'passed', s.passed === true ? null : true)}
                                className={`px-1.5 py-1 rounded text-[10px] font-medium shrink-0 ${s.passed === true ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-400 hover:bg-green-50'}`}>OK</button>
                              <button onClick={() => updateTestStep(idx, 'passed', s.passed === false ? null : false)}
                                className={`px-1.5 py-1 rounded text-[10px] font-medium shrink-0 ${s.passed === false ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-400 hover:bg-red-50'}`}>Fail</button>
                              <button onClick={() => removeTestStep(idx)} className="text-gray-300 hover:text-red-400 shrink-0">
                                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                              </button>
                            </div>
                          ))}
                        </div>

                        <div className="flex gap-2">
                          <button onClick={() => handleTestItem(item._id, 'passed')} className="px-4 py-2 text-xs bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium">Pass</button>
                          <button onClick={() => handleTestItem(item._id, 'failed')} className="px-4 py-2 text-xs bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium">Fail</button>
                          <button onClick={() => { setTestingItemId(null); setTestNotes(''); setTestSteps([]); }} className="px-4 py-2 text-xs text-gray-500 hover:text-gray-700">Cancel</button>
                        </div>
                      </div>
                    )}

                    {/* Test history panel */}
                    {historyItemId === item._id && (
                      <div className="mt-2 ml-8 bg-gray-50 border border-gray-200 rounded-lg overflow-hidden">
                        <div className="px-3 py-2 bg-gray-100 border-b border-gray-200 flex items-center justify-between">
                          <span className="text-[10px] font-bold text-gray-600 uppercase tracking-wider">Test History</span>
                          <button onClick={() => setHistoryItemId(null)} className="text-gray-400 hover:text-gray-600">
                            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                          </button>
                        </div>
                        {loadingHistory ? (
                          <div className="px-3 py-4 text-xs text-gray-400 text-center">Loading...</div>
                        ) : historyRecords.length === 0 ? (
                          <div className="px-3 py-4 text-xs text-gray-400 text-center">No test records yet</div>
                        ) : (
                          <div className="divide-y divide-gray-200 max-h-64 overflow-y-auto">
                            {historyRecords.map((rec: any) => (
                              <div key={rec._id} className="px-3 py-2.5">
                                <div className="flex items-center gap-2 mb-1">
                                  <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${rec.status === 'passed' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                                    {rec.status}
                                  </span>
                                  <span className="text-xs text-gray-600">{rec.testedBy?.name}</span>
                                  <span className="text-[10px] text-gray-400">
                                    {new Date(rec.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                  </span>
                                </div>
                                {rec.notes && <div className="text-xs text-gray-600 mb-1">{rec.notes}</div>}
                                {rec.steps?.length > 0 && (
                                  <div className="mb-1.5 space-y-0.5">
                                    {rec.steps.map((s: any, si: number) => (
                                      <div key={si} className="flex items-center gap-1.5 text-[10px]">
                                        <span className={`w-3.5 h-3.5 rounded-full flex items-center justify-center text-white text-[8px] shrink-0 ${s.passed === true ? 'bg-green-500' : s.passed === false ? 'bg-red-500' : 'bg-gray-300'}`}>
                                          {s.passed === true ? '✓' : s.passed === false ? '✗' : '-'}
                                        </span>
                                        <span className="text-gray-700">{s.step}</span>
                                        {s.actualResult && <span className="text-gray-400 italic ml-1">{s.actualResult}</span>}
                                      </div>
                                    ))}
                                  </div>
                                )}
                                {/* Comments */}
                                {rec.comments?.length > 0 && (
                                  <div className="ml-2 mt-1 space-y-1 border-l-2 border-gray-200 pl-2">
                                    {rec.comments.map((c: any, ci: number) => (
                                      <div key={ci} className="text-[10px]">
                                        <span className="font-medium text-gray-600">{c.userName}</span>
                                        <span className="text-gray-400 ml-1">{new Date(c.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                                        <div className="text-gray-600">{c.text}</div>
                                      </div>
                                    ))}
                                  </div>
                                )}
                                {/* Add comment */}
                                {commentRecordId === rec._id ? (
                                  <div className="flex items-center gap-1.5 mt-1.5">
                                    <input value={commentText} onChange={e => setCommentText(e.target.value)}
                                      onKeyDown={e => { if (e.key === 'Enter') handleAddComment(rec._id); }}
                                      placeholder="Add a comment..." autoFocus
                                      className="flex-1 text-[10px] border border-gray-200 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-400" />
                                    <button onClick={() => handleAddComment(rec._id)} className="text-[10px] text-blue-600 font-medium">Post</button>
                                    <button onClick={() => { setCommentRecordId(null); setCommentText(''); }} className="text-[10px] text-gray-400">Cancel</button>
                                  </div>
                                ) : (
                                  <button onClick={() => { setCommentRecordId(rec._id); setCommentText(''); }}
                                    className="text-[10px] text-blue-500 hover:text-blue-700 mt-1">Comment</button>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Add item form */}
          {showAddItem && (
            <div className="px-6 py-4 border-t border-gray-100 bg-gray-50/50">
              <div className="flex items-center gap-1 mb-3">
                <button onClick={() => setAddMode('new')}
                  className={`text-xs font-medium px-3 py-1.5 rounded-lg transition-colors ${addMode === 'new' ? 'bg-blue-100 text-blue-700' : 'text-gray-500 hover:bg-gray-100'}`}>
                  New Item
                </button>
                <button onClick={() => { setAddMode('task'); fetchProjectTasks(); }}
                  className={`text-xs font-medium px-3 py-1.5 rounded-lg transition-colors ${addMode === 'task' ? 'bg-blue-100 text-blue-700' : 'text-gray-500 hover:bg-gray-100'}`}>
                  From Tasks
                </button>
                <button onClick={() => { setShowAddItem(false); setNewItemTitle(''); setNewItemDesc(''); setTaskSearch(''); }}
                  className="ml-auto text-gray-400 hover:text-gray-600">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              </div>

              {addMode === 'new' ? (
                <div className="space-y-2.5">
                  <input value={newItemTitle} onChange={e => setNewItemTitle(e.target.value)}
                    placeholder="Item title"
                    className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-1 focus:ring-blue-400"
                    autoFocus
                    onKeyDown={e => { if (e.key === 'Enter' && newItemTitle.trim()) handleAddItem(); if (e.key === 'Escape') { setShowAddItem(false); setNewItemTitle(''); setNewItemDesc(''); } }} />
                  <textarea value={newItemDesc} onChange={e => setNewItemDesc(e.target.value)}
                    placeholder="Description (optional)"
                    className="w-full text-sm border border-gray-200 rounded-lg p-2.5 bg-white resize-none focus:outline-none focus:ring-1 focus:ring-blue-400" rows={2} />
                  <div className="flex gap-2">
                    <button onClick={handleAddItem} disabled={!newItemTitle.trim()}
                      className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium disabled:opacity-50">Add</button>
                  </div>
                </div>
              ) : (
                <div>
                  <input value={taskSearch} onChange={e => setTaskSearch(e.target.value)}
                    placeholder="Search project tasks..."
                    className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-1 focus:ring-blue-400 mb-2"
                    autoFocus />
                  {loadingTasks ? (
                    <div className="flex items-center justify-center py-6">
                      <div className="animate-spin h-4 w-4 border-2 border-blue-500 border-t-transparent rounded-full" />
                    </div>
                  ) : filteredTasks.length === 0 ? (
                    <div className="text-xs text-gray-400 text-center py-4">
                      {projectTasks.length === 0 ? 'No tasks in project' : taskSearch ? 'No matching tasks' : 'All tasks already added'}
                    </div>
                  ) : (
                    <div className="max-h-48 overflow-y-auto rounded-lg border border-gray-200 bg-white divide-y divide-gray-100">
                      {filteredTasks.map((t: any) => (
                        <button key={t._id} onClick={() => handleAddTaskAsItem(t)}
                          disabled={addingTaskId === t._id}
                          className="w-full text-left px-3 py-2 text-sm hover:bg-blue-50 flex items-center gap-2 transition-colors disabled:opacity-50">
                          {addingTaskId === t._id ? (
                            <div className="w-3.5 h-3.5 animate-spin border-2 border-blue-500 border-t-transparent rounded-full shrink-0" />
                          ) : (
                            <svg className="w-3.5 h-3.5 text-blue-500 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" /></svg>
                          )}
                          <div className="flex-1 min-w-0">
                            <div className="text-gray-700 truncate">{t.title}</div>
                            <div className="text-[10px] text-gray-400">{t.status?.replace('_', ' ')}{t.assignedTo?.[0]?.name ? ` · ${t.assignedTo[0].name}` : ''}</div>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Submit/Approval section */}
          {plan.status === 'submitted' && plan.submittedTo && (
            <div className="px-6 py-4 border-t border-gray-100 bg-amber-50/50">
              <div className="text-xs font-bold text-amber-700 mb-2">Pending Approval</div>
              <div className="text-sm text-gray-700">Submitted to <span className="font-medium">{plan.submittedTo.name}</span> on {plan.submittedAt && new Date(plan.submittedAt).toLocaleDateString()}</div>
              {canApproveOthers ? (
                <div className="mt-3 space-y-2">
                  <textarea value={approvalNotes} onChange={e => setApprovalNotes(e.target.value)}
                    placeholder="Approval notes (optional)..."
                    className="w-full text-sm border border-gray-200 rounded-lg p-2.5 bg-white resize-none focus:outline-none focus:ring-1 focus:ring-blue-400" rows={2} />
                  <div className="flex gap-2">
                    <button onClick={handleApprove} className="px-4 py-2 text-xs bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium">Approve</button>
                    <button onClick={handleReject} className="px-4 py-2 text-xs bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium">Reject</button>
                  </div>
                </div>
              ) : (
                <div className="mt-2 text-xs text-amber-600">You cannot approve your own submission. Waiting for another approver.</div>
              )}
            </div>
          )}

          {plan.status === 'approved' && plan.approvedBy && (
            <div className="px-6 py-4 border-t border-gray-100 bg-green-50/50">
              <div className="flex items-center gap-2 text-xs">
                <svg className="w-4 h-4 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                <span className="font-bold text-green-700">Approved</span>
                <span className="text-gray-500">by {plan.approvedBy.name} on {plan.approvedAt && new Date(plan.approvedAt).toLocaleDateString()}</span>
              </div>
              {plan.approvalNotes && <div className="text-sm text-gray-700 mt-1 ml-6">{plan.approvalNotes}</div>}
            </div>
          )}

          {plan.status === 'rejected' && plan.approvedBy && (
            <div className="px-6 py-4 border-t border-gray-100 bg-red-50/50">
              <div className="flex items-center gap-2 text-xs">
                <svg className="w-4 h-4 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                <span className="font-bold text-red-700">Rejected</span>
                <span className="text-gray-500">by {plan.approvedBy.name}</span>
              </div>
              {plan.approvalNotes && <div className="text-sm text-gray-700 mt-1 ml-6">{plan.approvalNotes}</div>}
            </div>
          )}
        </div>

        {/* Footer actions */}
        {canEdit && items.length > 0 && (
          <div className="border-t border-gray-100 shrink-0 bg-white">
            {!showSubmit ? (
              <div className="px-6 py-3 flex items-center gap-2">
                <div className="flex-1" />
                <button onClick={handleOpenSubmit}
                  className="px-4 py-2 text-xs bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium">
                  Submit for Approval
                </button>
              </div>
            ) : (
              <div className="px-6 py-4 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="text-xs font-bold text-gray-700 uppercase tracking-wider">Story Point Estimation</div>
                  <button onClick={() => { setShowSubmit(false); setAiResult(null); setAiMsg(''); }} className="text-xs text-gray-400 hover:text-gray-600">Cancel</button>
                </div>

                {aiLoading && (
                  <div className="flex items-center gap-2 py-3">
                    <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                    <span className="text-xs text-blue-600">AI is estimating story points...</span>
                  </div>
                )}

                {aiMsg && !aiLoading && (
                  <div className="text-xs text-amber-600 bg-amber-50 rounded-lg px-3 py-2">{aiMsg}</div>
                )}

                {(aiResult || aiMsg) && !aiLoading && (
                  <>
                    {/* Per-item SP breakdown */}
                    {aiResult?.tasks?.length > 0 && (
                      <div className="bg-gray-50 rounded-lg overflow-hidden border border-gray-200">
                        <div className="px-3 py-1.5 bg-gray-100 border-b border-gray-200 text-[10px] font-bold text-gray-500 uppercase tracking-wider flex items-center">
                          <span className="flex-1">Item</span>
                          <span className="w-14 text-center">SP</span>
                          <span className="flex-1">Reasoning</span>
                        </div>
                        <div className="divide-y divide-gray-200 max-h-40 overflow-y-auto">
                          {items.map((item: any) => {
                            const aiTask = aiResult.tasks.find((t: any) => t.itemId === item._id);
                            const sp = editItemSPs[item._id] ?? aiTask?.points ?? item.storyPoints ?? 0;
                            return (
                              <div key={item._id} className="px-3 py-1.5 flex items-center text-xs gap-2">
                                <span className="flex-1 truncate text-gray-700">{item.title}</span>
                                <input type="number" min="0" value={sp}
                                  onChange={e => {
                                    const v = parseInt(e.target.value, 10) || 0;
                                    setEditItemSPs(prev => ({ ...prev, [item._id]: v }));
                                    const allSPs = items.reduce((sum: number, it: any) => sum + (it._id === item._id ? v : (editItemSPs[it._id] ?? aiResult?.tasks?.find((t: any) => t.itemId === it._id)?.points ?? it.storyPoints ?? 0)), 0);
                                    setSubmitSP(String(allSPs));
                                  }}
                                  className="w-14 text-center text-xs border border-gray-200 rounded px-1 py-0.5 focus:outline-none focus:ring-1 focus:ring-blue-400" />
                                <span className="flex-1 text-[10px] text-gray-400 italic truncate">{aiTask?.reasoning || '—'}</span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Total SP + submit */}
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-gray-500">Total SP:</span>
                      <input type="number" min="1" value={submitSP} onChange={e => setSubmitSP(e.target.value)}
                        className="w-16 text-sm font-semibold text-center border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-400" />
                      <span className="text-[10px] text-gray-400 flex-1">Submits to department HOD</span>
                      <button onClick={handleSubmit} disabled={!submitSP || parseInt(submitSP, 10) <= 0}
                        className="px-4 py-2 text-xs bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium disabled:opacity-50">
                        Confirm & Submit
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Gate Detail Panel (modal overlay — matches TaskPanel pattern) ────────────
const GateDetailPanel: React.FC<{
  gate: any;
  tests: any[];
  projectId: string;
  projectMembers: any[];
  onClose: () => void;
  onTaskClick: (taskId: string) => void;
  onRefresh: () => void;
}> = ({ gate, tests: initialTests, projectId, projectMembers, onClose, onTaskClick, onRefresh }) => {
  const [gateTests, setGateTests] = useState<any[]>(initialTests);
  const [executeId, setExecuteId] = useState<string | null>(null);
  const [executeNotes, setExecuteNotes] = useState('');
  const [reassignId, setReassignId] = useState<string | null>(null);
  const [reassignSearch, setReassignSearch] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({ title: '', description: '', steps: [{ step: '', expectedResult: '' }] });
  const [createAssignee, setCreateAssignee] = useState({ userId: '', name: '' });
  const [error, setError] = useState<string | null>(null);

  const refreshTests = useCallback(async () => {
    try {
      const res = await testCaseAPI.getByGate(gate._id);
      setGateTests(res.data.data || []);
    } catch (err: any) { setError(extractErrMsg(err, 'Failed to load tests')); }
  }, [gate._id]);

  useEffect(() => { refreshTests(); }, [refreshTests]);

  const ss = GATE_STATUS_STYLE[gate.status] || GATE_STATUS_STYLE.pending;
  const passed = gateTests.filter(t => t.status === 'passed').length;
  const failed = gateTests.filter(t => t.status === 'failed').length;
  const pending = gateTests.filter(t => t.status === 'pending' || t.status === 'in_progress').length;

  const resolvedAssignee = createAssignee.userId
    ? createAssignee
    : gate.assignedTo?.userId ? { userId: gate.assignedTo.userId.toString(), name: gate.assignedTo.name || '' } : null;

  const handleCreate = async () => {
    if (!createForm.title || !resolvedAssignee) return;
    try {
      await testCaseAPI.create({
        title: createForm.title,
        description: createForm.description || undefined,
        taskId: gate.taskId,
        featureId: gate.resolvedFeatureId || undefined,
        projectId,
        stageGateRequestId: gate._id,
        steps: createForm.steps.filter(s => s.step),
        assignedToUserId: resolvedAssignee.userId,
        assignedToName: resolvedAssignee.name,
      });
      setCreateForm({ title: '', description: '', steps: [{ step: '', expectedResult: '' }] });
      setCreateAssignee({ userId: '', name: '' });
      setShowCreate(false);
      refreshTests();
      onRefresh();
    } catch (err: any) { setError(extractErrMsg(err, 'Failed to create test case')); }
  };

  const handleExecute = async (id: string, status: 'passed' | 'failed') => {
    try {
      await testCaseAPI.execute(id, { status, resultNotes: executeNotes });
      setExecuteId(null);
      setExecuteNotes('');
      refreshTests();
      onRefresh();
    } catch (err: any) { setError(extractErrMsg(err, 'Failed to execute test')); }
  };

  const handleReassign = async (id: string, userId: string, name: string) => {
    try {
      await testCaseAPI.reassign(id, { assignedToUserId: userId, assignedToName: name });
      setReassignId(null);
      refreshTests();
      onRefresh();
    } catch (err: any) { setError(extractErrMsg(err, 'Failed to reassign')); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" onClick={onClose}>
      <div className="absolute inset-0 bg-black/30" />
      <div className="relative bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden" style={{ width: '90vw', maxWidth: '720px', height: '85vh' }}
        onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="flex items-center gap-3 px-6 py-4 border-b border-gray-100 shrink-0">
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h3 className="text-base font-semibold text-gray-900 truncate">{gate.taskTitle || 'Untitled Task'}</h3>
              <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${ss.bg} ${ss.text}`}>{gate.status}</span>
            </div>
            <div className="flex items-center gap-3 mt-0.5 text-xs text-gray-400">
              <span>Stage: <span className="text-gray-600 font-medium">{gate.stage}</span></span>
              <span>QA: <span className="text-gray-600 font-medium">{gate.assignedTo?.name || '—'}</span></span>
              <span>{gate.createdAt ? new Date(gate.createdAt).toLocaleDateString() : ''}</span>
            </div>
          </div>
          <button onClick={() => onTaskClick(gate.taskId)} className="text-xs text-blue-600 hover:text-blue-700 font-medium px-3 py-1.5 rounded-lg hover:bg-blue-50 shrink-0">
            View Task
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto">
          <ErrorToast message={error} onDismiss={() => setError(null)} />
          {/* Gate info */}
          {(gate.notes || gate.requestedBy?.name) && (
            <div className="px-6 py-3 border-b border-gray-100">
              {gate.notes && (
                <div className="text-sm text-gray-700 bg-gray-50 rounded-lg p-3 border border-gray-100">
                  <span className="text-xs font-medium text-gray-400 block mb-1">Dev notes</span>
                  {gate.notes}
                </div>
              )}
              {gate.requestedBy?.name && !gate.notes && (
                <div className="text-xs text-gray-400">Requested by <span className="text-gray-600 font-medium">{gate.requestedBy.name}</span></div>
              )}
            </div>
          )}

          {/* Test summary bar */}
          <div className="px-6 py-3 border-b border-gray-100 flex items-center justify-between">
            <div className="flex items-center gap-4 text-xs">
              <span className="font-bold text-gray-700">Test Cases</span>
              <span className="text-gray-400">{gateTests.length} total</span>
              {passed > 0 && <span className="text-green-600 font-medium">{passed} passed</span>}
              {failed > 0 && <span className="text-red-600 font-medium">{failed} failed</span>}
              {pending > 0 && <span className="text-amber-600 font-medium">{pending} pending</span>}
            </div>
            {(gate.status === 'pending' || gate.status === 'failed') && (
              <button onClick={() => setShowCreate(true)}
                className="text-xs text-blue-600 hover:text-blue-700 font-medium px-3 py-1.5 rounded-lg hover:bg-blue-50 flex items-center gap-1">
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
                Add Test
              </button>
            )}
          </div>

          {/* Test progress bar */}
          {gateTests.length > 0 && (
            <div className="px-6 py-2 border-b border-gray-100">
              <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden flex">
                {passed > 0 && <div className="h-full bg-green-500 transition-all" style={{ width: `${(passed / gateTests.length) * 100}%` }} />}
                {failed > 0 && <div className="h-full bg-red-500 transition-all" style={{ width: `${(failed / gateTests.length) * 100}%` }} />}
                {pending > 0 && <div className="h-full bg-amber-300 transition-all" style={{ width: `${(pending / gateTests.length) * 100}%` }} />}
              </div>
            </div>
          )}

          {/* Test case list */}
          {gateTests.length === 0 && !showCreate ? (
            <div className="flex flex-col items-center justify-center py-12 text-gray-400">
              <svg className="w-8 h-8 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div className="text-sm font-medium">No test cases yet</div>
              <div className="text-xs mt-1">Create test cases to start testing this gate</div>
              {(gate.status === 'pending' || gate.status === 'failed') && (
                <button onClick={() => setShowCreate(true)}
                  className="mt-3 text-xs text-blue-600 hover:text-blue-700 font-medium px-4 py-2 rounded-lg hover:bg-blue-50 border border-blue-200">
                  Create First Test
                </button>
              )}
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {gateTests.map(tc => {
                const isExec = executeId === tc._id;
                const isReassign = reassignId === tc._id;

                return (
                  <div key={tc._id} className="px-6 py-3 hover:bg-gray-50/50 transition-colors">
                    <div className="flex items-center gap-3">
                      {tc.status === 'passed' ? (
                        <div className="w-5 h-5 rounded-full bg-green-500 flex items-center justify-center shrink-0">
                          <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                        </div>
                      ) : tc.status === 'failed' ? (
                        <div className="w-5 h-5 rounded-full bg-red-500 flex items-center justify-center shrink-0">
                          <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                        </div>
                      ) : (
                        <div className="w-5 h-5 rounded-full border-2 border-gray-300 shrink-0" />
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-gray-800 font-medium truncate">{tc.title}</div>
                        {tc.description && <div className="text-xs text-gray-400 mt-0.5 truncate">{tc.description}</div>}
                      </div>
                      {tc.assignedTo?.name && (
                        <div className="flex items-center gap-1 shrink-0">
                          <div className="w-5 h-5 rounded-full bg-blue-500 text-white text-[10px] flex items-center justify-center">{tc.assignedTo.name[0]?.toUpperCase()}</div>
                          <span className="text-xs text-gray-500">{tc.assignedTo.name.split(' ')[0]}</span>
                        </div>
                      )}
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium shrink-0 ${
                        tc.status === 'passed' ? 'bg-green-100 text-green-700' :
                        tc.status === 'failed' ? 'bg-red-100 text-red-700' :
                        tc.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                        'bg-gray-100 text-gray-600'
                      }`}>{tc.status === 'in_progress' ? 'in progress' : tc.status}</span>
                      <div className="flex items-center gap-1 shrink-0">
                        {(tc.status === 'pending' || tc.status === 'in_progress' || tc.status === 'failed') && (
                          <button onClick={() => { setExecuteId(isExec ? null : tc._id); setReassignId(null); setExecuteNotes(''); }}
                            className={`text-xs font-medium px-2 py-1 rounded-lg transition-colors ${isExec ? 'bg-blue-100 text-blue-700' : 'text-blue-600 hover:bg-blue-50'}`}>
                            {tc.status === 'failed' ? 'Retest' : 'Run'}
                          </button>
                        )}
                        {(tc.status === 'pending' || tc.status === 'in_progress') && (
                          <button onClick={() => { setReassignId(isReassign ? null : tc._id); setExecuteId(null); setReassignSearch(''); }}
                            className={`text-xs px-2 py-1 rounded-lg transition-colors ${isReassign ? 'bg-gray-200 text-gray-700' : 'text-gray-500 hover:bg-gray-100'}`}>
                            Reassign
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Steps (shown when executing or test failed) */}
                    {tc.steps && tc.steps.length > 0 && (isExec || tc.status === 'failed') && (
                      <div className="mt-2 ml-8 space-y-1 bg-gray-50 rounded-lg p-2.5">
                        {tc.steps.map((s: any, si: number) => (
                          <div key={si} className="text-xs text-gray-600 flex gap-2">
                            <span className="text-gray-400 shrink-0 font-medium">{si + 1}.</span>
                            <span className="flex-1">{s.step}</span>
                            {s.expectedResult && <span className="text-gray-400 shrink-0">→ {s.expectedResult}</span>}
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Execution history */}
                    {tc.executionHistory && tc.executionHistory.length > 0 && !isExec && (
                      <div className="mt-2 ml-8 space-y-1">
                        {tc.executionHistory.length > 1 && (
                          <div className="text-[10px] font-medium text-gray-400 uppercase tracking-wide mb-1">
                            {tc.executionHistory.length} attempt{tc.executionHistory.length !== 1 ? 's' : ''}
                          </div>
                        )}
                        {tc.executionHistory.map((run: any, ri: number) => (
                          <div key={ri} className={`text-xs rounded-lg p-2 flex items-start gap-2 ${run.status === 'passed' ? 'bg-green-50 border border-green-100' : 'bg-red-50 border border-red-100'}`}>
                            <div className={`w-4 h-4 rounded-full flex items-center justify-center shrink-0 mt-0.5 ${run.status === 'passed' ? 'bg-green-500' : 'bg-red-500'}`}>
                              {run.status === 'passed' ? (
                                <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                              ) : (
                                <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <span className={`font-medium ${run.status === 'passed' ? 'text-green-700' : 'text-red-700'}`}>{run.status === 'passed' ? 'Passed' : 'Failed'}</span>
                                <span className="text-gray-400">
                                  {run.executedBy?.name}{run.executedAt && ` · ${new Date(run.executedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}`}
                                </span>
                              </div>
                              {run.resultNotes && <div className="text-gray-600 mt-0.5">{run.resultNotes}</div>}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    {/* Fallback for tests without history */}
                    {tc.resultNotes && !isExec && (!tc.executionHistory || tc.executionHistory.length === 0) && (
                      <div className="mt-2 ml-8 text-xs text-gray-600 bg-gray-50 rounded-lg p-2.5">
                        <span className="font-medium text-gray-500">Result:</span> {tc.resultNotes}
                        {tc.executedBy?.name && <span className="text-gray-400"> — {tc.executedBy.name}{tc.executedAt && `, ${new Date(tc.executedAt).toLocaleDateString()}`}</span>}
                      </div>
                    )}

                    {/* Execute form */}
                    {isExec && (
                      <div className="mt-2 ml-8 bg-white border border-gray-200 rounded-lg p-3 space-y-2">
                        <textarea value={executeNotes} onChange={e => setExecuteNotes(e.target.value)}
                          placeholder="Describe the test result..."
                          className="w-full text-sm border border-gray-200 rounded-lg p-2.5 resize-none focus:outline-none focus:ring-1 focus:ring-blue-400 bg-white" rows={2} />
                        <div className="flex gap-2">
                          <button onClick={() => handleExecute(tc._id, 'passed')} className="px-4 py-2 text-xs bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium">Pass</button>
                          <button onClick={() => handleExecute(tc._id, 'failed')} className="px-4 py-2 text-xs bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium">Fail</button>
                          <button onClick={() => { setExecuteId(null); setExecuteNotes(''); }} className="px-4 py-2 text-xs text-gray-500 hover:text-gray-700">Cancel</button>
                        </div>
                      </div>
                    )}

                    {/* Reassign picker */}
                    {isReassign && (
                      <div className="mt-2 ml-8 bg-white border border-gray-200 rounded-lg p-3">
                        <input value={reassignSearch} onChange={e => setReassignSearch(e.target.value)}
                          placeholder="Search team members..."
                          className="w-full text-sm border border-gray-200 rounded-lg px-2.5 py-1.5 mb-2 focus:outline-none focus:ring-1 focus:ring-blue-400" />
                        <div className="max-h-36 overflow-y-auto space-y-0.5">
                          {projectMembers
                            .filter((u: any) => u.userId !== tc.assignedTo?.userId?.toString())
                            .filter((u: any) => !reassignSearch || u.name?.toLowerCase().includes(reassignSearch.toLowerCase()))
                            .map((u: any) => (
                              <button key={u.userId} onClick={() => handleReassign(tc._id, u.userId, u.name)}
                                className="w-full text-left px-3 py-2 text-sm hover:bg-blue-50 rounded-lg flex items-center gap-2 transition-colors">
                                <div className="w-6 h-6 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-xs font-medium shrink-0">{u.name?.charAt(0)?.toUpperCase()}</div>
                                <span className="text-gray-700">{u.name}</span>
                              </button>
                            ))
                          }
                        </div>
                        <button onClick={() => setReassignId(null)} className="mt-2 text-xs text-gray-500 hover:text-gray-700">Cancel</button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Create test form */}
          {showCreate && (
            <div className="px-6 py-4 border-t border-gray-100 bg-gray-50/50">
              <div className="text-xs font-bold text-gray-700 mb-3">New Test Case</div>
              <div className="space-y-2.5">
                <input value={createForm.title} onChange={e => setCreateForm(p => ({ ...p, title: e.target.value }))}
                  placeholder="Test case title"
                  className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-1 focus:ring-blue-400"
                  autoFocus
                  onKeyDown={e => { if (e.key === 'Escape') { setShowCreate(false); setCreateForm({ title: '', description: '', steps: [{ step: '', expectedResult: '' }] }); } }} />
                <textarea value={createForm.description} onChange={e => setCreateForm(p => ({ ...p, description: e.target.value }))}
                  placeholder="Description (optional)"
                  className="w-full text-sm border border-gray-200 rounded-lg p-2.5 bg-white resize-none focus:outline-none focus:ring-1 focus:ring-blue-400" rows={2} />
                <select value={createAssignee.userId} onChange={e => {
                  const u = projectMembers.find((m: any) => m.userId === e.target.value);
                  setCreateAssignee({ userId: e.target.value, name: u?.name || '' });
                }} className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-1 focus:ring-blue-400">
                  <option value="">{gate.assignedTo?.name ? `${gate.assignedTo.name} (gate assignee)` : 'Assign to...'}</option>
                  {projectMembers.filter((m: any) => m.userId !== gate.assignedTo?.userId?.toString()).map((m: any) => <option key={m.userId} value={m.userId}>{m.name}</option>)}
                </select>
                <div>
                  <div className="text-xs font-medium text-gray-500 mb-1.5">Test Steps</div>
                  {createForm.steps.map((s, si) => (
                    <div key={si} className="flex gap-2 mb-1.5">
                      <span className="text-xs text-gray-400 mt-2 shrink-0 w-4 text-right">{si + 1}.</span>
                      <input value={s.step} onChange={e => { const steps = [...createForm.steps]; steps[si] = { ...steps[si], step: e.target.value }; setCreateForm(p => ({ ...p, steps })); }}
                        placeholder="Step" className="flex-1 text-sm border border-gray-200 rounded-lg px-2.5 py-1.5 bg-white focus:outline-none focus:ring-1 focus:ring-blue-400" />
                      <input value={s.expectedResult} onChange={e => { const steps = [...createForm.steps]; steps[si] = { ...steps[si], expectedResult: e.target.value }; setCreateForm(p => ({ ...p, steps })); }}
                        placeholder="Expected result" className="flex-1 text-sm border border-gray-200 rounded-lg px-2.5 py-1.5 bg-white focus:outline-none focus:ring-1 focus:ring-blue-400" />
                      {createForm.steps.length > 1 && (
                        <button onClick={() => setCreateForm(p => ({ ...p, steps: p.steps.filter((_, i) => i !== si) }))} className="text-red-400 hover:text-red-600 text-sm px-1">x</button>
                      )}
                    </div>
                  ))}
                  <button onClick={() => setCreateForm(p => ({ ...p, steps: [...p.steps, { step: '', expectedResult: '' }] }))}
                    className="text-xs text-blue-600 hover:text-blue-700 font-medium mt-0.5">+ Add step</button>
                </div>
                <div className="flex gap-2 pt-1">
                  <button onClick={handleCreate} disabled={!createForm.title || !resolvedAssignee}
                    className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium disabled:opacity-50">Create</button>
                  <button onClick={() => { setShowCreate(false); setCreateForm({ title: '', description: '', steps: [{ step: '', expectedResult: '' }] }); setCreateAssignee({ userId: '', name: '' }); }}
                    className="px-4 py-2 text-sm text-gray-500 hover:text-gray-700">Cancel</button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const TaskTestPanel: React.FC<{
  task: any;
  tests: any[];
  projectId: string;
  projectMembers: any[];
  onClose: () => void;
  onTaskClick: (taskId: string) => void;
  onRefresh: () => void;
}> = ({ task, tests: initialTests, projectId, projectMembers, onClose, onTaskClick, onRefresh }) => {
  const [taskTests, setTaskTests] = useState<any[]>(initialTests);
  const [executeId, setExecuteId] = useState<string | null>(null);
  const [executeNotes, setExecuteNotes] = useState('');
  const [reassignId, setReassignId] = useState<string | null>(null);
  const [reassignSearch, setReassignSearch] = useState('');
  const [showCreate, setShowCreate] = useState(initialTests.length === 0);
  const [createForm, setCreateForm] = useState({ title: '', description: '', steps: [{ step: '', expectedResult: '' }] });
  const [createAssignee, setCreateAssignee] = useState({ userId: '', name: '' });
  const [error, setError] = useState<string | null>(null);

  const refreshTests = useCallback(async () => {
    try {
      const res = await testCaseAPI.getByTask(task._id);
      setTaskTests(res.data.data || []);
    } catch (err: any) { setError(extractErrMsg(err, 'Failed to load tests')); }
  }, [task._id]);

  useEffect(() => { refreshTests(); }, [refreshTests]);

  const passed = taskTests.filter(t => t.status === 'passed').length;
  const failed = taskTests.filter(t => t.status === 'failed').length;
  const pending = taskTests.filter(t => t.status === 'pending' || t.status === 'in_progress').length;
  const taskStatus = STATUS_PILLS[task.status] || STATUS_PILLS.todo;

  const featureId = task.parentType === 'feature' ? task.parentId : undefined;

  const handleCreate = async () => {
    if (!createForm.title) return;
    const assignee = createAssignee.userId
      ? createAssignee
      : projectMembers.length > 0 ? { userId: projectMembers[0].userId, name: projectMembers[0].name } : null;
    if (!assignee) return;
    try {
      await testCaseAPI.create({
        title: createForm.title,
        description: createForm.description || undefined,
        taskId: task._id,
        featureId,
        projectId,
        steps: createForm.steps.filter(s => s.step),
        assignedToUserId: assignee.userId,
        assignedToName: assignee.name,
      });
      setCreateForm({ title: '', description: '', steps: [{ step: '', expectedResult: '' }] });
      setCreateAssignee({ userId: '', name: '' });
      setShowCreate(false);
      refreshTests();
      onRefresh();
    } catch (err: any) { setError(extractErrMsg(err, 'Failed to create test case')); }
  };

  const handleExecute = async (id: string, status: 'passed' | 'failed') => {
    try {
      await testCaseAPI.execute(id, { status, resultNotes: executeNotes });
      setExecuteId(null);
      setExecuteNotes('');
      refreshTests();
      onRefresh();
    } catch (err: any) { setError(extractErrMsg(err, 'Failed to execute test')); }
  };

  const handleReassign = async (id: string, userId: string, name: string) => {
    try {
      await testCaseAPI.reassign(id, { assignedToUserId: userId, assignedToName: name });
      setReassignId(null);
      refreshTests();
      onRefresh();
    } catch (err: any) { setError(extractErrMsg(err, 'Failed to reassign')); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" onClick={onClose}>
      <div className="absolute inset-0 bg-black/30" />
      <div className="relative bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden" style={{ width: '90vw', maxWidth: '720px', height: '85vh' }}
        onClick={e => e.stopPropagation()}>

        <div className="flex items-center gap-3 px-6 py-4 border-b border-gray-100 shrink-0">
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h3 className="text-base font-semibold text-gray-900 truncate">{task.title}</h3>
              <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${taskStatus.bg} ${taskStatus.text}`}>{task.status === 'in_progress' ? 'active' : task.status}</span>
            </div>
            <div className="flex items-center gap-3 mt-0.5 text-xs text-gray-400">
              <span>QA Testing</span>
              {task.assignedTo?.[0]?.name && <span>Dev: <span className="text-gray-600 font-medium">{task.assignedTo[0].name}</span></span>}
            </div>
          </div>
          <button onClick={() => onTaskClick(task._id)} className="text-xs text-blue-600 hover:text-blue-700 font-medium px-3 py-1.5 rounded-lg hover:bg-blue-50 shrink-0">
            View Task
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          <ErrorToast message={error} onDismiss={() => setError(null)} />
          <div className="px-6 py-3 border-b border-gray-100 flex items-center justify-between">
            <div className="flex items-center gap-4 text-xs">
              <span className="font-bold text-gray-700">Test Cases</span>
              <span className="text-gray-400">{taskTests.length} total</span>
              {passed > 0 && <span className="text-green-600 font-medium">{passed} passed</span>}
              {failed > 0 && <span className="text-red-600 font-medium">{failed} failed</span>}
              {pending > 0 && <span className="text-amber-600 font-medium">{pending} pending</span>}
            </div>
            <button onClick={() => setShowCreate(true)}
              className="text-xs text-blue-600 hover:text-blue-700 font-medium px-3 py-1.5 rounded-lg hover:bg-blue-50 flex items-center gap-1">
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
              Add Test
            </button>
          </div>

          {taskTests.length > 0 && (
            <div className="px-6 py-2 border-b border-gray-100">
              <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden flex">
                {passed > 0 && <div className="h-full bg-green-500 transition-all" style={{ width: `${(passed / taskTests.length) * 100}%` }} />}
                {failed > 0 && <div className="h-full bg-red-500 transition-all" style={{ width: `${(failed / taskTests.length) * 100}%` }} />}
                {pending > 0 && <div className="h-full bg-amber-300 transition-all" style={{ width: `${(pending / taskTests.length) * 100}%` }} />}
              </div>
            </div>
          )}

          {taskTests.length === 0 && !showCreate ? (
            <div className="flex flex-col items-center justify-center py-12 text-gray-400">
              <svg className="w-8 h-8 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div className="text-sm font-medium">No test cases yet</div>
              <div className="text-xs mt-1">Create test cases to start testing this task</div>
              <button onClick={() => setShowCreate(true)}
                className="mt-3 text-xs text-blue-600 hover:text-blue-700 font-medium px-4 py-2 rounded-lg hover:bg-blue-50 border border-blue-200">
                Create First Test
              </button>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {taskTests.map(tc => {
                const isExec = executeId === tc._id;
                const isReassign = reassignId === tc._id;
                return (
                  <div key={tc._id} className="px-6 py-3 hover:bg-gray-50/50 transition-colors">
                    <div className="flex items-center gap-3">
                      {tc.status === 'passed' ? (
                        <div className="w-5 h-5 rounded-full bg-green-500 flex items-center justify-center shrink-0">
                          <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                        </div>
                      ) : tc.status === 'failed' ? (
                        <div className="w-5 h-5 rounded-full bg-red-500 flex items-center justify-center shrink-0">
                          <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                        </div>
                      ) : (
                        <div className="w-5 h-5 rounded-full border-2 border-gray-300 shrink-0" />
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-gray-800 font-medium truncate">{tc.title}</div>
                        {tc.description && <div className="text-xs text-gray-400 mt-0.5 truncate">{tc.description}</div>}
                      </div>
                      {tc.assignedTo?.name && (
                        <div className="flex items-center gap-1 shrink-0">
                          <div className="w-5 h-5 rounded-full bg-blue-500 text-white text-[10px] flex items-center justify-center">{tc.assignedTo.name[0]?.toUpperCase()}</div>
                          <span className="text-xs text-gray-500">{tc.assignedTo.name.split(' ')[0]}</span>
                        </div>
                      )}
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium shrink-0 ${
                        tc.status === 'passed' ? 'bg-green-100 text-green-700' :
                        tc.status === 'failed' ? 'bg-red-100 text-red-700' :
                        tc.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                        'bg-gray-100 text-gray-600'
                      }`}>{tc.status === 'in_progress' ? 'in progress' : tc.status}</span>
                      <div className="flex items-center gap-1 shrink-0">
                        {(tc.status === 'pending' || tc.status === 'in_progress' || tc.status === 'failed') && (
                          <button onClick={() => { setExecuteId(isExec ? null : tc._id); setReassignId(null); setExecuteNotes(''); }}
                            className={`text-xs font-medium px-2 py-1 rounded-lg transition-colors ${isExec ? 'bg-blue-100 text-blue-700' : 'text-blue-600 hover:bg-blue-50'}`}>
                            {tc.status === 'failed' ? 'Retest' : 'Run'}
                          </button>
                        )}
                        {(tc.status === 'pending' || tc.status === 'in_progress') && (
                          <button onClick={() => { setReassignId(isReassign ? null : tc._id); setExecuteId(null); setReassignSearch(''); }}
                            className={`text-xs px-2 py-1 rounded-lg transition-colors ${isReassign ? 'bg-gray-200 text-gray-700' : 'text-gray-500 hover:bg-gray-100'}`}>
                            Reassign
                          </button>
                        )}
                      </div>
                    </div>

                    {tc.steps && tc.steps.length > 0 && (isExec || tc.status === 'failed') && (
                      <div className="mt-2 ml-8 space-y-1 bg-gray-50 rounded-lg p-2.5">
                        {tc.steps.map((s: any, si: number) => (
                          <div key={si} className="text-xs text-gray-600 flex gap-2">
                            <span className="text-gray-400 shrink-0 font-medium">{si + 1}.</span>
                            <span className="flex-1">{s.step}</span>
                            {s.expectedResult && <span className="text-gray-400 shrink-0">→ {s.expectedResult}</span>}
                          </div>
                        ))}
                      </div>
                    )}

                    {tc.executionHistory && tc.executionHistory.length > 0 && !isExec && (
                      <div className="mt-2 ml-8 space-y-1">
                        {tc.executionHistory.length > 1 && (
                          <div className="text-[10px] font-medium text-gray-400 uppercase tracking-wide mb-1">
                            {tc.executionHistory.length} attempt{tc.executionHistory.length !== 1 ? 's' : ''}
                          </div>
                        )}
                        {tc.executionHistory.map((run: any, ri: number) => (
                          <div key={ri} className={`text-xs rounded-lg p-2 flex items-start gap-2 ${run.status === 'passed' ? 'bg-green-50 border border-green-100' : 'bg-red-50 border border-red-100'}`}>
                            <div className={`w-4 h-4 rounded-full flex items-center justify-center shrink-0 mt-0.5 ${run.status === 'passed' ? 'bg-green-500' : 'bg-red-500'}`}>
                              {run.status === 'passed' ? (
                                <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                              ) : (
                                <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <span className={`font-medium ${run.status === 'passed' ? 'text-green-700' : 'text-red-700'}`}>{run.status === 'passed' ? 'Passed' : 'Failed'}</span>
                                <span className="text-gray-400">
                                  {run.executedBy?.name}{run.executedAt && ` · ${new Date(run.executedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}`}
                                </span>
                              </div>
                              {run.resultNotes && <div className="text-gray-600 mt-0.5">{run.resultNotes}</div>}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    {tc.resultNotes && !isExec && (!tc.executionHistory || tc.executionHistory.length === 0) && (
                      <div className="mt-2 ml-8 text-xs text-gray-600 bg-gray-50 rounded-lg p-2.5">
                        <span className="font-medium text-gray-500">Result:</span> {tc.resultNotes}
                        {tc.executedBy?.name && <span className="text-gray-400"> — {tc.executedBy.name}{tc.executedAt && `, ${new Date(tc.executedAt).toLocaleDateString()}`}</span>}
                      </div>
                    )}

                    {isExec && (
                      <div className="mt-2 ml-8 bg-white border border-gray-200 rounded-lg p-3 space-y-2">
                        <textarea value={executeNotes} onChange={e => setExecuteNotes(e.target.value)}
                          placeholder="Describe the test result..."
                          className="w-full text-sm border border-gray-200 rounded-lg p-2.5 resize-none focus:outline-none focus:ring-1 focus:ring-blue-400 bg-white" rows={2} />
                        <div className="flex gap-2">
                          <button onClick={() => handleExecute(tc._id, 'passed')} className="px-4 py-2 text-xs bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium">Pass</button>
                          <button onClick={() => handleExecute(tc._id, 'failed')} className="px-4 py-2 text-xs bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium">Fail</button>
                          <button onClick={() => { setExecuteId(null); setExecuteNotes(''); }} className="px-4 py-2 text-xs text-gray-500 hover:text-gray-700">Cancel</button>
                        </div>
                      </div>
                    )}

                    {isReassign && (
                      <div className="mt-2 ml-8 bg-white border border-gray-200 rounded-lg p-3">
                        <input value={reassignSearch} onChange={e => setReassignSearch(e.target.value)}
                          placeholder="Search team members..."
                          className="w-full text-sm border border-gray-200 rounded-lg px-2.5 py-1.5 mb-2 focus:outline-none focus:ring-1 focus:ring-blue-400" />
                        <div className="max-h-36 overflow-y-auto space-y-0.5">
                          {projectMembers
                            .filter((u: any) => u.userId !== tc.assignedTo?.userId?.toString())
                            .filter((u: any) => !reassignSearch || u.name?.toLowerCase().includes(reassignSearch.toLowerCase()))
                            .map((u: any) => (
                              <button key={u.userId} onClick={() => handleReassign(tc._id, u.userId, u.name)}
                                className="w-full text-left px-3 py-2 text-sm hover:bg-blue-50 rounded-lg flex items-center gap-2 transition-colors">
                                <div className="w-6 h-6 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-xs font-medium shrink-0">{u.name?.charAt(0)?.toUpperCase()}</div>
                                <span className="text-gray-700">{u.name}</span>
                              </button>
                            ))
                          }
                        </div>
                        <button onClick={() => setReassignId(null)} className="mt-2 text-xs text-gray-500 hover:text-gray-700">Cancel</button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {showCreate && (
            <div className="px-6 py-4 border-t border-gray-100 bg-gray-50/50">
              <div className="text-xs font-bold text-gray-700 mb-3">New Test Case</div>
              <div className="space-y-2.5">
                <input value={createForm.title} onChange={e => setCreateForm(p => ({ ...p, title: e.target.value }))}
                  placeholder="Test case title"
                  className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-1 focus:ring-blue-400"
                  autoFocus
                  onKeyDown={e => { if (e.key === 'Escape') { setShowCreate(false); setCreateForm({ title: '', description: '', steps: [{ step: '', expectedResult: '' }] }); } }} />
                <textarea value={createForm.description} onChange={e => setCreateForm(p => ({ ...p, description: e.target.value }))}
                  placeholder="Description (optional)"
                  className="w-full text-sm border border-gray-200 rounded-lg p-2.5 bg-white resize-none focus:outline-none focus:ring-1 focus:ring-blue-400" rows={2} />
                <select value={createAssignee.userId} onChange={e => {
                  const u = projectMembers.find((m: any) => m.userId === e.target.value);
                  setCreateAssignee({ userId: e.target.value, name: u?.name || '' });
                }} className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-1 focus:ring-blue-400">
                  <option value="">Select QA tester...</option>
                  {projectMembers.map((m: any) => <option key={m.userId} value={m.userId}>{m.name}</option>)}
                </select>
                <div>
                  <div className="text-xs font-medium text-gray-500 mb-1.5">Test Steps</div>
                  {createForm.steps.map((s, si) => (
                    <div key={si} className="flex gap-2 mb-1.5">
                      <span className="text-xs text-gray-400 mt-2 shrink-0 w-4 text-right">{si + 1}.</span>
                      <input value={s.step} onChange={e => { const steps = [...createForm.steps]; steps[si] = { ...steps[si], step: e.target.value }; setCreateForm(p => ({ ...p, steps })); }}
                        placeholder="Step" className="flex-1 text-sm border border-gray-200 rounded-lg px-2.5 py-1.5 bg-white focus:outline-none focus:ring-1 focus:ring-blue-400" />
                      <input value={s.expectedResult} onChange={e => { const steps = [...createForm.steps]; steps[si] = { ...steps[si], expectedResult: e.target.value }; setCreateForm(p => ({ ...p, steps })); }}
                        placeholder="Expected result" className="flex-1 text-sm border border-gray-200 rounded-lg px-2.5 py-1.5 bg-white focus:outline-none focus:ring-1 focus:ring-blue-400" />
                      {createForm.steps.length > 1 && (
                        <button onClick={() => setCreateForm(p => ({ ...p, steps: p.steps.filter((_, i) => i !== si) }))} className="text-red-400 hover:text-red-600 text-sm px-1">x</button>
                      )}
                    </div>
                  ))}
                  <button onClick={() => setCreateForm(p => ({ ...p, steps: [...p.steps, { step: '', expectedResult: '' }] }))}
                    className="text-xs text-blue-600 hover:text-blue-700 font-medium mt-0.5">+ Add step</button>
                </div>
                <div className="flex gap-2 pt-1">
                  <button onClick={handleCreate} disabled={!createForm.title || (!createAssignee.userId && projectMembers.length === 0)}
                    className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium disabled:opacity-50">Create</button>
                  <button onClick={() => { setShowCreate(false); setCreateForm({ title: '', description: '', steps: [{ step: '', expectedResult: '' }] }); setCreateAssignee({ userId: '', name: '' }); }}
                    className="px-4 py-2 text-sm text-gray-500 hover:text-gray-700">Cancel</button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const EmployeeWorkspace: React.FC<{ embedded?: boolean }> = ({ embedded }) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Read initial state from URL
  const [sideTab, setSideTab] = useState<'projects' | 'sprints'>(
    searchParams.get('tab') === 'sprints' ? 'sprints' : 'projects'
  );
  const [selectedProject, setSelectedProject] = useState<any>(null);
  const [selectedSprint, setSelectedSprint] = useState<{ _id: string; name: string } | null>(null);
  const [viewTab, setViewTab] = useState<ViewTab>(
    (searchParams.get('view') as ViewTab) || 'list'
  );
  // If URL has projectId, we need to restore before rendering content
  const needsRestore = !!searchParams.get('projectId');
  const [restoredFromUrl, setRestoredFromUrl] = useState(!needsRestore);

  // Restore project + sprint from URL on mount
  useEffect(() => {
    const pid = searchParams.get('projectId');
    const sid = searchParams.get('sprintId');
    if (pid && !selectedProject) {
      projectAPI.getProjectById(pid).then(res => {
        const p = res.data?.data || res.data?.project || res.data;
        if (p?._id) {
          setSelectedProject(p);
          if (sid) {
            setSelectedSprint({ _id: sid, name: '' });
            setSideTab('sprints');
          }
        }
        setRestoredFromUrl(true);
      }).catch(() => setRestoredFromUrl(true));
    } else {
      setRestoredFromUrl(true);
    }
  }, []); // run once on mount

  // Reset state when navigating to clean dashboard (no params)
  useEffect(() => {
    if (restoredFromUrl && !searchParams.get('projectId') && selectedProject) {
      setSelectedProject(null);
      setSelectedSprint(null);
      setSideTab('projects');
      setViewTab('list');
    }
  }, [searchParams.get('projectId')]);

  // Sync state → URL (after initial restore)
  useEffect(() => {
    if (!restoredFromUrl) return;
    const params: Record<string, string> = {};
    if (sideTab !== 'projects') params.tab = sideTab;
    if (selectedProject?._id) params.projectId = selectedProject._id;
    if (selectedSprint?._id) params.sprintId = selectedSprint._id;
    if (viewTab !== 'list' && !selectedSprint) params.view = viewTab;
    setSearchParams(params, { replace: true });
  }, [sideTab, selectedProject?._id, selectedSprint?._id, viewTab, restoredFromUrl]);

  // Project data
  const [tasks, setTasks] = useState<any[]>([]);
  const [features, setFeatures] = useState<any[]>([]);
  const [dataLoading, setDataLoading] = useState(false);

  const fetchProjectData = useCallback(async (projectId: string, silent = false) => {
    if (!silent) setDataLoading(true);
    try {
      const [tasksRes, featuresRes] = await Promise.all([
        projectAPI.getTasks(projectId),
        featureAPI.getByProject(projectId),
      ]);
      setTasks(Array.isArray(tasksRes.data.data) ? tasksRes.data.data : []);
      setFeatures(Array.isArray(featuresRes.data.data) ? featuresRes.data.data : []);
    } catch {
      setTasks([]);
      setFeatures([]);
    }
    setDataLoading(false);
  }, []);

  // Sprint + bug data
  const dispatch = useAppDispatch();
  const { sprints } = useAppSelector(s => s.sprints);
  const [bugs, setBugs] = useState<any[]>([]);

  useEffect(() => {
    if (selectedProject?._id) {
      dispatch(fetchSprintsByProject({ projectId: selectedProject._id }));
      api.get('/bugs', { params: { projectId: selectedProject._id, limit: '200' } }).then(res => {
        setBugs(Array.isArray(res.data.data) ? res.data.data : []);
      }).catch(() => setBugs([]));
    }
  }, [selectedProject?._id, dispatch]);

  useEffect(() => {
    if (selectedProject?._id) {
      fetchProjectData(selectedProject._id);
    }
  }, [selectedProject?._id, fetchProjectData]);

  // Task batches
  const [showBatchModal, setShowBatchModal] = useState(false);
  const [batches, setBatches] = useState<any[]>([]);
  const [selectedBatch, setSelectedBatch] = useState<any>(null);
  useEffect(() => {
    if (selectedProject?._id) {
      taskBatchAPI.getByProject(selectedProject._id).then(res => {
        setBatches(Array.isArray(res.data?.data) ? res.data.data : []);
      }).catch(() => setBatches([]));
    }
  }, [selectedProject?._id]);
  const refreshBatches = () => {
    if (selectedProject?._id) {
      taskBatchAPI.getByProject(selectedProject._id).then(res => {
        setBatches(Array.isArray(res.data?.data) ? res.data.data : []);
      }).catch(() => {});
    }
  };

  // Stage gate settings from department
  const [stageGates, setStageGates] = useState<any[]>([]);
  useEffect(() => {
    const deptId = selectedProject?.departmentId;
    if (deptId) {
      departmentAPI.getDepartmentById(deptId).then(res => {
        const gates = res.data?.data?.workflowSettings?.stageGates || [];
        setStageGates(gates);
      }).catch(() => setStageGates([]));
    } else {
      setStageGates([]);
    }
  }, [selectedProject?.departmentId]);

  const projectMembers = useMemo(() => {
    if (!selectedProject) return [];
    const members: { userId: string; name: string }[] = [];
    if (selectedProject.owner?.userId) {
      members.push({ userId: selectedProject.owner.userId, name: selectedProject.owner.name || 'Owner' });
    }
    for (const m of (selectedProject.members || [])) {
      if (m.userId && !members.some(x => x.userId === m.userId)) {
        members.push({ userId: m.userId, name: m.name || 'Member' });
      }
    }
    return members;
  }, [selectedProject]);

  const handleSelectProject = (project: any) => {
    setSelectedProject(project);
    setSelectedSprint(null);
    setViewTab('list');
    // Auto-dismiss matching project notifications
    if (project?._id) {
      notificationAPI.getMyNotifications({ limit: 50, unreadOnly: true }).then(res => {
        const matching = (res.data?.data ?? []).filter((n: any) => !n.read && String(n.metadata?.projectId?.$oid || n.metadata?.projectId) === project._id);
        matching.forEach((n: any) => notificationAPI.markAsRead(n._id).catch(() => {}));
      }).catch(() => {});
    }
  };

  const handleSelectSprint = (sprint: any) => {
    setSelectedSprint({ _id: sprint._id, name: sprint.name });
    setSideTab('sprints');
  };

  // Folder panel
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);
  const [showProjectMenu, setShowProjectMenu] = useState(false);
  const [editingProjectName, setEditingProjectName] = useState(false);
  const [confirmDeleteProject, setConfirmDeleteProject] = useState(false);

  // Bug panel
  const [selectedBugId, setSelectedBugId] = useState<string | null>(
    searchParams.get('bugId') || null
  );
  const handleBugClick = (bugId: string) => {
    setSelectedBugId(bugId);
    setSearchParams(prev => { const n = new URLSearchParams(prev); n.set('bugId', bugId); return n; }, { replace: true });
  };
  const handleBugClose = () => {
    setSelectedBugId(null);
    setSearchParams(prev => { const n = new URLSearchParams(prev); n.delete('bugId'); return n; }, { replace: true });
  };

  // Feature panel (slide-in overlay)
  const [selectedFeatureId, setSelectedFeatureId] = useState<string | null>(
    searchParams.get('featureId') || null
  );
  const handleFeatureClick = (featureId: string) => {
    setSelectedFeatureId(featureId);
    setSearchParams(prev => { const n = new URLSearchParams(prev); n.set('featureId', featureId); return n; }, { replace: true });
  };
  const handleFeatureClose = () => {
    setSelectedFeatureId(null);
    setSearchParams(prev => { const n = new URLSearchParams(prev); n.delete('featureId'); return n; }, { replace: true });
  };

  // Task panel (slide-in overlay instead of full page navigation)
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(
    searchParams.get('taskId') || null
  );

  // React to taskId search param changes (e.g. from notification click while already on this page)
  useEffect(() => {
    const paramTaskId = searchParams.get('taskId');
    if (paramTaskId && paramTaskId !== selectedTaskId) {
      setSelectedTaskId(paramTaskId);
      // Auto-dismiss matching notifications
      notificationAPI.getMyNotifications({ limit: 50, unreadOnly: true }).then(res => {
        const matching = (res.data?.data ?? []).filter((n: any) => !n.read && String(n.metadata?.taskId?.$oid || n.metadata?.taskId) === paramTaskId);
        matching.forEach((n: any) => notificationAPI.markAsRead(n._id).catch(() => {}));
      }).catch(() => {});
    }
  }, [searchParams.get('taskId')]); // eslint-disable-line react-hooks/exhaustive-deps

  // Listen for notification-navigate custom events (when already on this page)
  useEffect(() => {
    const handler = (e: Event) => {
      const { taskId, projectId } = (e as CustomEvent).detail || {};
      if (taskId) {
        setSelectedTaskId(taskId);
        setSearchParams(prev => { const n = new URLSearchParams(prev); n.set('taskId', taskId); return n; }, { replace: true });
      }
      if (projectId) {
        // Set projectId in URL — the existing restore-from-URL logic will pick it up
        setSearchParams(prev => { const n = new URLSearchParams(prev); n.set('projectId', projectId); return n; }, { replace: true });
      }
    };
    window.addEventListener('notification-navigate', handler);
    return () => window.removeEventListener('notification-navigate', handler);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleTaskClick = (taskId: string) => {
    setSelectedTaskId(taskId);
    setSearchParams(prev => { const n = new URLSearchParams(prev); n.set('taskId', taskId); return n; }, { replace: true });
    // Auto-dismiss matching notifications
    notificationAPI.getMyNotifications({ limit: 50, unreadOnly: true }).then(res => {
      const matching = (res.data?.data ?? []).filter((n: any) => !n.read && String(n.metadata?.taskId?.$oid || n.metadata?.taskId) === taskId);
      matching.forEach((n: any) => notificationAPI.markAsRead(n._id).catch(() => {}));
    }).catch(() => {});
  };

  const handleTaskClose = () => {
    setSelectedTaskId(null);
    setSearchParams(prev => { const n = new URLSearchParams(prev); n.delete('taskId'); return n; }, { replace: true });
  };

  const handleRefresh = () => {
    if (selectedProject?._id) {
      fetchProjectData(selectedProject._id, true);
      // Also refresh the project object itself (for members, etc.)
      projectAPI.getProjectById(selectedProject._id).then(res => {
        const p = res.data?.data || res.data?.project || res.data;
        if (p?._id) setSelectedProject(p);
      }).catch(() => {});
    }
  };

  const statusColor: Record<string, string> = {
    planning: 'bg-gray-400', development: 'bg-blue-500', testing: 'bg-yellow-500',
    sit: 'bg-orange-400', uat: 'bg-teal-500', done: 'bg-green-500',
    production: 'bg-emerald-500', archived: 'bg-gray-300',
  };

  const STATUS_SEQ = ['planning', 'development', 'testing', 'sit', 'uat', 'done', 'production', 'archived'] as const;
  const STATUS_LABEL: Record<string, string> = {
    planning: 'Planning', development: 'Development', testing: 'Testing',
    sit: 'SIT', uat: 'UAT', done: 'Done', production: 'Production', archived: 'Archived',
  };
  const STATUS_BG: Record<string, string> = {
    planning: 'bg-gray-100 text-gray-700', development: 'bg-blue-100 text-blue-700',
    testing: 'bg-yellow-100 text-yellow-700', sit: 'bg-orange-100 text-orange-700',
    uat: 'bg-teal-100 text-teal-700', done: 'bg-green-100 text-green-700',
    production: 'bg-emerald-100 text-emerald-700', archived: 'bg-gray-200 text-gray-600',
  };

  const changeProjectStatus = (direction: 'prev' | 'next') => {
    if (!selectedProject) return;
    const idx = STATUS_SEQ.indexOf(selectedProject.status as any);
    if (idx === -1) return;
    const newIdx = direction === 'next' ? idx + 1 : idx - 1;
    if (newIdx < 0 || newIdx >= STATUS_SEQ.length) return;
    const newStatus = STATUS_SEQ[newIdx];
    projectAPI.updateProject(selectedProject._id, { status: newStatus })
      .then(() => handleRefresh())
      .catch(() => {});
  };

  return (
    <div className={`flex ${embedded ? 'h-full' : 'h-[calc(100vh-56px)]'}`}>
      {/* ── Left Panel ─────────────────────────────────────── */}
      <div className="w-60 border-r border-gray-200 bg-white flex flex-col shrink-0 hidden md:flex">
        {/* Tab bar */}
        <div className="flex border-b border-gray-200">
          {(['projects', 'sprints'] as const).map(t => (
            <button
              key={t}
              onClick={() => setSideTab(t)}
              className={`flex-1 px-3 py-2.5 text-xs font-semibold uppercase tracking-wider transition-colors ${
                sideTab === t ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-400 hover:text-gray-600'
              }`}
            >
              {t}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {sideTab === 'projects' && (
            <ProjectTree
              selectedProjectId={selectedProject?._id ?? null}
              onSelectProject={handleSelectProject}
            />
          )}
          {sideTab === 'sprints' && (
            selectedProject
              ? <SprintTree projectId={selectedProject._id} onSelectSprint={handleSelectSprint} selectedSprintId={selectedSprint?._id ?? null} />
              : <div className="p-4 text-center text-xs text-gray-400">Select a project first</div>
          )}
        </div>
      </div>

      {/* ── Right Panel — Workspace ────────────────────────── */}
      <div className="flex-1 flex flex-col overflow-hidden bg-gray-50">
        {!restoredFromUrl ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="animate-spin h-5 w-5 border-2 border-blue-500 border-t-transparent rounded-full" />
          </div>
        ) : selectedSprint && selectedProject ? (
          <SprintKanban
            projectId={selectedProject._id}
            sprintId={selectedSprint._id}
            onBack={() => { setSelectedSprint(null); setViewTab('sprints'); }}
            stageGates={stageGates}
            projectMembers={projectMembers}
          />
        ) : selectedProject ? (
          <>
            {/* Project header + view tabs */}
            <div className="bg-white border-b border-gray-200 px-4 shrink-0">
              {/* Breadcrumb */}
              <div className="flex items-center justify-between py-2">
                <div className="flex items-center gap-2">
                  <div className={`w-2.5 h-2.5 rounded-full ${statusColor[selectedProject.status] || 'bg-gray-400'}`} />
                  {editingProjectName ? (
                    <input autoFocus defaultValue={selectedProject.name}
                      className="text-sm font-semibold text-gray-900 border-b border-blue-400 outline-none bg-transparent px-0 py-0 capitalize"
                      onBlur={e => {
                        const val = e.target.value.trim();
                        if (val && val !== selectedProject.name) {
                          projectAPI.updateProject(selectedProject._id, { name: val }).then(() => handleRefresh()).catch(() => {});
                        }
                        setEditingProjectName(false);
                      }}
                      onKeyDown={e => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); if (e.key === 'Escape') setEditingProjectName(false); }}
                    />
                  ) : (
                    <h2 className="text-sm font-semibold text-gray-900 truncate max-w-md capitalize cursor-pointer" title="Double-click to edit"
                      onDoubleClick={() => setEditingProjectName(true)}>
                      {selectedProject.name}
                    </h2>
                  )}
                  {/* Status toggle: back / label / forward */}
                  <div className="flex items-center gap-0 shrink-0 ml-1">
                    <button
                      onClick={() => changeProjectStatus('prev')}
                      disabled={STATUS_SEQ.indexOf(selectedProject.status as any) <= 0}
                      className="p-0.5 rounded hover:bg-gray-100 disabled:opacity-20 disabled:cursor-not-allowed transition-colors"
                      title={STATUS_SEQ.indexOf(selectedProject.status as any) > 0 ? `Back to ${STATUS_LABEL[STATUS_SEQ[STATUS_SEQ.indexOf(selectedProject.status as any) - 1]]}` : ''}
                    >
                      <svg className="w-3.5 h-3.5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                      </svg>
                    </button>
                    <span className={`px-2 py-0.5 text-[11px] font-medium rounded-full ${STATUS_BG[selectedProject.status] || 'bg-gray-100 text-gray-700'}`}>
                      {STATUS_LABEL[selectedProject.status] || selectedProject.status}
                    </span>
                    <button
                      onClick={() => changeProjectStatus('next')}
                      disabled={STATUS_SEQ.indexOf(selectedProject.status as any) >= STATUS_SEQ.length - 1}
                      className="p-0.5 rounded hover:bg-gray-100 disabled:opacity-20 disabled:cursor-not-allowed transition-colors"
                      title={STATUS_SEQ.indexOf(selectedProject.status as any) < STATUS_SEQ.length - 1 ? `Advance to ${STATUS_LABEL[STATUS_SEQ[STATUS_SEQ.indexOf(selectedProject.status as any) + 1]]}` : ''}
                    >
                      <svg className="w-3.5 h-3.5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  </div>
                  {selectedProject.approvalStatus && selectedProject.approvalStatus !== 'not_required' && (
                    <span className={`text-[10px] px-2 py-0.5 rounded-full ${
                      selectedProject.approvalStatus === 'approved' ? 'bg-green-50 text-green-700' :
                      selectedProject.approvalStatus === 'pending' ? 'bg-yellow-50 text-yellow-700' :
                      selectedProject.approvalStatus === 'rejected' ? 'bg-red-50 text-red-700' :
                      'bg-gray-50 text-gray-400'
                    }`}>{selectedProject.approvalStatus === 'pending' ? 'Pending Approval' : cap(selectedProject.approvalStatus)}</span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                </div>
              </div>

              {/* View tabs — scrollable + ellipsis menu */}
              <div className="flex items-center -mb-px">
                <div className="flex gap-0.5 overflow-x-auto scrollbar-hide flex-1">
                  {VIEW_TABS.map(t => (
                    <button
                      key={t.key}
                      onClick={() => setViewTab(t.key)}
                      className={`px-3 py-2 text-xs font-medium border-b-2 transition-colors whitespace-nowrap shrink-0 flex items-center gap-1 ${
                        viewTab === t.key
                          ? 'border-blue-600 text-blue-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
                {/* Ellipsis menu */}
                <div className="relative shrink-0">
                  <button onClick={() => setShowProjectMenu(!showProjectMenu)}
                    className="px-2 py-2 text-gray-400 hover:text-gray-600 transition-colors">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><circle cx="4" cy="10" r="1.5" /><circle cx="10" cy="10" r="1.5" /><circle cx="16" cy="10" r="1.5" /></svg>
                  </button>
                  {showProjectMenu && (
                    <>
                      <div className="fixed inset-0 z-10" onClick={() => setShowProjectMenu(false)} />
                      <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-20 py-1 w-48">
                        {(!selectedProject.approvalStatus || selectedProject.approvalStatus === 'not_required' || selectedProject.approvalStatus === 'rejected') && (
                          <button onClick={() => {
                            projectAPI.submitForApproval(selectedProject._id).then(() => { handleRefresh(); setShowProjectMenu(false); }).catch(() => {});
                          }} className="w-full text-left px-3 py-2 text-xs text-gray-700 hover:bg-gray-50 transition-colors">
                            Submit for approval
                          </button>
                        )}
                        {selectedProject.approvalStatus === 'pending' && (
                          <span className="block px-3 py-2 text-xs text-yellow-600">Awaiting approval...</span>
                        )}
                        <button onClick={() => { setShowBatchModal(true); setShowProjectMenu(false); }}
                          className="w-full text-left px-3 py-2 text-xs text-gray-700 hover:bg-gray-50 transition-colors">
                          Submit batch for approval
                        </button>
                        <button onClick={() => navigate(`/employee/projects/${selectedProject._id}`)}
                          className="w-full text-left px-3 py-2 text-xs text-gray-700 hover:bg-gray-50 transition-colors">
                          Full view
                        </button>
                        <div className="border-t border-gray-100 my-1" />
                        <button onClick={() => { setConfirmDeleteProject(true); setShowProjectMenu(false); }}
                          className="w-full text-left px-3 py-2 text-xs text-red-600 hover:bg-red-50 transition-colors">
                          Delete project
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* View content */}
            <div className="flex-1 overflow-auto">
              {dataLoading ? (
                <div className="flex items-center justify-center h-48">
                  <div className="animate-spin h-5 w-5 border-2 border-blue-500 border-t-transparent rounded-full" />
                </div>
              ) : (
                <>
                  {viewTab === 'list' && (
                    <StatusGroupedList
                      projectId={selectedProject._id}
                      tasks={tasks}
                      features={features}
                      onTaskClick={handleTaskClick}
                      onRefresh={handleRefresh}
                    />
                  )}
                  {viewTab === 'board' && (
                    <div className="p-0">
                      <WorkspaceBoard
                        projectId={selectedProject._id}
                        tasks={tasks}
                        features={features}
                        onRefresh={handleRefresh}
                        onTaskClick={handleTaskClick}
                        stageGates={stageGates}
                        projectMembers={projectMembers}
                      />
                    </div>
                  )}
                  {viewTab === 'backlog' && (() => {
                    const backlogTasks = tasks.filter(t => !t.sprintId);
                    const backlogFeatures = features.filter(f => !f.sprintId);

                    // Group tasks by parent
                    const featureGroups: Record<string, { feature: any; tasks: any[] }> = {};
                    const folderGroups: Record<string, { name: string; tasks: any[] }> = {};
                    const standalone: any[] = [];

                    for (const t of backlogTasks) {
                      if (t.parentType === 'feature' && t.parentId) {
                        const f = features.find((ft: any) => ft._id === t.parentId);
                        if (f) {
                          if (!featureGroups[t.parentId]) featureGroups[t.parentId] = { feature: f, tasks: [] };
                          featureGroups[t.parentId].tasks.push(t);
                          continue;
                        }
                      }
                      if (t.parentType === 'folder' && t.parentId) {
                        if (!folderGroups[t.parentId]) folderGroups[t.parentId] = { name: t.parentId, tasks: [] };
                        folderGroups[t.parentId].tasks.push(t);
                        continue;
                      }
                      standalone.push(t);
                    }

                    // Features without tasks in backlog
                    const emptyFeatures = backlogFeatures.filter(f => !featureGroups[f._id]);

                    const fmtDate = (d?: string) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '';
                    const STATUS_DOT: Record<string, string> = { todo: 'bg-gray-400', in_progress: 'bg-blue-500', blocked: 'bg-red-500', review: 'bg-yellow-500', done: 'bg-green-500' };
                    const PRIORITY_PILL: Record<string, string> = { critical: 'bg-red-500 text-white', high: 'bg-orange-400 text-white', medium: 'bg-yellow-400 text-gray-900', low: 'bg-gray-200 text-gray-600' };

                    const TaskRow = ({ task, indent = false }: { task: any; indent?: boolean }) => (
                      <div className="grid grid-cols-12 gap-1 py-2.5 text-sm hover:bg-gray-50/80 cursor-pointer transition-colors" style={{ paddingLeft: indent ? '48px' : '20px', paddingRight: '20px' }} onClick={() => handleTaskClick(task._id)}>
                        <div className="col-span-4 flex items-center gap-2.5 min-w-0">
                          <div className={`w-2 h-2 rounded-full shrink-0 ${STATUS_DOT[task.status] || 'bg-gray-300'}`} />
                          <span className={`truncate ${task.status === 'done' ? 'line-through text-gray-400' : 'text-gray-800'}`} title={task.title}>{task.title}</span>
                        </div>
                        <div className="col-span-2 flex items-center">
                          {(task.assignedTo ?? []).length > 0 ? (
                            <div className="flex -space-x-1">
                              {(task.assignedTo ?? []).slice(0, 2).map((a: any, i: number) => (
                                <div key={i} className="w-5 h-5 rounded-full bg-blue-500 text-white text-xs flex items-center justify-center border border-white" title={a.name}>{(a.name || '?')[0]}</div>
                              ))}
                            </div>
                          ) : <span className="text-xs text-gray-300">—</span>}
                        </div>
                        <div className="col-span-1">{task.priority && <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${PRIORITY_PILL[task.priority] || 'bg-gray-200'}`}>{task.priority}</span>}</div>
                        <div className="col-span-1 text-xs text-gray-500">{fmtDate(task.dueDate)}</div>
                        <div className="col-span-1 text-xs text-blue-600 font-medium">{task.storyPoints > 0 ? task.storyPoints : ''}</div>
                        <div className="col-span-1 text-xs text-gray-400">{cap(task.status || '')}</div>
                        <div className="col-span-2 text-xs text-gray-400 truncate">{task.createdBy?.name || ''}</div>
                      </div>
                    );

                    const totalBacklog = backlogTasks.length + emptyFeatures.length;

                    return (
                      <div>
                        {/* Column header */}
                        <div className="grid grid-cols-12 gap-1 px-5 py-2 text-xs text-gray-400 font-medium select-none border-b border-gray-100">
                          <div className="col-span-4 pl-3">Name</div>
                          <div className="col-span-2">Assignee</div>
                          <div className="col-span-1">Priority</div>
                          <div className="col-span-1">Due</div>
                          <div className="col-span-1">SP</div>
                          <div className="col-span-1">Status</div>
                          <div className="col-span-2">Owner</div>
                        </div>

                        {totalBacklog === 0 && (
                          <div className="text-center py-12 text-sm text-gray-400">Backlog is empty — all items are assigned to sprints.</div>
                        )}

                        {/* Feature groups */}
                        {Object.values(featureGroups).map(({ feature: f, tasks: fTasks }) => (
                          <div key={f._id}>
                            <div className="grid grid-cols-12 gap-1 px-5 py-2.5 hover:bg-blue-50/30 cursor-pointer" onClick={() => handleFeatureClick(f._id)}>
                              <div className="col-span-4 flex items-center gap-2">
                                <svg className="w-4 h-4 text-blue-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6A2.25 2.25 0 016 3.75h2.25A2.25 2.25 0 0110.5 6v2.25a2.25 2.25 0 01-2.25 2.25H6a2.25 2.25 0 01-2.25-2.25V6z" />
                                </svg>
                                <span className="text-sm font-medium text-blue-700 truncate">{f.name}</span>
                                <span className="text-xs text-gray-400">{fTasks.length}</span>
                              </div>
                              <div className="col-span-2" />
                              <div className="col-span-1">{f.priority && <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${PRIORITY_PILL[f.priority] || 'bg-gray-200'}`}>{f.priority}</span>}</div>
                              <div className="col-span-1 text-xs text-gray-500">{fmtDate(f.dates?.estimatedEndDate)}</div>
                              <div className="col-span-1 text-xs text-blue-600 font-medium">{f.storyPoints > 0 ? f.storyPoints : ''}</div>
                              <div className="col-span-1">
                                {f.approvalStatus && f.approvalStatus !== 'not_required' && (
                                  <span className={`text-xs px-1.5 py-0.5 rounded ${f.approvalStatus === 'approved' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>{f.approvalStatus === 'approved' ? '✓' : '⏳'}</span>
                                )}
                              </div>
                              <div className="col-span-2 text-xs text-gray-400 truncate">{f.owner?.name || ''}</div>
                            </div>
                            {fTasks.map(t => <TaskRow key={t._id} task={t} indent />)}
                          </div>
                        ))}

                        {/* Empty features (no tasks) */}
                        {emptyFeatures.map(f => (
                          <div key={f._id} className="grid grid-cols-12 gap-1 px-5 py-2.5 hover:bg-blue-50/30 cursor-pointer" onClick={() => handleFeatureClick(f._id)}>
                            <div className="col-span-4 flex items-center gap-2">
                              <svg className="w-4 h-4 text-blue-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6A2.25 2.25 0 016 3.75h2.25A2.25 2.25 0 0110.5 6v2.25a2.25 2.25 0 01-2.25 2.25H6a2.25 2.25 0 01-2.25-2.25V6z" />
                              </svg>
                              <span className="text-sm font-medium text-blue-700 truncate">{f.name}</span>
                              <span className="text-xs text-gray-400">0 tasks</span>
                            </div>
                            <div className="col-span-2" />
                            <div className="col-span-1">{f.priority && <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${PRIORITY_PILL[f.priority] || 'bg-gray-200'}`}>{f.priority}</span>}</div>
                            <div className="col-span-1 text-xs text-gray-500">{fmtDate(f.dates?.estimatedEndDate)}</div>
                            <div className="col-span-1 text-xs text-blue-600 font-medium">{f.storyPoints > 0 ? f.storyPoints : ''}</div>
                            <div className="col-span-1" />
                            <div className="col-span-2 text-xs text-gray-400 truncate">{f.owner?.name || ''}</div>
                          </div>
                        ))}

                        {/* Standalone tasks */}
                        {standalone.length > 0 && (
                          <div>
                            <div className="px-5 py-2 text-xs font-bold text-gray-500 flex items-center gap-2">
                              <div className="w-2 h-2 rounded-full bg-gray-400" />
                              Unassigned
                              <span className="text-gray-400 font-normal">{standalone.length}</span>
                            </div>
                            {standalone.map(t => <TaskRow key={t._id} task={t} />)}
                          </div>
                        )}

                        {/* Summary */}
                        {totalBacklog > 0 && (
                          <div className="px-5 py-2 text-xs text-gray-400 border-t border-gray-100">
                            SUM {backlogTasks.reduce((s: number, t: any) => s + (t.storyPoints || 0), 0)} SP · {backlogTasks.length} tasks · {Object.keys(featureGroups).length + emptyFeatures.length} features
                          </div>
                        )}
                      </div>
                    );
                  })()}
                  {/* ── Sprints ─────────────────────────────── */}
                  {viewTab === 'sprints' && <SprintTabView
                    sprints={sprints}
                    projectId={selectedProject._id}
                    onOpenSprint={(sprint: any) => { setSelectedSprint({ _id: sprint._id, name: sprint.name }); setSideTab('sprints'); }}
                    onNavigateFeature={handleFeatureClick}
                  />}

                  {/* ── Features (expandable with inline create) ── */}
                  {viewTab === 'features' && <FeaturesTabView
                    features={features}
                    tasks={tasks}
                    projectId={selectedProject._id}
                    onNavigate={handleFeatureClick}
                    onTaskClick={handleTaskClick}
                    onRefresh={handleRefresh}
                  />}

                  {/* ── Bugs (with inline create) ─────────────── */}
                  {viewTab === 'bugs' && <BugsTabView
                    bugs={bugs}
                    projectId={selectedProject._id}
                    onNavigate={handleBugClick}
                    onRefresh={() => {
                      api.get('/bugs', { params: { projectId: selectedProject._id, limit: '200' } }).then(res => {
                        setBugs(Array.isArray(res.data.data) ? res.data.data : []);
                      }).catch(() => {});
                    }}
                  />}

                  {viewTab === 'testing' && <TestingTabView projectId={selectedProject._id} features={features} bugs={bugs} tasks={tasks} onTaskClick={handleTaskClick} onRefresh={handleRefresh} />}

                  {/* ── Approvals ──────────────────────────────── */}
                  {viewTab === 'approvals' && (() => {
                    const pending = features.filter(f => f.approvalStatus === 'pending_approval' || f.approvalStatus === 'disputed');
                    const BATCH_STATUS_STYLE: Record<string, string> = {
                      draft: 'bg-gray-100 text-gray-600',
                      pending_approval: 'bg-yellow-100 text-yellow-700',
                      approved: 'bg-green-100 text-green-700',
                      rejected: 'bg-red-100 text-red-700',
                      disputed: 'bg-orange-100 text-orange-700',
                    };
                    const BATCH_STATUS_LABEL: Record<string, string> = {
                      draft: 'Draft',
                      pending_approval: 'Pending',
                      approved: 'Approved',
                      rejected: 'Rejected',
                      disputed: 'Disputed',
                    };
                    return (
                      <div>
                        {/* Submit batch button */}
                        <div className="px-4 py-2 border-b border-gray-100">
                          <button
                            onClick={() => setShowBatchModal(true)}
                            className="text-xs text-gray-400 hover:text-blue-600 transition-colors"
                          >+ Submit batch for approval</button>
                        </div>

                        {/* Feature approvals */}
                        {pending.length > 0 && (
                          <div>
                            <div className="px-4 py-1.5">
                              <span className="text-[10px] font-medium text-gray-400 uppercase tracking-wider">Features</span>
                            </div>
                            {pending.map(f => (
                              <div
                                key={f._id}
                                onClick={() => handleFeatureClick(f._id)}
                                className="flex items-center gap-3 px-4 py-2.5 border-b border-gray-100 hover:bg-gray-50 cursor-pointer transition-colors"
                              >
                                <svg className="w-3.5 h-3.5 text-yellow-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                                <span className="text-sm text-gray-800 truncate flex-1">{f.name}</span>
                                <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${f.approvalStatus === 'disputed' ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'}`}>
                                  {f.approvalStatus === 'disputed' ? 'Disputed' : 'Pending'}
                                </span>
                                {f.storyPoints > 0 && <span className="text-xs text-blue-600 font-medium shrink-0">{f.storyPoints} SP</span>}
                                <svg className="w-3.5 h-3.5 text-gray-300 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Batch approvals */}
                        {batches.length > 0 && (
                          <div>
                            <div className="px-4 py-1.5 mt-1">
                              <span className="text-[10px] font-medium text-gray-400 uppercase tracking-wider">Batches</span>
                            </div>
                            {batches.map((b: any) => (
                              <div
                                key={b._id}
                                onClick={() => setSelectedBatch(b)}
                                className="flex items-center gap-3 px-4 py-2.5 border-b border-gray-100 hover:bg-gray-50 cursor-pointer transition-colors"
                              >
                                <svg className="w-3.5 h-3.5 text-gray-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M6 6.878V6a2.25 2.25 0 012.25-2.25h7.5A2.25 2.25 0 0118 6v.878m-12 0c.235-.083.487-.128.75-.128h10.5c.263 0 .515.045.75.128m-12 0A2.25 2.25 0 004.5 9v.878m13.5-3A2.25 2.25 0 0119.5 9v.878m-15 0A2.248 2.248 0 003 11.25v1.5c0 .621.504 1.125 1.125 1.125h15.75c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.252-1.183-.66-1.59m0 0A2.246 2.246 0 0019.5 9.878" /></svg>
                                <span className="text-sm text-gray-800 truncate flex-1">{b.name}</span>
                                <span className="text-[10px] text-gray-400 tabular-nums shrink-0">{b.items?.length || 0} items</span>
                                <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${BATCH_STATUS_STYLE[b.approvalStatus] || 'bg-gray-100 text-gray-700'}`}>
                                  {BATCH_STATUS_LABEL[b.approvalStatus] || b.approvalStatus}
                                </span>
                                {b.totalStoryPoints > 0 && <span className="text-xs text-blue-600 font-medium shrink-0 tabular-nums">{b.totalStoryPoints} SP</span>}
                                <svg className="w-3.5 h-3.5 text-gray-300 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
                              </div>
                            ))}
                          </div>
                        )}

                        {pending.length === 0 && batches.length === 0 && (
                          <p className="text-center py-12 text-sm text-gray-400">No pending approvals</p>
                        )}
                      </div>
                    );
                  })()}

                  {viewTab === 'folders' && (
                    <FolderTree
                      projectId={selectedProject._id}
                      onSelectFolder={(fid: string | null) => { if (fid) setSelectedFolderId(fid); }}
                      selectedFolderId={selectedFolderId}
                    />
                  )}
                  {viewTab === 'kb' && <KBTabView projectId={selectedProject._id} />}

                  {/* ── Lazy-loaded views ─────────────────────── */}
                  <React.Suspense fallback={<div className="flex items-center justify-center h-48"><div className="animate-spin h-5 w-5 border-2 border-blue-500 border-t-transparent rounded-full" /></div>}>
                    {viewTab === 'team' && <TeamTabView project={selectedProject} onRefresh={handleRefresh} />}
                    {viewTab === 'activity' && <ActivityTabView projectId={selectedProject._id} />}
                    {viewTab === 'leaderboard' && <SPTabView projectId={selectedProject._id} />}
                  </React.Suspense>
                </>
              )}
            </div>
          </>
        ) : (
          <QuickActions onTaskClick={handleTaskClick} />
        )}
      </div>

      {/* Folder panel */}
      {selectedFolderId && selectedProject && (
        <div className="!mt-0 fixed inset-0 z-40 flex items-center justify-center p-4" onClick={() => setSelectedFolderId(null)}>
          <div className="absolute inset-0 bg-black/30" style={{ animation: 'backdropFadeIn 300ms ease-out' }} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-[90vw] h-[90vh] flex flex-col overflow-hidden border border-gray-200" style={{ animation: 'modalFadeIn 300ms ease-out' }} onClick={e => e.stopPropagation()}>
            <React.Suspense fallback={<div className="flex-1 flex items-center justify-center"><div className="animate-spin h-5 w-5 border-2 border-blue-500 border-t-transparent rounded-full" /></div>}>
              <FolderDetailModal
                folderId={selectedFolderId}
                projectId={selectedProject._id}
                onClose={() => setSelectedFolderId(null)}
                onOpenTask={handleTaskClick}
                onOpenFeature={handleFeatureClick}
                onRefresh={handleRefresh}
              />
            </React.Suspense>
          </div>
        </div>
      )}

      {/* Bug panel */}
      {selectedBugId && (
        <BugPanel
          bugId={selectedBugId}
          projectMembers={selectedProject?.members || []}
          onClose={handleBugClose}
          onRefresh={() => {
            api.get('/bugs', { params: { projectId: selectedProject?._id, limit: '200' } }).then(res => {
              setBugs(Array.isArray(res.data.data) ? res.data.data : []);
            }).catch(() => {});
          }}
        />
      )}

      {/* Feature panel */}
      {selectedFeatureId && (
        <FeaturePanel
          featureId={selectedFeatureId}
          onClose={handleFeatureClose}
          onRefresh={handleRefresh}
          onOpenTask={handleTaskClick}
        />
      )}

      {/* Task panel */}
      {selectedTaskId && (
        <TaskPanel
          taskId={selectedTaskId}
          onClose={handleTaskClose}
          onRefresh={handleRefresh}
        />
      )}

      {/* Delete project confirmation */}
      {confirmDeleteProject && selectedProject && (
        <div className="!mt-0 fixed inset-0 z-50 flex items-center justify-center p-4" onClick={() => setConfirmDeleteProject(false)}>
          <div className="absolute inset-0 bg-black/30" style={{ animation: 'backdropFadeIn 300ms ease-out' }} />
          <div className="relative bg-white rounded-xl shadow-xl p-5 w-full max-w-xs" style={{ animation: 'modalFadeIn 300ms ease-out' }} onClick={e => e.stopPropagation()}>
            <h3 className="text-sm font-semibold text-gray-900">Delete project</h3>
            <p className="text-xs text-gray-500 mt-1">This will permanently delete <strong>{selectedProject.name}</strong> and all its data.</p>
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setConfirmDeleteProject(false)} className="px-3 py-1.5 text-xs text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">Cancel</button>
              <button onClick={() => {
                projectAPI.deleteProject(selectedProject._id).then(() => {
                  setConfirmDeleteProject(false);
                  setSelectedProject(null);
                  setSearchParams({}, { replace: true });
                  window.dispatchEvent(new CustomEvent('project-deleted'));
                }).catch(() => {});
              }} className="px-3 py-1.5 text-xs bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors">Delete</button>
            </div>
          </div>
        </div>
      )}

      {/* Batch submit modal */}
      {showBatchModal && selectedProject && (
        <BatchSubmitModal
          projectId={selectedProject._id}
          projectName={selectedProject.name}
          tasks={tasks}
          bugs={bugs}
          features={features}
          onClose={() => setShowBatchModal(false)}
          onSubmitted={() => { refreshBatches(); handleRefresh(); }}
        />
      )}

      {/* Batch detail modal */}
      {selectedBatch && (() => {
        const b = selectedBatch;
        const details: Record<string, any> = b.itemDetails || {};
        const submitterName = typeof b.submittedBy === 'object' ? b.submittedBy?.name : '';
        const aiItems = b.aiEstimation?.items || [];
        const items = aiItems.length > 0 ? aiItems : (b.items || []);
        const TASK_STATUS_PILL: Record<string, string> = {
          todo: 'bg-gray-100 text-gray-600', in_progress: 'bg-blue-100 text-blue-700',
          blocked: 'bg-red-100 text-red-700', review: 'bg-yellow-100 text-yellow-700',
          done: 'bg-green-100 text-green-700',
        };
        const PRIO_PILL: Record<string, string> = {
          critical: 'bg-red-100 text-red-700', high: 'bg-orange-100 text-orange-700',
          medium: 'bg-yellow-100 text-yellow-700', low: 'bg-green-100 text-green-700',
        };
        const batchStatusStyle: Record<string, string> = {
          draft: 'bg-gray-100 text-gray-600', pending_approval: 'bg-yellow-100 text-yellow-700',
          approved: 'bg-green-100 text-green-700', rejected: 'bg-red-100 text-red-700',
          disputed: 'bg-orange-100 text-orange-700',
        };
        const batchStatusLabel: Record<string, string> = {
          draft: 'Draft', pending_approval: 'Pending',
          approved: 'Approved', rejected: 'Rejected', disputed: 'Disputed',
        };

        return (
          <div className="!mt-0 fixed inset-0 z-50 flex items-center justify-center" onClick={() => setSelectedBatch(null)}>
            <div className="absolute inset-0 bg-black/30" style={{ animation: 'backdropFadeIn 300ms ease-out' }} />
            <div className="relative bg-white rounded-2xl shadow-2xl w-[90vw] h-[90vh] flex flex-col overflow-hidden border border-gray-200" style={{ animation: 'modalFadeIn 300ms ease-out' }} onClick={e => e.stopPropagation()}>
              {/* Header */}
              <div className="flex items-center justify-between px-5 py-2.5 border-b border-gray-200 shrink-0">
                <div className="flex items-center gap-2.5 text-xs">
                  <span className={`px-2.5 py-1 rounded-full font-medium ${batchStatusStyle[b.approvalStatus] || 'bg-gray-100 text-gray-700'}`}>
                    {batchStatusLabel[b.approvalStatus] || b.approvalStatus}
                  </span>
                  <span className="text-gray-400">Batch</span>
                  {submitterName && <><span className="text-gray-300">·</span><span className="text-gray-400">by {submitterName}</span></>}
                  {(b.totalStoryPoints ?? 0) > 0 && (
                    <><span className="text-gray-300">·</span><span className="text-blue-600 font-medium">{b.totalStoryPoints} SP</span></>
                  )}
                  {b.aiEstimation && (
                    <><span className="text-gray-300">·</span><span className="text-blue-500">AI estimated</span></>
                  )}
                </div>
                <button onClick={() => setSelectedBatch(null)} className="text-gray-400 hover:text-gray-600">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              </div>

              <div className="flex-1 overflow-y-auto px-6 py-5">
                <h2 className="text-xl font-semibold text-gray-900 mb-5">{b.name}</h2>

                {/* Properties grid */}
                <div className="grid grid-cols-2 gap-x-8 gap-y-3.5 mb-6">
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-gray-400 w-24">Submitted by</span>
                    <span className="text-xs text-gray-700">{submitterName || '—'}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-gray-400 w-24">Items</span>
                    <span className="text-xs text-gray-700">{b.items?.length || 0} total</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-gray-400 w-24">Submitted</span>
                    <span className="text-xs text-gray-700">{b.submittedAt ? new Date(b.submittedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}</span>
                  </div>
                  {b.approvedAt && (
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-gray-400 w-24">Approved</span>
                      <span className="text-xs text-gray-700">{new Date(b.approvedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}{b.approvedByName ? ` by ${b.approvedByName}` : ''}</span>
                    </div>
                  )}
                </div>

                {/* Rejection reason */}
                {b.approvalStatus === 'rejected' && b.rejectionReason && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-6">
                    <div className="text-xs font-medium text-red-600 mb-1">Rejection Reason</div>
                    <p className="text-sm text-red-800">{b.rejectionReason}</p>
                  </div>
                )}

                {/* Dispute reason */}
                {b.approvalStatus === 'disputed' && b.disputeReason && (
                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 mb-6">
                    <div className="text-xs font-medium text-orange-600 mb-1">Dispute Reason</div>
                    <p className="text-sm text-orange-800">{b.disputeReason}</p>
                  </div>
                )}

                {/* AI Estimation info */}
                {b.aiEstimation && (
                  <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 mb-6">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-medium text-blue-600">AI Estimation</span>
                      <span className="text-[10px] text-blue-400">
                        {b.aiEstimation.model}
                        {b.aiEstimation.estimatedAt ? ' · ' + new Date(b.aiEstimation.estimatedAt).toLocaleDateString() : ''}
                      </span>
                    </div>
                    <p className="text-sm text-blue-800">Total: <strong>{b.aiEstimation.totalPoints ?? b.totalStoryPoints} SP</strong></p>
                  </div>
                )}

                {/* Items list */}
                <div className="mb-6">
                  <div className="text-xs font-medium text-gray-500 mb-1.5">Items ({items.length})</div>
                  <div className="border border-gray-200 rounded-lg divide-y divide-gray-100 max-h-[50vh] overflow-y-auto">
                    {items.length > 0 ? items.map((item: any, i: number) => {
                      const detail = item.itemId ? details[item.itemId.toString?.()] || details[item.itemId] : null;
                      const title = detail?.title || item.title || item.itemId;
                      const status = detail?.status;
                      const priority = detail?.priority;
                      const assignees = detail?.assignedTo ?? [];
                      return (
                        <div key={item.itemId || i} className="px-3 py-2.5">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-xs text-gray-400 w-4 text-right shrink-0">{i + 1}.</span>
                            <span className={`text-[10px] px-1.5 py-0.5 rounded ${item.itemType === 'bug' ? 'bg-red-50 text-red-500' : 'bg-gray-100 text-gray-500'}`}>
                              {item.itemType}
                            </span>
                            {status && (
                              <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${TASK_STATUS_PILL[status] || 'bg-gray-100 text-gray-700'}`}>
                                {(status || '').replace('_', ' ')}
                              </span>
                            )}
                            <span className="text-sm text-gray-900 truncate flex-1" title={String(title)}>{title}</span>
                            {priority && (
                              <span className={`text-[10px] px-1.5 py-0.5 rounded ${PRIO_PILL[priority] || 'bg-gray-100 text-gray-600'}`}>{priority}</span>
                            )}
                            <span className="text-sm font-medium text-blue-600 tabular-nums shrink-0">{item.points ?? detail?.storyPoints ?? '—'}</span>
                            <span className="text-[10px] text-gray-400">SP</span>
                          </div>
                          {assignees.length > 0 && (
                            <div className="flex items-center gap-1 mt-1.5 ml-6">
                              {assignees.map((a: any, j: number) => (
                                <div key={j} className="flex items-center gap-1 bg-gray-100 rounded-full px-2 py-0.5">
                                  <div className="w-4 h-4 rounded-full bg-blue-500 text-white text-[10px] flex items-center justify-center">{(a.name || '?')[0]}</div>
                                  <span className="text-xs text-gray-600">{a.name}</span>
                                </div>
                              ))}
                            </div>
                          )}
                          {item.reasoning && item.reasoning !== 'Previously estimated' && (
                            <div className="flex items-start gap-1 mt-1 ml-6">
                              <svg className="w-3 h-3 text-blue-400 mt-0.5 shrink-0" fill="currentColor" viewBox="0 0 20 20"><path d="M10 2a1 1 0 011 1v1.323l3.954 1.582 1.599-.8a1 1 0 01.894 1.79l-1.233.616 1.738 5.42a1 1 0 01-.285 1.05A3.989 3.989 0 0115 15a3.989 3.989 0 01-2.667-1.019 1 1 0 01-.285-1.05l1.715-5.349L10 6.158 6.237 7.582l1.715 5.349a1 1 0 01-.285 1.05A3.989 3.989 0 015 15a3.989 3.989 0 01-2.667-1.019 1 1 0 01-.285-1.05l1.738-5.42-1.233-.617a1 1 0 01.894-1.788l1.599.799L9 4.323V3a1 1 0 011-1z" /></svg>
                              <span className="text-[10px] text-blue-500 italic">{item.reasoning}</span>
                            </div>
                          )}
                        </div>
                      );
                    }) : (
                      <div className="px-3 py-6 text-center text-xs text-gray-400">No items in this batch</div>
                    )}
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="px-6 py-3 border-t border-gray-200 flex items-center justify-end shrink-0">
                <button onClick={() => setSelectedBatch(null)} className="text-xs text-gray-500 px-4 py-2 hover:bg-gray-50 rounded-lg transition-colors">Close</button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default EmployeeWorkspace;
